/**
 * Procedural room background generator for the raid grid.
 *
 * Composes a 120x120 top-down pixel-art map tile for any room from a
 * shared element library (roofs, pads, crates, fences, trees, water...)
 * arranged by a recipe chosen from the room name, painted and weathered
 * with the owning region's theme. Road/path connectors meet at cell edges
 * so the whole grid reads as one continuous map, and opts.anim drives the
 * live effects: wind, day/night, clouds, powered lights, daylight wells.
 *
 * Deterministic: the same room name in the same region always yields the
 * same image (seeded with the same PRNG the room-naming system uses).
 * Selection logic is pure and Node-safe; only the renderer touches the DOM.
 */

import { makePRNG, RENDER_CONFIG, zoneArchetypes } from '../data/raidRegions.js';
import { MAJOR as MAJOR_RANK } from './roadNetwork-core.js';

// Tiles are AUTHORED on a BASE-unit grid and RENDERED at SCALE device pixels
// per unit. The two multiply out to the canvas, so they trade off: the art
// began on a 40-unit grid drawn 3x, which is 120px of 3px blocks, and now
// sits on a 120-unit grid drawn 1:1 — the same canvas, the same cost, but
// every coordinate in the element library can name a single pixel instead of
// a 3x3 square. That is what makes detail authorable; raising SCALE instead
// would only have made the blocks bigger.
export const BASE = 120;                 // authoring grid, in units
export const SCALE = 1;                  // device px per unit
export const TILE_PX = BASE * SCALE;     // 120 — the canvas a tile is drawn into
export const IMAGE_SIZE = TILE_PX;       // PNG export size (image lab)
// How close to the tile edge a structure may be drawn. A roof clipped by the
// canvas reads as a rendering fault rather than as a building continuing into
// the next tile, because the neighbour draws its own recipe and never
// continues it — so structures are held short of the join instead.
const TILE_CLAMP = 6;

// ---- world-space effect grid ----
// Clouds and daylight wells deliberately do NOT follow the authoring grid.
// Every constant they are tuned with — cloud noise scale, WELL_CELL, the ray
// geometry — is expressed in these coarse world units, and their per-tile
// cost is their cell count. Pinning both here keeps the ambient layer looking
// and costing exactly what it did however fine the art grid gets; a 1px cloud
// cell would be 9x the work for weather nobody can see at that scale.
export const WORLD = 40;                 // world units across one tile
export const FX_STEP = 2;                // world units per effect cell
const FX_PX = TILE_PX / WORLD;           // device px per world unit

// ===============================
// RENDER CONFIG (data lives in js/data/raidRegions.js)
// ===============================
// Region themes, tuning knobs, and room recipes are all JSON-compatible
// data in RENDER_CONFIG — this module supplies the element library and the
// interpreter that turns that data into pixels. Designing a new room look
// means editing the config, not this file.
export const DEFAULT_TUNE = RENDER_CONFIG.defaultTune;
export const REGION_THEMES = RENDER_CONFIG.regions;
export const ROOM_RECIPES = RENDER_CONFIG.recipes;
export const SKILL_ANCHORS = RENDER_CONFIG.skillAnchors || {};
const FALLBACK_THEME = REGION_THEMES['shard-cities'];

// Cloud formation presets. scale = noise feature size in WORLD units (a tile
// is WORLD across, a building a third of one) — bigger = broader banks,
// stretch = elongation along the drift direction, sharp = edge hardness,
// maxA = peak opacity, thr(cover) = density threshold.
// Scale is the one knob that does NOT trade against cover: thresholding a
// noise field is scale-invariant, so enlarging the features veils exactly as
// much sky in bigger pieces. These sit where they do because a cloud smaller
// than the building it is passing over reads as smoke, not weather — at the
// low end a bank is a couple of tiles across, at the high end most of a map.
// (Stays in code: thr is a function, and presets are renderer internals —
// themes reference them by name via tune.cloudType.)
export const CLOUD_TYPES = {
  scattered: { scale: 65,  stretch: 1,   sharp: 2.2, maxA: 0.40, thr: c => 0.78 - c * 0.50 }, // fair-weather puffs
  broken:    { scale: 100, stretch: 1,   sharp: 2.8, maxA: 0.42, thr: c => 0.72 - c * 0.45 }, // big banks, clear gaps
  overcast:  { scale: 130, stretch: 1.2, sharp: 1.2, maxA: 0.30, thr: c => 0.85 - c * 0.65 }, // flat marine sheet
  wispy:     { scale: 75,  stretch: 3.5, sharp: 1.6, maxA: 0.24, thr: c => 0.75 - c * 0.45 }, // thin streaks
};

// ===============================
// RECIPE SELECTION (pure, Node-safe)
// ===============================
// Ordered: first match against the lowercased room name wins. Short tokens
// that hide inside other words (lock in "block", hive in "archive", vent in
// "inventory") are anchored with \b.
//
// The order runs from what a name tells you MOST about how the tile looks from
// above, down to what it tells you least.
//
// Room names are built as "<place> <fitting> <sector>" — Arcade Shutter Sector,
// Marketfront Depot, Habitation Annex Gate — and every region names all four of
// its gates that way. With the fitting tokens above the place tokens, a name
// resolved to whatever was bolted to it: every gate in the game came out as
// `security`, `depot` beat `market`, `shutter` beat `arcade`, and `commerce`
// went from being a whole recipe to being drawn three times in sixty rooms
// while `security` and `storage` took a third of the map between them.
//
// Reading down: a reactor is unmistakable. A substation, an archive, a
// shopfront and a housing block each have their own architecture and keep it
// even in ruin. Then `environmental` — because a collapse or a breach REPLACES
// what was there with rubble and weeds, and that is louder than the two uses
// below it: a yard and a deck are generic enough that the damage is the more
// legible thing on the tile. `security` is the lowest real match because a
// checkpoint is the most portable fitting of the lot, and gateEmphasis dresses
// a gate tile as a gate whatever recipe it draws — so nothing is lost by
// letting the arcade underneath it show through. `infrastructure` is the
// catch-all and stays last.
const KEYWORD_RECIPES = [
  [/reactor/, 'reactor'],
  [/substation|transformer|\bpower|switchgear|feeder|breaker|boiler|volt|generat|energy/, 'power'],
  [/record|archive|ledger|librar|registr/, 'records'],
  // `commerc`, not `commerce`: "Commercial Airlock Sector" shares no prefix
  // with the noun, and matched `airlock` instead for want of two letters.
  [/market|arcade|commissary|canteen|commerc|plaza|\bmart\b|\bshop|finance|teller|retail/, 'commerce'],
  [/apartment|housing|habitat|barrack|residential|nursery|dorm|\bliving/, 'habitation'],
  [/fungal|spore|breach|ruptur|\bleak|collapse|salvage|scrap|hazard|firebreak|isolation|cleanup|containment|\bwild|\bhive\b|observation/, 'environmental'],
  [/skybridge|overpass|interchange|\brail|transit|causeway|skyway/, 'transit'],
  [/silo|\bseed|storage|depot|vault|supply|inventory|cache|pantry|warehouse|freight|cargo/, 'storage'],
  [/checkpoint|security|barricade|barrier|perimeter|\block\b|\bseal|airlock|turnstile|shutter|decon|\bgate/, 'security'],
  [/relay|junction|conduit|spine|network|maintenance|control|operations|\bhub\b|assembl|conveyor|logistic|crane|drydock|\bdock|intake|\bvent|tower|\blift\b|access/, 'infrastructure'],
];

const CATEGORY_RECIPES = {
  power: 'power', storage: 'storage', security: 'security',
  infrastructure: 'infrastructure', environmental: 'environmental',
  wildlife: 'environmental',
};

const TYPE_RECIPES = {
  start: 'portal', exit: 'portal', road: 'road', water: 'water', campsite: 'campsite',
  blocked: 'blocked',
};

export function selectRecipe(roomName = '', category = '', type = '') {
  if (TYPE_RECIPES[type]) return TYPE_RECIPES[type];
  const name = String(roomName).toLowerCase();
  if (name === 'entry' || name === 'exit') return 'portal';
  for (const [re, recipe] of KEYWORD_RECIPES) {
    if (re.test(name)) return recipe;
  }
  return CATEGORY_RECIPES[category] || 'infrastructure';
}

// ===============================
// PIXEL HELPERS
// ===============================
// Shadow-casting content is rendered in two passes over identical rng
// streams: pass 1 (SHADOW_PASS) lets only the shadow helpers paint, so
// every shadow lands on the bare ground; pass 2 suppresses the shadow
// helpers and paints the objects on top. That way one object's shadow can
// never fall across a neighboring object (same idea as the lake banks).
let SHADOW_PASS = false;
let IN_SHADOW = false;

function castShadow(fn) {
  IN_SHADOW = true;
  fn();
  IN_SHADOW = false;
}

// ---- static / dynamic split ----
// Almost all of a tile sits perfectly still: floor, roads, structures,
// weathering. Only a handful of things move between frames — swaying
// canopies, lamp strobes, flame and portal glow, pond crests — and those are
// wrapped in dynamic(). During a STATIC render the wrapped closures are
// RECORDED instead of drawn, so the cached layer holds only the still parts;
// the animation loop then replays that short list over a blit of the cache
// every frame (see composite). Recording rather than re-running the recipe
// interpreter is the point: the interpreter's rng streams and element
// dispatch cost as much as the painting does.
//
// A recorded closure takes (g, T) so replay can hand it the live context and
// the day-tinted palette; it must not touch the rng, or the tile's layout
// would drift every frame. Resolve anything seeded BEFORE the wrapper and
// capture it. A one-shot drawRoomTile leaves DYN null and the closures run
// inline against CUR_G/CUR_T, which is what the image lab still does.
let DYN = null;                   // recording target during a static render
let CUR_G = null, CUR_T = null;   // inline target when not recording

function dynamic(fn) {
  if (SHADOW_PASS) return;        // pass 1 replays the same stream; record once
  if (DYN) DYN.push(fn);
  else fn(CUR_G, CUR_T);
}

/**
 * A small offscreen surface for pre-rasterising something that will be blitted
 * many times rather than redrawn.
 *
 * A detached <canvas>, deliberately, and NOT an OffscreenCanvas. The two are
 * interchangeable here and OffscreenCanvas is the tidier object — it carries no
 * DOM node, and a wooded tier-3 grid asks for a couple of thousand of these —
 * but as a drawImage SOURCE it is three times slower on a browser that
 * GPU-accelerates canvas (11.2ms a frame against 3.7ms, same count, same
 * sizes, same draws). A main-thread OffscreenCanvas does not get the same
 * accelerated backing, so every blit pays to fetch the pixels. Browsers that
 * rasterise canvas in software show no difference at all, which is exactly why
 * this is worth a comment: it is invisible in the wrong environment.
 */
function scratchCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

// ---- above the weather ----
// A second recording channel, replayed AFTER the world-space effects instead of
// before them. Almost nothing belongs here: clouds and daylight shafts are the
// air of the place and should fall on everything the tile contains.
//
// The exception is anything that is not IN the room but over it. A sublevel's
// rock masses are a cutaway through stone that runs up to the cavern roof, so a
// shaft of daylight crossing one would be light passing through the ceiling.
// Recorded rather than drawn in place because the sampling that finds the rock
// is expensive and the fills are not — this replays a few dozen rects a frame
// off work done once per still-layer repaint.
let OVER = null;

// Where the tile's GROUND PLAN is collected during a static render, on the same
// pattern as DYN and OVER above: composeBody fills it in if somebody put one
// here, and ignores it otherwise. The renderer already measures every footprint
// it draws in order to route roads and keep vegetation off roofs — this just
// stops that knowledge being thrown away at the end of the pass, so the raid
// team can be kept off the roofs as well as the shrubbery.
let PLAN = null;

/**
 * Record `fn` to be replayed above the effects, or — when nobody is collecting —
 * draw it into `g`/`T` right now.
 *
 * The fallback target is passed in rather than read from CUR_G/CUR_T the way
 * dynamic() does it. Both of the real paths always collect, so that pair is
 * only ever whatever the LAST inline render happened to leave behind, and a
 * caller outside those paths would silently paint into a stale canvas.
 */
function overhead(g, T, fn) {
  if (SHADOW_PASS) return;        // same reason as dynamic: record once
  if (OVER) OVER.push(fn);
  else fn(g, T);
}

/**
 * A graphics object that draws nothing and remembers where it would have.
 *
 * Element functions draw straight from the rng, so an element that is going to
 * be measured or suppressed still has to RUN — skipping the call outright would
 * shift the stream for everything after it and reshuffle the whole tile. It
 * runs into this instead of into the canvas.
 *
 * Measuring beats declaring: a hand-written "this recipe's building sits here"
 * table is a second copy of the art's coordinates, and the two drift the first
 * time anyone edits a recipe.
 */
function makeProbeG() {
  const box = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  const add = (x, y, w, h, s) => {
    box.x0 = Math.min(box.x0, x / s); box.y0 = Math.min(box.y0, y / s);
    box.x1 = Math.max(box.x1, (x + w) / s); box.y1 = Math.max(box.y1, (y + h) / s);
  };
  return {
    box,
    r(x, y, w, h) { if (SHADOW_PASS !== IN_SHADOW) return; add(x | 0, y | 0, w | 0, h | 0, 1); },
    p(x, y, w, h) { if (SHADOW_PASS !== IN_SHADOW) return; add(x | 0, y | 0, w | 0, h | 0, SCALE); },
    a(alpha, fn) { fn(); },
  };
}

function makeG(ctx) {
  return {
    ctx,
    // authoring units — the whole element library draws through this
    r(x, y, w, h, c) {
      if (SHADOW_PASS !== IN_SHADOW) return;
      ctx.fillStyle = c;
      ctx.fillRect((x | 0) * SCALE, (y | 0) * SCALE, (w | 0) * SCALE, (h | 0) * SCALE);
    },
    // device pixels, for detail finer than the authoring grid
    p(x, y, w, h, c) {
      if (SHADOW_PASS !== IN_SHADOW) return;
      ctx.fillStyle = c;
      ctx.fillRect(x | 0, y | 0, w | 0, h | 0);
    },
    a(alpha, fn) { ctx.globalAlpha = alpha; fn(); ctx.globalAlpha = 1; },
  };
}

// Speckle at DEVICE resolution: the base tone laid down in units, the grain
// scattered in device pixels. Coverage matches what the old unit-space
// dither gave, so surfaces read the same from across the map and gain
// texture up close. SPECK trades grain size against fillRect count — 2px
// specks are still a third the size of the old unit-grid blocks and cost a
// quarter of what 1px ones would on the roads, which are the widest dithered
// areas left now that floors go through grain().
const SPECK = 2;
function dither(g, rng, x, y, w, h, c1, c2, density = 0.2) {
  g.r(x, y, w, h, c1);
  const n = Math.floor((w * h * SCALE * SCALE * density) / (SPECK * SPECK));
  for (let i = 0; i < n; i++) g.p((x + rng() * w) * SCALE, (y + rng() * h) * SCALE, SPECK, SPECK, c2);
}

const hexRGB = hex => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

/**
 * A palette colour taken down, and optionally toward grey — for material that
 * is the same stuff as its neighbour but in a different state. `k` scales
 * brightness; `grey` (0..1) pulls the channels toward the colour's own
 * luminance, which is what turns a brown into charcoal rather than into a
 * darker brown. Memoised: this runs per element, per tile, per repaint.
 */
const shadeCache = new Map();
function shade(hex, k, grey = 0) {
  const key = `${hex}|${k}|${grey}`;
  let out = shadeCache.get(key);
  if (out) return out;
  const [r, g, b] = hexRGB(hex);
  const lum = 0.3 * r + 0.59 * g + 0.11 * b;
  const ch = c => Math.max(0, Math.min(255, Math.round((c * (1 - grey) + lum * grey) * k)));
  shadeCache.set(key, out = `#${[ch(r), ch(g), ch(b)].map(c => c.toString(16).padStart(2, '0')).join('')}`);
  return out;
}

// Full-tile floor grain, written straight into an ImageData buffer. The
// ground is 100% of a tile's area, so it is where added resolution shows
// first — 3x3 blocks of flat color read as an upscale, per-pixel grain reads
// as dirt. Scattering 14k 1px fillRects would cost more than the entire rest
// of the tile; one buffer write costs almost nothing.
let grainBuf = null;   // one scratch buffer: putImageData copies out of it
function grain(g, rng, c1, c2, density) {
  if (SHADOW_PASS !== IN_SHADOW) return;
  const img = grainBuf || (grainBuf = new ImageData(TILE_PX, TILE_PX));
  const d = img.data;
  const [r1, g1, b1] = hexRGB(c1), [r2, g2, b2] = hexRGB(c2);
  for (let i = 0, o = 0; i < TILE_PX * TILE_PX; i++, o += 4) {
    const speck = rng() < density;
    d[o] = speck ? r2 : r1;
    d[o + 1] = speck ? g2 : g1;
    d[o + 2] = speck ? b2 : b1;
    d[o + 3] = 255;
  }
  g.ctx.putImageData(img, 0, 0);
}

const ri = (rng, min, max) => min + Math.floor(rng() * (max - min + 1));

// ===============================
// SHARED ELEMENT LIBRARY
// ===============================
// Broken chunks with a lit top edge — on the old 3px grid a chunk WAS one
// block, so it could only ever be a dot.
/**
 * One hole in the ground: shadowed lip, the far dark below, and a crumbling rim
 * catching the light. Shared by the sinkhole and the chasm — they differ only
 * in how many of these there are and where.
 */
function voidPit(g, rng, T, cx, cy, r) {
  const left = wobbler(rng), right = wobbler(rng);
  blob(g, cx, cy, r + 3, T.structDark, left, right);            // crumbling rim
  g.a(0.3, () => blob(g, cx, cy, r + 2, T.structLight, left, right));
  blob(g, cx, cy, r, T.far, left, right);                       // the ground below
  g.a(0.6, () => blob(g, cx, cy, Math.max(2, r - 6), '#000000', left, right));
}

function rubbleChunk(g, rng, T, x, y) {
  const c = [T.structDark, T.metal, T.struct][ri(rng, 0, 2)];
  const w = ri(rng, 2, 6), h = ri(rng, 1, 3);
  g.r(Math.round(x), Math.round(y), w, h, c);
  if (h > 1) g.r(Math.round(x), Math.round(y), w, 1, T.structLight);
}

function rubble(g, rng, T, x1, x2, y1, y2, n = 12) {
  for (let i = 0; i < n; i++) rubbleChunk(g, rng, T, ri(rng, x1, x2), ri(rng, y1, y2));
}

// A hairline that wanders a pixel at a time. This is the clearest single
// example of what the finer grid bought: the same call used to lay down a
// chain of 3x3 blocks.
function crack(g, rng, T, x, y, len = 10) {
  let cx = x, cy = y;
  for (let i = 0; i < len; i++) {
    g.r(cx, cy, 1, 1, T.road);
    cx += ri(rng, -1, 1);
    cy += ri(rng, 0, 1);
  }
}

function foliageClump(g, rng, T, x, y, size = 5) {
  for (let i = 0; i < size; i++) {
    g.r(x + ri(rng, -6, 6), y - ri(rng, 0, 9), ri(rng, 5, 9), 5, T.veg1);
  }
  for (let i = 0; i < Math.ceil(size / 2); i++) {
    g.r(x + ri(rng, -6, 3), y - ri(rng, 3, 9), ri(rng, 3, 6), 3, T.veg2);
  }
  for (let i = 0; i < size; i++) {                               // leaf flecks
    g.r(x + ri(rng, -5, 5), y - ri(rng, 0, 8), 1, 1, T.veg2);
  }
}

// ===============================
// TOP-DOWN STYLE
// ===============================
// Map tiles viewed from above. Connectivity comes from two rules: floors are
// full-cell fills whose detail lines sit at fixed offsets (so they continue
// across cells), and road/path connectors always run from the cell center to
// the edge midpoint at a fixed width — two adjacent cells that both draw a
// connector on their shared edge meet exactly. No vignette, no sky.

function disc(g, cx, cy, r, c) {
  for (let dy = -r; dy <= r; dy++) {
    const dx = Math.floor(Math.sqrt(r * r - dy * dy));
    g.r(cx - dx, cy + dy, 2 * dx + 1, 1, c);
  }
}

function ringO(g, cx, cy, r, c) {
  for (let dy = -r; dy <= r; dy++) {
    const dx = Math.round(Math.sqrt(r * r - dy * dy));
    g.r(cx - dx, cy + dy, 1, 1, c);
    g.r(cx + dx, cy + dy, 1, 1, c);
  }
}

// How coarsely a pre-rasterised sprite tracks the daylight. Anything cached
// across frames has to be rebuilt when the palette under it moves, and `light`
// moves continuously — tintPalette alone buckets it 512 ways, which over a
// ten-minute day is a fresh palette every half second and so no cache at all.
//
// 16 steps rebuilds roughly twice a game minute and cannot be seen: the tint
// scales a channel by 0.30 + 0.70q, so quantising q to 1/16 moves a mid-toned
// leaf by under a level and a half out of 255 — inside the rounding difference
// that already exists between tinting a colour and multiplying a pixel.
const TINT_STEPS = 16;

// ...but every sprite on the map crossing the SAME step on the same frame is
// its own problem: a wooded tier-3 grid rebuilds ~1500 canopies at once, which
// measured at 227k fillRects in a single frame — worse, in one hitch, than the
// per-frame cost the sprites were introduced to remove. So each sprite is given
// a fixed fraction of a step to cross on instead, spreading the rebuilds evenly
// across the whole step. The count of them over a day is unchanged; only the
// clumping goes. The offset is derived from position so it is stable across
// reloads, and costs at most one extra step of tint error between neighbours,
// which is the same level-and-a-half nobody can see.
const TINT_SPREAD = 16;
const tintPhase = (x, y) => ((x * 7 + y * 13) % TINT_SPREAD) / TINT_SPREAD;

// Animation state for the current draw call, set once per render like
// SHADOW below. wind (0..1) sways tree canopies; t is elapsed seconds and
// phases the sway per tree; lamp (0..1) is how lit the powered lights are —
// it ramps up at dusk and back down at dawn. Zero when rendering static
// tiles. tintScale is the daylight on the sprite-cache scale above — a cache
// floors it with its own tintPhase offset to decide whether to rebuild.
let ANIM = { wind: 0, t: 0, lamp: 0, lightUptime: 0.12, lightSpeed: 1, tintScale: TINT_STEPS };

// Drop shadows give top-down structures depth and lift them off the floor.
// The direction comes from tune.shadowAngle, set once per render — rendering
// is synchronous, so module state is safe here and keeps element signatures
// free of tune plumbing.
let SHADOW = { dx: 1, dy: 1 };

// dist grows when the sun is low (dawn/dusk) so shadow length conveys the
// time of day, not just its direction. alpha comes from tune.shadowAlpha.
// The offset is quantized to SHADOW_STEP pixels rather than following the
// finer art grid, for two reasons: a shadow that creeps a pixel at a time
// reads as jitter, and the cached still layer is keyed on this offset — at
// 1px granularity every tile would repaint three times as often (staticKey).
const SHADOW_STEP = 3;
function setShadowAngle(angleDeg, dist = 1.4, alpha = 0.6) {
  const a = (angleDeg * Math.PI) / 180;
  SHADOW = {
    dx: Math.round(Math.cos(a) * dist) * SHADOW_STEP,
    dy: Math.round(Math.sin(a) * dist) * SHADOW_STEP,
    a: alpha,
  };
}

function dropShadow(g, T, x, y, w, h) {
  castShadow(() => g.a(SHADOW.a, () => g.r(x + SHADOW.dx, y + SHADOW.dy, w, h, T.far)));
}

/**
 * A smooth loop of seeded wobble: t in [0,1] around the shape, returning a
 * radius multiplier near 1. Wraps, so the outline closes without a seam.
 */
function wobbler(rng, knots = 5, amount = 0.2) {
  const k = Array.from({ length: knots }, () => 1 + (rng() * 2 - 1) * amount);
  return t => {
    const x = Math.max(0, Math.min(0.9999, t)) * knots, i = Math.floor(x), f = x - i;
    const s = f * f * (3 - 2 * f);                    // smoothstep between knots
    return k[i % knots] + (k[(i + 1) % knots] - k[i % knots]) * s;
  };
}

/**
 * An irregular disc, scanline by scanline: each side's half-width is scaled by
 * its own wobble, so a pond or a sinkhole reads as something that formed rather
 * than something drawn with a compass. Same shape of loop as disc(), so the two
 * can be nested to build a bank or a rim that follows the outline.
 */
function blob(g, cx, cy, r, c, left, right) {
  for (let dy = -r; dy <= r; dy++) {
    const t = (dy + r) / (2 * r);
    const base = Math.sqrt(Math.max(0, r * r - dy * dy));
    const l = Math.round(base * left(t)), rr = Math.round(base * right(t));
    if (l + rr < 1) continue;
    g.r(cx - l, cy + dy, l + rr + 1, 1, c);
  }
}

/** Half-widths of a blob on a given scanline — what the wave crests ride on. */
function blobSpan(r, dy, left, right) {
  const t = (dy + r) / (2 * r);
  const base = Math.sqrt(Math.max(0, r * r - dy * dy));
  return [Math.round(base * left(t)), Math.round(base * right(t))];
}

function discShadow(g, T, cx, cy, r) {
  castShadow(() => g.a(SHADOW.a, () => disc(g, cx + SHADOW.dx, cy + SHADOW.dy, r, T.far)));
}

// Pulse 0..1 for light sources (portals, fires, lamps, fungi). Phase comes
// from the light's position so neighbors don't blink in unison; a fixed 0.5
// when not animating so static renders keep today's look.
function flicker(cx, cy, speed = 6) {
  return ANIM.t ? 0.5 + 0.5 * Math.sin(ANIM.t * speed + cx * 3.1 + cy * 1.7) : 0.5;
}

// The grid is failing: a powered light is DARK most of the time, then a
// power surge hits and it strobes rapidly for a moment before dying again.
// tune.lightUptime sets how often the surges come, tune.lightFlickerSpeed
// the strobe rate; the seed staggers the schedule so tiles don't blink
// together. Zero when static — the dead grid is the daytime look too.
function powerFlicker(seed) {
  if (!ANIM.t) return 0;
  const n = smoothNoise(ANIM.t * 0.9 + seed * 13.7, seed * 7.3);
  if (n < 1 - ANIM.lightUptime) return 0;                      // dead, most of the time
  const s = Math.sin(ANIM.t * 55 * ANIM.lightSpeed + seed * 9);
  return s > -0.25 ? 0.95 : 0.1;                               // rapid surge strobe
}

// Soft tonal patches that break up flat floors without disturbing edge
// continuity (same tones as the floor itself).
function mottle(g, rng, c, n = 3, alpha = 0.12) {
  for (let i = 0; i < n; i++) {
    g.a(alpha, () => disc(g, ri(rng, 6, 111), ri(rng, 6, 111), ri(rng, 12, 21), c));
  }
}

/**
 * A plant's own green: a shade down from the palette's, and nudged warm or cool
 * per plant.
 *
 * DOWN, because the ground it stands on is drawn from the same two greens — a
 * canopy over a patch of grass was the same colour as the grass and dissolved
 * into it. The shift moves red and blue in opposite directions and leaves green
 * alone, which walks along the green arc of the wheel — toward olive one way,
 * toward pine the other — rather than off it. Where a region's own vegetation
 * is already a dry khaki the guard does nothing and it stays khaki; all it
 * stops is a shift turning a green into something that is not one.
 *
 * `hue` is quantised by the caller so this memoises against a bounded key set:
 * it is called per plant, per frame, on the animated canopies.
 */
const LEAF_DARKEN = 0.85, LEAF_HUE = 0.22;
/**
 * The canopy's own outline, a shade under its body. Two greens of similar
 * weight sitting on each other — a crown over the grass it is growing out of —
 * have no edge between them to read, and no amount of darkening the whole tree
 * fixes that because the grass moves with it. A rim does: it is a boundary
 * rather than a contrast.
 */
const LEAF_RIM = 0.58;
const leafCache = new Map();

export function leafTone(hex, hue, dark = LEAF_DARKEN) {
  const key = `${hex}|${hue}|${dark}`;
  let out = leafCache.get(key);
  if (out) return out;
  const [r, g, b] = hexRGB(hex);
  const ch = v => Math.max(0, Math.min(255, Math.round(v * dark)));
  const ng = ch(g);
  let nr = ch(r * (1 + hue * LEAF_HUE));
  const nb = ch(b * (1 - hue * LEAF_HUE));
  if (r <= g) nr = Math.min(nr, ng);                   // a green stays a green
  leafCache.set(key, out = `#${[nr, ng, nb].map(c => c.toString(16).padStart(2, '0')).join('')}`);
  return out;
}

/** Nine steps of warm-to-cool, so leafTone's cache stays small. */
const LEAF_STEPS = 4;
const leafHue = rng => ri(rng, -LEAF_STEPS, LEAF_STEPS) / LEAF_STEPS;

/**
 * Every green a plant can be drawn in, for a given palette.
 *
 * Exported because anything measuring vegetation has to know them, and twice
 * now a check has gone on passing while quietly seeing nothing: once when
 * canopies stopped using the palette entry raw, and again when they gained a
 * rim tone. Deriving the set here means the answer cannot drift from the code
 * that produces it.
 */
export function leafPalette(T) {
  const out = new Set();
  for (let k = -LEAF_STEPS; k <= LEAF_STEPS; k++) {
    for (const hex of [T.veg1, T.veg2]) {
      out.add(leafTone(hex, k / LEAF_STEPS));
      out.add(leafTone(hex, k / LEAF_STEPS, LEAF_RIM));
    }
  }
  return out;
}

function tree(g, rng, T, cx, cy, r = 9) {
  // Wind sways the canopy around the anchored trunk/shadow, so the shadow is
  // static and the canopy is not. The leaf detail is resolved from the stream
  // here and captured — the replayed closure must never touch the rng, or the
  // tile's layout would drift every frame. The hue is resolved here for the
  // same reason, and applied to the palette the replay hands back so the canopy
  // still dims with the hour.
  discShadow(g, T, cx, cy, r);
  const hue = leafHue(rng);
  const speck = rng() < 0.5 ? [ri(rng, -3, 3), ri(rng, -3, 3)] : null;
  // clumped foliage: offsets within the crown, fixed per tree so the canopy
  // keeps its shape while it sways
  const leaves = [];
  for (let i = 0, n = ri(rng, 3, 6); i < n; i++) {
    leaves.push([ri(rng, -r + 2, r - 2), ri(rng, -r + 2, r - 2), ri(rng, 1, 3)]);
  }
  // The canopy is ONE FIXED IMAGE THAT SLIDES. sway is Math.round()ed and the
  // gust formula never exceeds 0.42, so it only ever takes the five values -2..2,
  // and cy never moves at all: every frame redrew the same discs one integer
  // pixel to the left or right of where it had just drawn them. At ~55 fillRects
  // a tree that was by a wide margin the most expensive thing on the map — a
  // wooded tier-3 grid spent 144k fillRects per frame on it, held 31% of a core,
  // and still missed its 12fps target. So it is rasterised once and blitted
  // thereafter, which is the same picture for one draw call instead of 55.
  //
  // It stays in dynamic() rather than moving somewhere the day tint could reach
  // it, because the draw ORDER here is load-bearing: vegetation composes last,
  // so a canopy paints over the lit windows of the house it overhangs. Lights
  // only stay bright by landing after the tint (tintPalette exempts `glow`), so
  // anything that must cover them has to land after it too. Hence a pre-tinted
  // sprite, rebuilt when the hour has moved far enough to see (see TINT_STEPS).
  //
  // Leaf clumps reach r+1 from the centre and the speck reaches 5, so the
  // surface is padded to whichever is larger rather than to r.
  const pad = Math.max(r + 1, 5);

  // The canopy itself, wherever it is going. Both routes below go through this
  // one function at the same coordinates, so the cached path cannot quietly
  // drift from the drawn one — and the geometry stays visible to a caller that
  // is counting rects rather than looking at pixels.
  const paintCanopy = (gg, ox, oy, T) => {
    // Outer disc in the rim tone, inner one a unit smaller in the body tone —
    // one pixel of outline the whole way round, and the two agree on where the
    // edge is because they are the same routine at r and r-1. Stroking a ring
    // over the top would not: ringO rounds where disc floors.
    disc(gg, ox, oy, r, leafTone(T.veg1, hue, LEAF_RIM));
    disc(gg, ox, oy, r - 1, leafTone(T.veg1, hue));
    gg.a(0.85, () => disc(gg, ox - 3, oy - 3, Math.max(2, r - 5), leafTone(T.veg2, hue))); // lit crown
    for (const [lx, ly, lr] of leaves) {                          // canopy texture
      gg.a(0.35, () => disc(gg, ox + lx, oy + ly, lr, T.structDark));
    }
    if (speck) gg.r(ox + speck[0], oy + speck[1], 2, 2, T.structDark);
  };

  const qPhase = tintPhase(cx, cy);
  let surf;                        // undefined = untried, null = no surface to be had
  let spriteQ = null;
  dynamic((g, T) => {
    // phase from the tree's position (not rng) so neighbors sway apart
    const sway = ANIM.wind
      ? Math.round(ANIM.wind * 6 * Math.sin(ANIM.t * 1.8 + cx * 0.3 + cy * 0.23))
      : 0;
    if (surf === undefined) surf = spriteSurface((pad * 2 + 1) * SCALE, g.ctx);
    // Nothing to rasterise into or to blit with — draw it in place instead.
    if (!surf) return paintCanopy(g, cx + sway, cy, T);
    const q = Math.floor(ANIM.tintScale + qPhase);
    if (spriteQ !== q) {
      surf.ctx.clearRect(0, 0, surf.canvas.width, surf.canvas.height);
      paintCanopy(surf.g, pad, pad, T);
      spriteQ = q;
    }
    g.ctx.drawImage(surf.canvas, (cx + sway - pad) * SCALE, (cy - pad) * SCALE);
  });
}

/**
 * A surface to pre-rasterise a sprite onto, or null if this environment has no
 * such thing. Both halves have to hold: somewhere to draw the sprite, and a
 * target able to blit it. The renderer is driven through a recording stub in
 * the tests and through the image lab's own contexts as well as the game's, so
 * the answer is not always yes and the caller needs a way to fall back.
 */
function spriteSurface(size, target) {
  if (typeof target?.drawImage !== 'function') return null;
  const canvas = scratchCanvas(size, size);
  const ctx = canvas?.getContext?.('2d');
  if (typeof ctx?.clearRect !== 'function') return null;
  return { canvas, ctx, g: makeG(ctx) };
}

function bushPatch(g, rng, T, cx, cy) {
  const hue = leafHue(rng);                            // same treatment, same reason
  disc(g, cx, cy, 3, leafTone(T.veg1, hue));
  g.r(cx + ri(rng, -3, 3), cy - 3, 2, 2, leafTone(T.veg2, hue));
  g.r(cx + ri(rng, -2, 2), cy + ri(rng, -1, 2), 1, 1, leafTone(T.veg2, hue));
}

// A bare snag: forked limbs rather than the old plus-sign of four blocks.
function deadTree(g, rng, T, cx, cy) {
  g.r(cx - 1, cy - 6, 2, 13, T.structDark);                      // trunk
  for (const [dx, dy, len] of [[-1, -4, 6], [1, -1, 5], [-1, 2, 4]]) {
    const s = Math.sign(dx);
    for (let i = 0; i < len; i++) g.r(cx + s * (1 + i), cy + dy - (i >> 1), 1, 1, T.structDark);
  }
}

function glowShroom(g, rng, T, cx, cy) {
  for (let i = 0, n = ri(rng, 3, 5); i < n; i++) {               // a little colony
    const sx = cx + ri(rng, -6, 6), sy = cy + ri(rng, -6, 6);
    g.r(sx, sy, 1, 2, T.structDark);                             // stalk
    g.r(sx - 1, sy - 1, 3, 1, T.accent);                         // cap
  }
  // teal like the caps, not warm — amber halos read as lamplight
  dynamic((g, T) => g.a(0.18 + 0.24 * flicker(cx, cy, 3), () => disc(g, cx, cy, 7, T.accent))); // slow bioluminescent pulse
}

// 24 x 9 units — 4.8 x 1.8 m, a full-size car. The width was 12 (2.4 m), which
// is not a car at all: that is the legal maximum for a TRUCK, and it read
// squat next to the 9-wide compacts in the parking bays. Length is unchanged.
// Exported because the roadside pass needs the footprint to place and bound
// one, and had its own copy of these numbers to drift out of step.
export const CAR_LONG = 24, CAR_WIDE = 9;

function vehicle(g, rng, T, x, y, vert) {
  const w = vert ? CAR_WIDE : CAR_LONG, h = vert ? CAR_LONG : CAR_WIDE;
  dropShadow(g, T, x, y, w, h);
  const body = [T.rust, T.accent, T.metal][ri(rng, 0, 2)];
  g.r(x, y, w, h, body);
  if (vert) {
    g.r(x, y, w, 1, T.structLight);                              // lit roof edge
    g.r(x, y, 1, h, T.structLight);
    g.r(x + w - 1, y, 1, h, T.structDark);
    g.r(x + 2, y + 5, w - 4, 4, T.structDark);                   // windshield
    g.r(x + 2, y + 15, w - 4, 3, T.structDark);                  // rear glass
    g.r(x + 1, y + 10, w - 2, 1, T.structLight);                 // roof seam
  } else {
    g.r(x, y, 1, h, T.structLight);
    g.r(x, y, w, 1, T.structLight);
    g.r(x, y + h - 1, w, 1, T.structDark);
    g.r(x + 5, y + 2, 4, h - 4, T.structDark);
    g.r(x + 15, y + 2, 3, h - 4, T.structDark);
    g.r(x + 10, y + 1, 1, h - 2, T.structLight);
  }
}

function containerBox(g, rng, T, x, y, vert) {
  const w = vert ? 12 : 27, h = vert ? 27 : 12;
  dropShadow(g, T, x, y, w, h);
  const c = [T.rust, T.accent, T.veg1, T.metal][ri(rng, 0, 3)];
  g.r(x, y, w, h, c);
  g.r(x, y, w, 1, T.structLight);                                // lit top edge
  g.r(x, y, 1, h, T.structLight);
  g.r(x, y + h - 1, w, 1, T.structDark);
  g.r(x + w - 1, y, 1, h, T.structDark);
  g.a(0.3, () => {                              // corrugation ridges, 1px now
    if (vert) for (let ry = y + 3; ry < y + h - 2; ry += 3) g.r(x + 1, ry, w - 2, 1, T.far);
    else for (let rx = x + 3; rx < x + w - 2; rx += 3) g.r(rx, y + 1, 1, h - 2, T.far);
  });
  if (rng() < 0.5) g.a(0.5, () => {             // door end
    if (vert) g.r(x + 1, y + h - 5, w - 2, 4, T.structDark);
    else g.r(x + w - 5, y + 1, 4, h - 2, T.structDark);
  });
}

// A compact car for parking stalls (9x18, nose-in).
// 9 x 18 units — 1.8 x 3.6 m, a compact. `vert` is its default and means the
// car's LENGTH runs down the screen; a row of bays alongside a north-south road
// needs the other one, since a car always parks nose-in to the row.
function carSmall(g, rng, T, x, y, vert = true) {
  const w = vert ? 9 : 18, h = vert ? 18 : 9;
  dropShadow(g, T, x, y, w, h);
  const body = [T.rust, T.accent, T.metal][ri(rng, 0, 2)];
  g.r(x, y, w, h, body);
  if (vert) {
    g.r(x, y, 9, 1, T.structLight);
    g.r(x + 8, y, 1, 18, T.structDark);
    g.r(x + 1, y + 4, 7, 3, T.structDark);      // windshield
    g.r(x + 1, y + 12, 7, 2, T.structDark);     // rear glass
    g.r(x + 1, y + 9, 7, 1, T.structLight);     // roof seam
  } else {
    g.r(x, y, 1, 9, T.structLight);
    g.r(x, y + 8, 18, 1, T.structDark);
    g.r(x + 4, y + 1, 3, 7, T.structDark);
    g.r(x + 12, y + 1, 2, 7, T.structDark);
    g.r(x + 9, y + 1, 1, 7, T.structLight);
  }
}

// A paved parking lot: solid pad with a full curb ring, worn stall markings,
// and a random count of compact cars in the stalls.
//
// The bays run along the lot's LONG axis, and `vert` says which that is. The
// row used to be laid out along w whichever way the lot was turned, so a lot
// beside a north-south road counted its bays across its own depth and came out
// with one. The pavement paints through the pass-1 (castShadow) channel — it is
// ground, so car shadows must land ON it rather than be covered by it.
const STALL_PITCH = 12;                         // 2.4 m, a real bay
const BAY_DEPTH = 24;                           // 4.8 m, one row nose-in
/** Length of a lot holding `bays` stalls, given how stalls are counted below. */
const lotLength = bays => bays * STALL_PITCH + 6;

function parkingLot(g, rng, T, x, y, w, h, vert = false) {
  castShadow(() => {
    dither(g, rng, x, y, w, h, T.road, T.ground2, 0.06);
    g.r(x, y, w, 2, T.structDark);              // full curb ring
    g.r(x, y + h - 2, w, 2, T.structDark);
    g.r(x, y, 2, h, T.structDark);
    g.r(x + w - 2, y, 2, h, T.structDark);
    g.a(0.35, () => g.r(x + 1, y + 1, w - 2, 1, T.structLight)); // lit curb lip
  });
  const along = vert ? h : w, deep = vert ? w : h;
  const stalls = Math.floor((along - 6) / STALL_PITCH);
  for (let i = 0; i <= stalls; i++) {           // stall paint, partly worn away
    if (rng() < 0.85) {
      g.a(0.8, () => (vert
        ? g.r(x + 6, y + 3 + i * STALL_PITCH, 15, 1, T.accent)
        : g.r(x + 3 + i * STALL_PITCH, y + 6, 1, 15, T.accent)));
    }
  }
  // Nose-in, and kept inside the kerb: a car is 18 long in a 24-deep bay, so
  // there is 3 units of clearance at each end and nothing to jitter over.
  for (let i = 0; i < stalls; i++) {
    const slack = ri(rng, 0, Math.max(0, deep - 18 - 6));
    if (rng() < 0.6) {
      const at = 3 + slack;
      carSmall(g, rng, T, vert ? x + at : x + 6 + i * STALL_PITCH,
        vert ? y + 6 + i * STALL_PITCH : y + at, !vert);
    }
  }
}

/**
 * A run of pipe on the surface, lit along its crown and dark along its
 * underside, with flanges at a fixed pitch. Irrigation lattices, cable trays
 * and conveyor runs are all this shape at different widths and axes, so it
 * lives here rather than being copied into each of them.
 *
 * @param {number} th  the run's thickness across its axis
 * @param {number} [pitch] units between flanges; 0 draws a bare run
 */
function pipeRun(g, T, x, y, len, vert, th = 6, pitch = 24, body = 'metal') {
  const w = vert ? th : len, h = vert ? len : th;
  g.r(x, y, w, h, T[body]);
  if (vert) {
    g.r(x, y, 1, h, T.structLight);
    g.r(x + th - 1, y, 1, h, T.structDark);
  } else {
    g.r(x, y, w, 1, T.structLight);
    g.r(x, y + th - 1, w, 1, T.structDark);
  }
  if (!pitch) return;
  for (let d = pitch >> 1; d < len; d += pitch) {
    if (vert) g.r(x - 2, y + d, th + 4, 3, T.structDark);
    else g.r(x + d, y - 2, 3, th + 4, T.structDark);
  }
}

function tankTop(g, rng, T, cx, cy, r = 15) {
  discShadow(g, T, cx, cy, r);
  disc(g, cx, cy, r, T.metal);
  ringO(g, cx, cy, r, T.structDark);
  ringO(g, cx, cy, r - 1, T.structDark);
  g.a(0.5, () => disc(g, cx - 2, cy - 2, Math.max(2, r - 7), T.structLight)); // sheen
  g.a(0.25, () => ringO(g, cx, cy, Math.max(3, r - 5), T.structDark));        // seam ring
  for (let i = 0; i < 4; i++) {                                  // access hatches
    const a = (i * Math.PI) / 2 + 0.4;
    g.r(cx + Math.round(Math.cos(a) * (r - 4)) - 1, cy + Math.round(Math.sin(a) * (r - 4)) - 1, 3, 3, T.structDark);
  }
  g.r(cx - 2, cy - 2, 5, 5, T.structDark);                       // center cap
  g.r(cx - 1, cy - 1, 3, 3, T.metal);
}

function towerTop(g, rng, T, x, y) {
  castShadow(() => g.a(0.4, () => g.r(x + SHADOW.dx * 2, y + SHADOW.dy * 2, 15, 15, T.far))); // tall = long shadow
  g.r(x, y, 15, 15, T.struct);
  g.r(x, y, 15, 1, T.structLight);
  g.r(x, y, 1, 15, T.structLight);
  g.r(x, y + 14, 15, 1, T.structDark);
  g.r(x + 14, y, 1, 15, T.structDark);
  // lattice legs meeting at the mast
  g.a(0.45, () => {
    for (let i = 1; i < 14; i++) {
      g.r(x + i, y + i, 1, 1, T.structDark);
      g.r(x + 14 - i, y + i, 1, 1, T.structDark);
    }
  });
  g.r(x + 5, y + 5, 5, 5, T.metal);               // mast base
  g.r(x + 6, y + 6, 3, 3, T.accent);              // mast tip
}

// Full-cell floor per region backdrop. Detail lines use fixed offsets so
// they align from cell to cell.
export const FLOORS = {
  city(g, rng, T) {
    grain(g, rng, T.ground1, T.ground2, 0.18);
    mottle(g, rng, T.structDark, 2);
    mottle(g, rng, T.structLight, 1, 0.07);
    // Pavement slab seams. The pitch stays on the 30px world grid so slabs
    // line up across cells, but the seam itself is now a 1px score line
    // rather than a 3px band — the reason it had to be held at alpha 0.06 to
    // avoid reading as a grid, so it can carry a little more weight.
    g.a(0.10, () => {
      for (let i = 0; i < BASE; i += 30) { g.r(i, 0, 1, BASE, T.structDark); g.r(0, i, BASE, 1, T.structDark); }
    });
    for (let i = 0; i < 2; i++) crack(g, rng, T, ri(rng, 12, 102), ri(rng, 12, 90), ri(rng, 15, 27));
  },
  greenhouse(g, rng, T) {
    grain(g, rng, T.ground1, T.ground2, 0.25);
    mottle(g, rng, T.veg1, 3, 0.18);
    mottle(g, rng, T.veg2, 2, 0.1);
    for (let i = 0; i < 30; i++) g.r(ri(rng, 0, 116), ri(rng, 0, 116), ri(rng, 2, 5), ri(rng, 1, 2), T.veg1); // moss
    for (let i = 0; i < 24; i++) g.r(ri(rng, 0, 118), ri(rng, 0, 118), ri(rng, 1, 2), 1, T.veg2);
    for (let i = 0; i < 10; i++) g.r(ri(rng, 0, 118), ri(rng, 0, 118), 1, 1, T.glow);            // wildflowers
    for (let i = 0; i < 14; i++) {                                                              // grass blades
      const bx = ri(rng, 0, 118), by = ri(rng, 2, 118);
      g.r(bx, by - 2, 1, 3, T.veg2);
    }
  },
  harbor(g, rng, T) {
    grain(g, rng, T.ground1, T.ground2, 0.2);
    mottle(g, rng, T.structDark, 2);
    mottle(g, rng, T.water1, 2, 0.15);                         // damp silt sheen
    for (let i = 0; i < 8; i++) {                              // tide lines in the silt
      const ty = ri(rng, 6, 114);
      g.a(0.18, () => g.r(ri(rng, 0, 90), ty, ri(rng, 12, 30), 1, T.water2));
    }
  },
  foundry(g, rng, T) {
    grain(g, rng, T.ground1, T.ground2, 0.22);
    mottle(g, rng, T.rust, 2, 0.18);
    mottle(g, rng, T.structDark, 1);
    for (let i = 0; i < 18; i++) g.a(0.5, () => g.r(ri(rng, 6, 108), ri(rng, 6, 108), ri(rng, 3, 9), ri(rng, 1, 2), T.rust));
    g.a(0.15, () => g.r(0, 60, BASE, 2, T.structDark));        // expansion joint
    g.a(0.10, () => g.r(0, 59, BASE, 1, T.structLight));
  },
  underground(g, rng, T) {
    grain(g, rng, T.ground1, T.ground2, 0.18);
    mottle(g, rng, T.far, 2, 0.2);
    g.a(0.3, () => {
      for (let i = 12; i < BASE; i += 24) { g.r(i, 0, 1, BASE, T.structDark); g.r(0, i, BASE, 1, T.structDark); } // floor grating
    });
    g.a(0.15, () => {                                          // grate cross-bars
      for (let i = 0; i < BASE; i += 24) { g.r(i, 0, 1, BASE, T.structLight); g.r(0, i, BASE, 1, T.structLight); }
    });
  },
};

// ---- clearings ----
// A rest stop and a shoreline are the two places on the map nobody paved. They
// were drawn as the region's own floor with a tent or a lake dropped on it,
// which read as a pond in a car park. They get turf instead: solid across the
// middle, and thinning to nothing over the last CLEARING_MARGIN units so the
// tile still meets whatever is next door without a line around it. The seam
// has to survive in every direction — a clearing can have a street, a hazard
// or another clearing on any side.
const CLEARING_MARGIN = 14;

/** The rooms nobody paved. */
const CLEARING_RECIPES = new Set(['water', 'campsite']);

/**
 * @param {object} [cont] which sides the open ground carries on across, from
 *   room.clearing. Those sides run full bleed instead of fading, or the rest
 *   stop and the shoreline beside it each fade at the edge they share and put
 *   a strip of tarmac through the middle of one clearing.
 */
function clearing(g, rng, T, cont = {}) {
  const M = CLEARING_MARGIN;
  const x0 = cont.w ? 0 : M, y0 = cont.n ? 0 : M;
  const x1 = cont.e ? BASE : BASE - M, y1 = cont.s ? BASE : BASE - M;
  g.r(x0, y0, x1 - x0, y1 - y0, T.veg1);                      // turf
  // The fringe reaches from the turf out toward the edges that END here,
  // thinning as it goes, so the two grounds interleave rather than meeting at a
  // border. It overlaps the turf by a few units, which hides the turf's own
  // edge. A side the clearing continues across is already full bleed and has
  // nothing to fade into.
  const OPEN = BASE;                                          // a side that never fades
  for (let i = 0; i < 3600; i++) {
    const x = rng() * BASE, y = rng() * BASE;
    const d = Math.min(cont.w ? OPEN : x, cont.n ? OPEN : y,
      cont.e ? OPEN : BASE - x, cont.s ? OPEN : BASE - y);
    if (d > M + 4 || rng() > (d / M) ** 1.5) continue;
    g.r(x | 0, y | 0, ri(rng, 2, 5), ri(rng, 1, 3), rng() < 0.65 ? T.veg1 : T.veg2);
  }
  // and the turf is not a flat colour: tufts, moss and blades, the same
  // vocabulary the greenhouse floor is built from
  for (let i = 0; i < 150; i++) g.r(ri(rng, x0, x1), ri(rng, y0, y1), ri(rng, 2, 6), ri(rng, 1, 2), T.veg2);
  for (let i = 0; i < 60; i++) g.a(0.5, () => g.r(ri(rng, x0, x1), ri(rng, y0, y1), ri(rng, 1, 3), 1, T.ground2));
  for (let i = 0; i < 40; i++) {                              // blades standing up out of it
    const bx = ri(rng, x0, x1), by = ri(rng, y0 + 3, y1);
    g.r(bx, by - 2, 1, 3, T.veg2);
  }
}

// Road/path connectors, drawn ONLY where the generated road network says a
// road runs (see roadNetwork-core.js). Directions come from that network, not
// from "is this neighbour walkable" — under the old rule almost every tile was
// connected to all four sides, so every tile drew the same cross and the roads
// told you nothing about the place.
//
// The trunk gets the full roadway; branches get a narrower lane. A tile the
// network misses draws nothing at all.
// tune.roadWear (0..1) degrades surfaces: potholes, missing lane paint,
// cracks, and vegetation reclaiming the pavement.
const ROAD_W = 30, LANE_W = 15;
const RANK_W = { major: ROAD_W, minor: LANE_W };

// ---- where a road crosses a tile edge ----
//
// Locking every crossing to the edge midpoint is what forced the old shapes: a
// tile with road on opposite edges could only draw a straight line or a bow,
// and bowing each tile independently produced a sine wave with a one-tile
// period, because the curvature reversed at every seam.
//
// So the crossings move. Both tiles sharing an edge derive the SAME offset from
// that edge's own coordinates, so they agree without talking to each other —
// there is no propagation across the grid, and no way for one tile to pick a
// crossing its neighbour cannot meet.
//
// The offset is sampled from the smooth-noise field rather than hashed per
// edge. Neighbouring edges then get SIMILAR offsets and a road wanders over
// several tiles; hashing each edge independently would kink at every seam, the
// same mistake as the bow at a different frequency.
const EDGE_DRIFT = 3.5;                  // tiles per wavelength of the wander
const EDGE_SWING = 24;                   // furthest a crossing may sit off centre
const EDGE_MARGIN = 6;                   // and never nearer than this to a corner

export function edgeOffset(row, col, dir = 'n', w = LANE_W) {
  // Without a grid position there is no shared edge to key off, so every
  // crossing goes down the middle. Unhelpful, but every tile agrees — which is
  // the one property that must never break. Defaulting the position to 0,0
  // instead gave each tile the same WRONG pair of offsets (a tile's north edge
  // and its neighbour's south edge are different samples at 0,0), and the
  // seams came apart wherever a caller forgot to pass the position.
  if (row == null || col == null) return 0;
  const horizontal = dir === 'n' || dir === 's';
  // the edge's own identity: the cells either side of it name it the same way
  const r = dir === 's' ? row + 1 : row;
  const c = dir === 'e' ? col + 1 : col;
  const n = smoothNoise((c + (horizontal ? 0.5 : 0)) / EDGE_DRIFT,
    (r + (horizontal ? 0 : 0.5)) / EDGE_DRIFT + (horizontal ? 0 : 37));
  const limit = Math.min(EDGE_SWING, Math.max(0, (BASE - w) / 2 - EDGE_MARGIN));
  return Math.round((n * 2 - 1) * limit);
}

// ---- stroking a road along a path ----
// Every form is a centreline and a width. Rendering them all through one
// routine means a bend, a straight run and a junction arm get the same surface,
// curbs and paint, and a new shape is a new list of points rather than new
// drawing code — which is what the quarter-annulus could never be.

const mid2 = () => BASE / 2;
/** Where a road meets one edge of the tile, given that edge's offset. */
const edgePoint = (dir, off) => dir === 'n' ? [mid2() + off, 0]
  : dir === 's' ? [mid2() + off, BASE]
  : dir === 'w' ? [0, mid2() + off]
  : [BASE, mid2() + off];

/** A point straight in from an edge — a control point that leaves it squarely. */
const inward = (dir, [x, y], d) => dir === 'n' ? [x, y + d]
  : dir === 's' ? [x, y - d]
  : dir === 'w' ? [x + d, y]
  : [x - d, y];

/**
 * Cubic through p0 and p3 with the tangents fixed by c1/c2.
 *
 * Cubic rather than quadratic because both ends need their own tangent: a road
 * has to leave every edge at right angles to it, or its tangent disagrees with
 * the neighbour's and the seam shows as a kink.
 */
function cubic(p0, c1, c2, p3, n) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    out.push([a * p0[0] + b * c1[0] + c * c2[0] + d * p3[0],
      a * p0[1] + b * c1[1] + c * c2[1] + d * p3[1]]);
  }
  return out;
}

/** 1px line between two points, for curbs that follow a curve. */
function plot(g, [x0, y0], [x1, y1], c) {
  const steps = Math.max(1, Math.round(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  for (let i = 0; i <= steps; i++) {
    g.r(Math.round(x0 + ((x1 - x0) * i) / steps), Math.round(y0 + ((y1 - y0) * i) / steps), 1, 1, c);
  }
}

/**
 * Lay road down a centreline.
 *
 * The band is the centreline offset by half its width to either side, filled by
 * scanline. An earlier version drew one axis-aligned span per sample, picking
 * the axis from the local tangent — but that choice flips somewhere in every
 * turn, and where it did, the two families of spans met at a corner and left a
 * triangular notch bitten out of the outside of the bend. Offsetting the curve
 * has no axis to flip, and costs less: the work follows the tile's height
 * rather than the number of samples.
 *
 * `width` may be a number or a function of t, which is what lets a branch flare
 * out where it meets the roadway.
 */
function strokePath(g, rng, T, pts, p, tune, width = p.w) {
  const wear = tune.roadWear;
  const at = typeof width === 'function' ? width : () => width;
  const left = [], right = [];
  let lo = BASE, hi = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
    let tx = b[0] - a[0], ty = b[1] - a[1];
    const len = Math.hypot(tx, ty) || 1;
    tx /= len; ty /= len;
    const h = at(i / (pts.length - 1)) / 2;
    const [x, y] = pts[i];
    left.push([x - ty * h, y + tx * h]);
    right.push([x + ty * h, y - tx * h]);
    lo = Math.min(lo, y - h); hi = Math.max(hi, y + h);
  }
  const poly = [...left, ...right.slice().reverse()];

  // scanline fill; a turn can put two runs of road on one row, so cross every
  // edge and fill the crossings in pairs
  const rows = [];
  for (let y = Math.max(0, Math.floor(lo)); y < Math.min(BASE, Math.ceil(hi) + 1); y++) {
    const yc = y + 0.5, xs = [];
    for (let i = 0, n = poly.length; i < n; i++) {
      const [x1, y1] = poly[i], [x2, y2] = poly[(i + 1) % n];
      if ((y1 <= yc) === (y2 <= yc)) continue;
      xs.push(x1 + ((yc - y1) / (y2 - y1)) * (x2 - x1));
    }
    xs.sort((a, b) => a - b);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const x0 = Math.max(0, Math.round(xs[i])), x1 = Math.min(BASE, Math.round(xs[i + 1]));
      if (x1 > x0) rows.push([x0, y, x1 - x0]);
    }
  }
  if (!rows.length) return;

  let grit = 0;
  for (const [x, y, w] of rows) {
    g.r(x, y, w, 1, p.col);
    grit += (w * SCALE * SCALE * p.den) / (SPECK * SPECK);
    while (grit >= 1) {                                        // grain, paced by area covered
      g.p((x + rng() * w) * SCALE, y * SCALE, SPECK, SPECK, p.alt);
      grit--;
    }
  }
  for (let i = 0, n = Math.round(rows.length * wear * 0.06); i < n; i++) {
    const [x, y, w] = rows[Math.floor(rng() * rows.length)];   // wear, scattered along the run
    const pw = ri(rng, 3, 8);
    g.r(x + rng() * Math.max(1, w - pw), y, pw, ri(rng, 2, 4), rng() < 0.5 ? T.ground1 : T.ground2);
  }
  if (!p.isMajor) return;

  for (let i = 1; i < left.length; i++) {                      // curbs follow the band's own edges
    plot(g, left[i - 1], left[i], T.structDark);
    plot(g, right[i - 1], right[i], T.structDark);
  }
  // Dashed centreline, by arc length — and only where the surface is still a
  // full roadway. A branch flaring out to meet the road, or a run narrowing
  // into a lane, carries the median no further than its own width earns it.
  let run = 0;
  for (let i = 1; i < pts.length; i++) {
    run += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    if (at(i / (pts.length - 1)) < ROAD_W - 1) continue;
    if (run % 18 > 9 || rng() < wear) continue;
    g.r(Math.round(pts[i][0]) - 1, Math.round(pts[i][1]) - 1, 2, 2, T.accent);
  }
}

// Surface of one band: base tone, grit, potholes, and for the roadway some
// growth pushing through and the odd crack.
function potholes(g, rng, T, bx, by, bw, bh, wear) {
  const n = Math.round(bw * bh * wear * 0.006);                // px^2 — densities are per pixel
  for (let i = 0; i < n; i++) {
    const px = bx + rng() * bw, py = by + rng() * bh;
    const pw = ri(rng, 3, 9), ph = ri(rng, 2, 4);
    g.r(px, py, pw, ph, rng() < 0.5 ? T.ground1 : T.ground2);
    g.a(0.4, () => g.r(px, py, pw, 1, T.structDark));          // broken lip
  }
}

function bandSurface(g, rng, T, bx, by, bw, bh, col, alt, den, isMajor, wear) {
  dither(g, rng, bx, by, bw, bh, col, alt, den);
  potholes(g, rng, T, bx, by, bw, bh, wear);
  if (!isMajor) return;
  const tufts = Math.round(bw * bh * wear * wear * 0.02);      // growth through pavement
  for (let i = 0; i < tufts; i++) {
    g.r(bx + rng() * bw, by + rng() * bh, ri(rng, 1, 2), ri(rng, 1, 2), rng() < 0.6 ? T.veg1 : T.veg2);
  }
  if (rng() < wear) crack(g, rng, T, bx + (bw >> 1), by + (bh >> 1), ri(rng, 12, 24));
}

// ---- road forms ----
// The network already produces a good spread of shapes — a third of road tiles
// run straight through, a quarter bend, a sixth dead-end. A form turns one of
// those into a centreline: two edge crossings (wherever edgeOffset puts them)
// joined by a curve that leaves each edge squarely, so the tangent agrees with
// whatever the neighbour drew.

const CURVE_SAMPLES = 48;                                      // enough for the offset band to read smooth
const REACH_IN = BASE * 0.55;                                  // control-point pull off an edge

/**
 * A run between two edges — opposite or adjacent, it is the same curve.
 *
 * `pull` is how far the control points sit off each edge, which sets the turn
 * radius. It does not affect the tangent AT the edge, so tightening a turn
 * cannot break the seam. Junction fillets use a short pull: at the full sweep
 * a three-way turned the whole tile into one apron of asphalt.
 */
function throughPath(d0, off0, d1, off1, pull = REACH_IN) {
  const a = edgePoint(d0, off0), b = edgePoint(d1, off1);
  return cubic(a, inward(d0, a, pull), inward(d1, b, pull), b, CURVE_SAMPLES);
}


/** An arm from one edge to the apron in the middle of the junction. */
function armPath(dir, off, to) {
  const a = edgePoint(dir, off);
  return cubic(a, inward(dir, a, REACH_IN * 0.6), to, to, CURVE_SAMPLES >> 1);
}

const OPPOSITE_DIR = { n: 's', s: 'n', e: 'w', w: 'e' };

/**
 * Split a junction into the road that runs THROUGH it and the roads that join
 * that road.
 *
 * The through-road is the WIDEST pair of directions, with an opposite pair
 * winning ties so a straight run is never drawn as a turn. Taking the opposite
 * pair outright looked right until the trunk bent inside a junction: at a tile
 * where the roadway turned north-to-east and a lane left westward, the only
 * opposite pair was east-west — so the trunk was drawn as the side road, and
 * its median ran off down the lane.
 */
export function junctionParts(dirs, road) {
  const rank = d => RANK_W[road[d]] || LANE_W;
  const pairs = [];
  for (let i = 0; i < dirs.length; i++) {
    for (let j = i + 1; j < dirs.length; j++) pairs.push([dirs[i], dirs[j]]);
  }
  const opposed = ([a, b]) => (OPPOSITE_DIR[a] === b ? 1 : 0);
  pairs.sort((a, b) =>
    Math.min(rank(b[0]), rank(b[1])) - Math.min(rank(a[0]), rank(a[1])) || opposed(b) - opposed(a));

  const through = [], rest = new Set(dirs);
  for (const [a, b] of pairs) {
    if (!rest.has(a) || !rest.has(b)) continue;
    through.push([a, b]);
    rest.delete(a); rest.delete(b);
  }
  return { through, joins: [...rest] };
}

/** A round patch of road surface — the base of a turning circle. */
function pavedDisc(g, rng, T, cx, cy, r, p, wear) {
  disc(g, cx, cy, r, p.col);
  const specks = Math.round(r * r * 3 * p.den / (SPECK * SPECK));
  for (let i = 0; i < specks; i++) {                           // grain, clipped to the circle
    const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * r;
    g.p(cx + Math.cos(a) * d, cy + Math.sin(a) * d, SPECK, SPECK, p.alt);
  }
  for (let i = 0, n = Math.round(r * wear * 0.3); i < n; i++) {
    const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * (r - 3);
    g.r(cx + Math.cos(a) * d, cy + Math.sin(a) * d, ri(rng, 2, 5), 2, rng() < 0.5 ? T.ground1 : T.ground2);
  }
}

// A branch meeting the roadway shouldn't jump to lane width the moment it
// leaves — that reads as a step. Width eases from the arm's own road at the
// tile edge to the apron's at the middle, so a narrow branch flares as it joins.
const smoothstep = t => t * t * (3 - 2 * t);
const easedWidth = (w0, w1) => t => w0 + (w1 - w0) * smoothstep(t);

/**
 * The end of a track. Three ways for a road to stop, where before there was
 * only a band that ran to the middle of the tile and quit: a gated entryway
 * that never reaches the centre, a turning circle, and a hardstand apron.
 */
function terminus(g, rng, T, dir, p, tune, kind, reach, off = 0) {
  const wear = tune.roadWear;
  const horizontal = dir === 'e' || dir === 'w';
  const start = edgePoint(dir, off);
  // the run heads straight in from wherever it crossed the edge and stops
  const stop = inward(dir, start, reach);
  strokePath(g, rng, T, cubic(start, inward(dir, start, reach * 0.5), stop, stop, 90), p, tune);

  const [hx, hy] = stop.map(Math.round);
  const o = Math.round((horizontal ? hy : hx) - p.w / 2);
  if (kind === 'gate') {                                      // gated entryway
    const post = (x, y) => { g.r(x, y, 3, 3, T.structDark); g.r(x, y, 3, 1, T.structLight); };
    if (horizontal) {
      post(hx - 1, o - 4); post(hx - 1, o + p.w + 1);
      g.a(0.8, () => g.r(hx - 1, o - 3, 2, p.w + 6, T.metal)); // the barrier itself
    } else {
      post(o - 4, hy - 1); post(o + p.w + 1, hy - 1);
      g.a(0.8, () => g.r(o - 3, hy - 1, p.w + 6, 2, T.metal));
    }
  } else if (kind === 'circle') {                             // turning circle
    const r = Math.max(12, p.w);
    pavedDisc(g, rng, T, hx, hy, r, p, wear);
    g.a(0.35, () => ringO(g, hx, hy, r, T.structDark));
    // a third of the radius, not a fixed inset — at lane width a fixed one ate
    // the whole circle and left a donut
    const island = Math.max(3, Math.round(r * 0.36));
    disc(g, hx, hy, island, T.ground2);
    g.a(0.4, () => ringO(g, hx, hy, island, T.structDark));
    for (let i = 0, n = Math.round(r * 0.4); i < n; i++) {    // planted over
      const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * Math.max(2, island - 2);
      g.r(hx + Math.cos(a) * d, hy + Math.sin(a) * d, 2, 2, rng() < 0.6 ? T.veg1 : T.veg2);
    }
  } else {                                                    // hardstand apron
    const aw = ri(rng, 30, 42), ah = ri(rng, 24, 33);
    const ax = Math.round(hx - aw / 2), ay = Math.round(hy - ah / 2);
    bandSurface(g, rng, T, ax, ay, aw, ah, p.col, p.alt, p.den, p.isMajor, wear);
    g.a(0.3, () => {                                          // kerb ring so it reads as laid, not spilled
      g.r(ax, ay, aw, 1, T.structDark); g.r(ax, ay + ah - 1, aw, 1, T.structDark);
      g.r(ax, ay, 1, ah, T.structDark); g.r(ax + aw - 1, ay, 1, ah, T.structDark);
    });
  }
}

// ---- routing around what a road cannot cross ----
// Every form above is one CANDIDATE. Which one a tile uses is settled by
// sampling each candidate's centreline against the buildings the recipe is
// about to draw (measured, not declared — see makeProbeG) and taking the one
// that runs through the fewest. The seeded preference order decides ties, so
// tiles still vary wherever there is nothing to avoid.
const SAMPLE_STEP = 4;                                       // units between centreline samples

// Samples are a BOUND on where the roadway lands, not a copy of it, so they run
// a shade wider than the surface: a disc rasterises a pixel past its radius, a
// kerb sits on the outside of its band, and every coordinate rounds. Suppressing
// a building a pixel early costs nothing anybody can see; keeping one a pixel
// late is a roof on the tarmac.
const CLEARANCE = 1;

function samplesFor(spec) {
  const mid = BASE >> 1, out = [];
  const step = SAMPLE_STEP / 2;
  // A sample covers the road's full width ACROSS its direction of travel and
  // only one step ALONG it. Squaring it off instead would claim the road is as
  // long as it is wide at every point, which reads a building well ahead of
  // the road as being in its way.
  const along = (pts, w) => {
    for (let i = 0; i < pts.length; i += 2) {   // paths are ~48 samples; every other is ~6 units
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
      const horizontal = Math.abs(b[0] - a[0]) >= Math.abs(b[1] - a[1]);
      out.push({ x: pts[i][0], y: pts[i][1],
        hx: (horizontal ? step : w / 2) + CLEARANCE, hy: (horizontal ? w / 2 : step) + CLEARANCE });
    }
  };
  const blob = (x, y, w) => out.push({ x, y, hx: w / 2 + CLEARANCE, hy: w / 2 + CLEARANCE });

  if (spec.form === 'through') {
    along(throughPath(spec.d0, spec.off0, spec.d1, spec.off1), spec.p.w);
  } else if (spec.form === 'end') {
    const start = edgePoint(spec.dir, spec.off || 0);
    const stop = inward(spec.dir, start, spec.reach);
    along(cubic(start, inward(spec.dir, start, spec.reach * 0.5), stop, stop, 60), spec.p.w);
    const pad = spec.kind === 'gate' ? spec.p.w : Math.max(24, spec.p.w * 2);
    blob(stop[0], stop[1], pad);                             // the pad is the widest part
  } else {                                                    // junction: a through-road and its arms
    const { through, joins } = junctionParts(spec.dirs, spec.road);
    const runs = through.map(([a, b]) =>
      throughPath(a, spec.offs?.[a] ?? 0, b, spec.offs?.[b] ?? 0));
    runs.forEach((pts, i) => along(pts, Math.max(spec.widths[through[i][0]], spec.widths[through[i][1]])));
    const host = runs[runs.length - 1];
    const meet = host[host.length >> 1];
    for (const dir of joins) along(armPath(dir, spec.offs?.[dir] ?? 0, meet), spec.widths[dir]);
  }
  return out;
}

// Inclusive at both ends: a band whose edge lands exactly on a building's edge
// shares that row of pixels with it, and the strict comparison this replaced
// called that a clear miss — the roadway then painted a sliver of roof.
const runsThrough = (samples, b) => samples.some(s =>
  s.x + s.hx >= b.x0 && s.x - s.hx <= b.x1 && s.y + s.hy >= b.y0 && s.y - s.hy <= b.y1);

/** Seeded shuffle, so the preference order varies per tile but stays stable. */
function shuffled(list, rng) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Pick the form that runs through the fewest buildings, and report the ones it
 * still could not miss.
 *
 * The shuffle comes first so the seed decides between equally good forms —
 * reduce() keeps the earliest minimum, so a tile with nothing to avoid picks
 * freely and only a real obstruction overrides it.
 *
 * Forms flagged `fallback` sit out the shuffle and stay last. Those are the
 * wide swings it takes to clear a building — a road has to shift by half its
 * own width plus half the building's, which is a much bigger detour than looks
 * good on a tile with nothing to avoid. Keeping them last means they win only
 * by being strictly better.
 *
 * @returns {{form: object, unavoidable: object[]}}
 */
export function pickRoadForm(options, solids, rng) {
  const ordered = [
    ...shuffled(options.filter(o => !o.fallback), rng),
    ...options.filter(o => o.fallback),
  ];
  const scored = ordered.map(o => {
    const samples = samplesFor(o);
    return { o, samples, hit: solids.filter(s => runsThrough(samples, s)) };
  });
  const best = scored.reduce((a, b) => (b.hit.length < a.hit.length ? b : a));
  return { form: best.o, samples: best.samples };
}

// `road` maps each direction to the RANK of the road continuing that way, so a
// trunk tile that anchors a branch runs full width along the trunk and leaves
// as a narrow lane toward the branch. Keying width off the tile instead made
// the roadway push a full-width stub to the tile edge, and the branch only
// narrowed in the NEXT tile — the join read as a step rather than a junction.
export function connectorBands(g, rng, T, road, tune, solids = [], row = null, col = null) {
  const dirs = ['n', 'e', 's', 'w'].filter(d => road[d]);
  if (!dirs.length) return null;
  const mid = BASE >> 1;
  const wear = tune.roadWear;
  // A minor road is the SAME MATERIAL as a major one, just narrower, older and
  // less swept — so it is a shade of the road colour rather than a different
  // palette key. It used to borrow structDark, which is a building colour and
  // in several regions a different hue entirely: the lanes came out blue-grey
  // against brown-grey arterials and read as two unrelated networks. Deriving
  // it keeps the hue identical by construction, in every region, for free.
  const paint = rank => ({
    w: RANK_W[rank] || LANE_W,
    isMajor: rank === MAJOR_RANK,
    col: rank === MAJOR_RANK ? T.road : shade(T.road, 0.78),
    alt: rank === MAJOR_RANK ? T.ground2 : T.road,
    den: rank === MAJOR_RANK ? 0.08 : 0.30,
  });
  // where this tile's road meets each of its edges — agreed with the neighbour
  // on the far side of that edge, by construction
  const off = dir => edgeOffset(row, col, dir, RANK_W[road[dir]] || LANE_W);

  // Every roll happens here, whatever the tile ends up drawing, so a tile's
  // appearance never depends on which candidate wins.
  const reach = ri(rng, 30, 45);
  let options;
  if (dirs.length === 1) {
    const p = paint(road[dirs[0]]);
    // The crossing offset belongs on the option, not just on the draw call:
    // samplesFor reads it, and without it every dead end was measured running
    // down the middle of the tile while being drawn up to 24 units off it — so
    // the layout pass cleared a strip of ground the road was never on, and
    // built on the strip it was. Entry and extraction tiles are dead ends by
    // construction, which is where it showed worst.
    const o = off(dirs[0]);
    options = [
      { form: 'end', dir: dirs[0], off: o, p, kind: 'gate', reach },
      { form: 'end', dir: dirs[0], off: o, p, kind: 'circle', reach: mid },
      { form: 'end', dir: dirs[0], off: o, p, kind: 'apron', reach: mid },
    ];
  } else if (dirs.length === 2) {
    // One curve covers the straight run AND the bend: it leaves each edge
    // square to it and joins the two crossings. Where the crossings sit apart,
    // the run drifts across the tile — and because neighbouring edges get
    // similar offsets, that drift carries into the next tile instead of
    // reversing at the seam. Ranks need not match: a bend from a roadway into a
    // lane is still one run, and routing it through the junction code instead
    // gave it a square elbow on the inside of the turn.
    options = [{ form: 'through', d0: dirs[0], d1: dirs[1], off0: off(dirs[0]), off1: off(dirs[1]),
      p: paint(road[dirs[0]]), p1: paint(road[dirs[1]]) }];
  } else {
    const widths = Object.fromEntries(dirs.map(d => [d, paint(road[d]).w]));
    const offs = Object.fromEntries(dirs.map(d => [d, off(d)]));
    options = [{ form: 'junction', dirs, widths, offs, road }];
  }

  const { form: chosen, samples } = pickRoadForm(options, solids, rng);

  if (chosen.form === 'end') {
    terminus(g, rng, T, chosen.dir, chosen.p, tune, chosen.kind, chosen.reach, chosen.off);
    return samples;
  }
  if (chosen.form === 'through') {
    const wide = chosen.p.w >= chosen.p1.w ? chosen.p : chosen.p1;
    strokePath(g, rng, T, throughPath(chosen.d0, chosen.off0, chosen.d1, chosen.off1),
      wide, tune, easedWidth(chosen.p.w, chosen.p1.w));
    return samples;
  }

  const { through, joins } = junctionParts(dirs, road);
  const runs = through.map(([a, b]) => ({
    pts: throughPath(a, chosen.offs[a], b, chosen.offs[b]),
    pa: paint(road[a]), pb: paint(road[b]),
  })).sort((x, y) => Math.max(x.pa.w, x.pb.w) - Math.max(y.pa.w, y.pb.w));

  // Arms first, so the through-road's surface covers where they land on it —
  // which is what a side road joining a main road actually looks like.
  const host = runs[runs.length - 1];
  const meet = host.pts[host.pts.length >> 1];
  for (const dir of joins) {
    const p = paint(road[dir]);
    strokePath(g, rng, T, armPath(dir, chosen.offs[dir], meet), p, tune,
      easedWidth(p.w, Math.max(host.pa.w, host.pb.w)));
  }
  for (const run of runs) {
    strokePath(g, rng, T, run.pts, run.pa.w >= run.pb.w ? run.pa : run.pb, tune,
      easedWidth(run.pa.w, run.pb.w));
  }
  return samples;
}

// ---- top-down elements ----
// The roof carries most of what a building reads as, and at 3px-per-block it
// could only ever be a slab with a stripe. Everything below — parapet, panel
// seams, plant, hatch — is one or two pixels wide and simply had nowhere to
// live before.
function roofRect(g, rng, T, x, y, w, h) {
  dropShadow(g, T, x, y, w, h);
  g.r(x, y, w, h, T.struct);
  if (rng() < 0.25) g.a(0.2, () => g.r(x + 1, y + 1, w - 2, h - 2, T.structDark)); // darker roofing material
  // panel seams across the long axis, under the edging
  g.a(0.16, () => {
    if (w >= h) for (let sx = x + 6; sx < x + w - 2; sx += 6) g.r(sx, y + 2, 1, h - 4, T.structDark);
    else for (let sy = y + 6; sy < y + h - 2; sy += 6) g.r(x + 2, sy, w - 4, 1, T.structDark);
  });
  g.r(x, y, w, 1, T.structLight);                               // lit N/W edges, 1px
  g.r(x, y, 1, h, T.structLight);
  g.r(x, y + h - 1, w, 1, T.structDark);                        // shaded S/E edges
  g.r(x + w - 1, y, 1, h, T.structDark);
  if (w >= 9 && h >= 9) g.a(0.3, () => {                        // parapet lip inside the edge
    g.r(x + 2, y + 2, w - 4, 1, T.structLight);
    g.r(x + 2, y + 2, 1, h - 4, T.structLight);
    g.r(x + 2, y + h - 3, w - 4, 1, T.structDark);
    g.r(x + w - 3, y + 2, 1, h - 4, T.structDark);
  });
  if (w >= h) g.r(x + 1, y + (h >> 1), w - 2, 1, T.structDark); // roof ridge
  else g.r(x + (w >> 1), y + 1, 1, h - 2, T.structDark);
  if (rng() < 0.7) {                                            // rooftop plant
    const vx = x + 4 + ri(rng, 0, Math.max(0, w - 12)), vy = y + 4;
    g.r(vx, vy, 6, 5, T.metal);
    g.r(vx, vy, 6, 1, T.structLight);
    g.r(vx, vy + 4, 6, 1, T.structDark);
    g.a(0.5, () => { for (let i = 1; i < 5; i += 2) g.r(vx + i, vy + 1, 1, 3, T.structDark); }) // louvres
  }
  // Size-gated detail rolls its dice BEFORE the gate, never inside it. A roof
  // is measured where the recipe authored it and drawn where the layout pass
  // mirrored it to, and the canvas clamp can leave those two sizes different —
  // so `w >= 24 && rng() < 0.35` short-circuits in one pass and not the other,
  // spending a different number of rolls on a stream every later element
  // shares. What that cost was a building measured clear of the road being
  // drawn with a neighbour's mirror offset, square on the roadway. Same rule as
  // the day/night gate below: what is DRAWN may depend on size, what is DRAWN
  // FROM may not.
  const skylight = rng() < 0.35, skylightX = ri(rng, 0, Math.max(0, w - 16));
  if (w >= 24 && h >= 21 && skylight) {                         // skylight glass in a frame
    const sx = x + 4 + skylightX, sy = y + h - 11;
    g.r(sx, sy, 9, 7, T.structDark);
    g.r(sx + 1, sy + 1, 7, 5, T.water1);
    g.a(0.45, () => g.r(sx + 1, sy + 1, 7, 1, T.structLight));  // glare on the pane
  }
  const hatch = rng() < 0.5;
  if (w >= 15 && h >= 15 && hatch) {                            // roof access hatch
    const hx = x + w - 8, hy = y + h - 8;
    g.r(hx, hy, 5, 5, T.structDark);
    g.r(hx + 1, hy + 1, 3, 3, T.metal);
  }
  // Window light spilling out at the south wall after dusk. The rng calls
  // run unconditionally so the tile layout is identical day and night; the
  // lamp gate lives INSIDE the closure so dusk falling never invalidates the
  // cached static layer — it just starts painting on replay.
  const spillX = x + 1 + ri(rng, 0, Math.max(0, w - 3));
  const hasLight = rng() < 0.55;
  if (hasLight) dynamic((g, T) => {
    if (ANIM.lamp <= 0.02) return;
    const a = ANIM.lamp * powerFlicker(spillX * 5.1 + y * 3.3);
    if (a <= 0.05) return;
    g.a(0.3 * a, () => disc(g, spillX, y + h, 6, T.glow));    // pool on the ground
    g.a(0.9 * a, () => g.r(spillX - 1, y + h - 2, 3, 2, T.glow)); // the lit window
  });
}

// Building footprint with tune.buildingScale applied, anchored at x,y and
// clamped to the canvas. Larger footprints pick a composite shape from the
// seed — L-shaped or main-block-plus-wing — so blocks read as real
// structures instead of uniform rectangles.
//
// The shape is chosen from the SCALED size, before the clamp, and deliberately
// not from the clamped one. The layout pass measures a recipe where it was
// authored and then draws it mirrored, so the same building is clamped
// differently in the two passes — and each branch below spends a different
// number of draws on the stream every element after it shares. Branching on
// the clamped size therefore desynchronised measuring from drawing, and the
// elements downstream were drawn with another element's mirror offset: a
// building that had been measured clear of the road, and moved on that basis,
// landed on it. The clamp still decides what is DRAWN; it just no longer
// decides how much of the stream gets spent.
function buildingTop(g, rng, T, tune, x, y, w, h) {
  const s = tune.buildingScale;
  const full = Math.round(w * s), tall = Math.round(h * s);
  w = Math.min(full, BASE - TILE_CLAMP - x);
  h = Math.min(tall, BASE - TILE_CLAMP - y);
  const composite = full >= 30 && tall >= 27;
  const shape = rng();
  if (composite && shape < 0.35) {                              // L-shape
    const wingW = ri(rng, Math.max(12, w >> 1), Math.max(12, w - 9));
    const wingH = ri(rng, 9, Math.max(9, (h >> 1) - 3));
    roofRect(g, rng, T, x, y, w, h - wingH);
    roofRect(g, rng, T, x, y + h - wingH - 3, wingW, wingH + 3);
  } else if (composite && shape < 0.6) {                        // main block + offset wing
    roofRect(g, rng, T, x, y, w, h - 12);
    const wingW = ri(rng, 12, Math.max(12, w - 15));
    roofRect(g, rng, T, x + ri(rng, 0, Math.max(0, w - wingW)), y + h - 15, wingW, 15);
  } else {
    roofRect(g, rng, T, x, y, w, h);
  }
}

// ---- terraces ----
// A free-standing block is a thing somebody built. A TERRACE is a thing
// somebody built a street out of: a run of units under one roof, sharing party
// walls, flush along a common frontage and ragged at the back. Every structure
// on the map being free-standing is most of why it reads as a business park
// rather than a town — real built ground is mostly attached, and the gaps
// between attached ranges are what a street IS.
//
// What makes it legible from directly overhead, in order of how much it
// carries: the comb of party walls crossing the run at uneven intervals, a
// stack straddling most of those walls, a continuous ridge down the length,
// and a back line that steps where a unit was built shallower than its
// neighbours. The frontage never steps — a terrace is flush at the street,
// which is the whole reason it looks planned and a row of sheds does not.
const TERRACE_UNIT = [12, 22];           // one unit's frontage, before scale
const TERRACE_MIN = 9;                   // a last unit narrower than this joins the one before

/**
 * Plan the run: where the party walls fall, how deep each unit is, which walls
 * carry a stack. EVERY roll this element makes happens here, off the scaled
 * but UNCLAMPED length, and the drawing below spends none — same rule as
 * buildingTop and for the same reason. A terrace is measured where the recipe
 * authored it and drawn where the layout pass mirrored it to, so the canvas
 * clamp can leave those two lengths different; a plan that rolled per drawn
 * unit would spend a different number of draws in the two passes and every
 * element after it would land somewhere the measuring pass never cleared.
 */
function planTerrace(rng, run, depth, s) {
  const units = [];
  const min = Math.round(TERRACE_MIN * s);
  for (let at = 0; at < run;) {
    const wide = Math.min(Math.round(ri(rng, TERRACE_UNIT[0], TERRACE_UNIT[1]) * s), run - at);
    // A unit built shallower than its neighbours steps the back line. The
    // frontage stays flush: that is what separates a terrace from a row.
    const back = rng() < 0.3 ? Math.round(ri(rng, 3, 7) * s) : 0;
    const stack = rng() < 0.65;
    const lit = rng() < 0.5;                 // whether this one is still occupied
    units.push({ at, wide, back, stack, lit });
    at += wide;
    if (run - at > 0 && run - at < min) { units[units.length - 1].wide += run - at; at = run; }
  }
  return units;
}

/**
 * @param {number} w,h  the footprint in TILE space; the longer of the two is
 *   the run unless vert says otherwise, and y (or x, when vert) is the frontage
 */
function terraceTop(g, rng, T, tune, x, y, w, h, vert) {
  const s = tune.buildingScale;
  const full = Math.round((vert ? h : w) * s), deep = Math.round((vert ? w : h) * s);
  const units = planTerrace(rng, full, deep, s);
  // Clamped only now, and only for what is DRAWN.
  const run = Math.min(full, BASE - TILE_CLAMP - (vert ? y : x));
  const across = Math.min(deep, BASE - TILE_CLAMP - (vert ? x : y));
  if (run < 6 || across < 6) return;
  // `a` runs along the terrace, `c` across it from the frontage back, so one
  // body of code draws both a street facing north and one facing west.
  const put = (a, c, la, lc, col) => {
    const la2 = Math.min(la, run - a), lc2 = Math.min(lc, across - c);
    if (a >= run || c >= across || la2 <= 0 || lc2 <= 0) return;
    if (vert) g.r(x + c, y + a, lc2, la2, col);
    else g.r(x + a, y + c, la2, lc2, col);
  };
  const atXY = (a, c) => (vert ? [x + c, y + a] : [x + a, y + c]);
  dropShadow(g, T, x, y, vert ? across : run, vert ? run : across);
  for (const u of units) {
    const deepU = across - u.back;
    put(u.at, 0, u.wide, deepU, T.struct);                      // the unit's slab
    put(u.at, deepU - 1, u.wide, 1, T.structDark);              // its own back edge, stepped
  }
  // The roof reads as pitched: the frontage slope catches the sun, the rear
  // slope does not, and the ridge is the line between them.
  const ridge = across >> 1;
  g.a(0.13, () => put(0, 0, run, ridge, T.structLight));
  g.a(0.13, () => put(0, ridge, run, across - ridge, T.structDark));
  // Rafters across both slopes, close enough together to read as texture
  // rather than as detail — the same job roofRect's panel seams do, at the
  // pitch a house roof is boarded at.
  g.a(0.1, () => { for (let a = 3; a < run; a += 3) put(a, 1, 1, across - 2, T.structDark); });
  put(0, ridge, run, 1, T.structDark);
  put(0, 0, run, 1, T.structLight);                             // the flush frontage
  put(0, 0, 1, across, T.structLight);                          // and the lit end of the run
  put(run - 1, 0, 1, across, T.structDark);
  // The comb. A party wall is one line, not two edges and a gap — that is the
  // whole difference between a terrace and two buildings standing near enough
  // to look like one.
  for (const u of units) {
    if (!u.at) continue;
    put(u.at - 1, 0, 1, across - u.back, T.structDark);
    put(u.at, 0, 1, across - u.back, T.structLight);
  }
  // Stacks on the party walls, which is what says HOUSES from overhead.
  const stackW = Math.max(2, Math.round(2 * s));
  for (const u of units) {
    if (!u.stack || !u.at) continue;
    put(u.at - stackW, ridge - 3, stackW * 2, 5, T.structDark);
    put(u.at - stackW, ridge - 3, stackW * 2, 1, T.structLight);
  }
  // Lit windows on the frontage after dusk, unit by unit. Without this a
  // street of terraces is the one black stretch on a map whose every
  // free-standing block glows — roofRect has spilled light since it was
  // written, and a terrace is the same building repeated. Same shape of gate,
  // for the same reason: the rolls are already spent, the lamp test lives
  // inside the closure so nightfall never invalidates the cached still layer.
  for (const u of units) {
    if (!u.lit || u.at + u.wide > run) continue;
    const [wx, wy] = atXY(u.at + (u.wide >> 1), 0);
    dynamic((g, T) => {
      if (ANIM.lamp <= 0.02) return;
      const a = ANIM.lamp * powerFlicker(wx * 4.7 + wy * 2.9);
      if (a <= 0.05) return;
      g.a(0.28 * a, () => disc(g, wx, wy, 5, T.glow));           // pooled on the pavement
      g.a(0.9 * a, () => (vert ? g.r(wx, wy - 1, 2, 3, T.glow) : g.r(wx - 1, wy, 3, 2, T.glow)));
    });
  }
}

function transformerPad(g, rng, T, x, y) {
  dropShadow(g, T, x, y, 21, 21);
  g.r(x, y, 21, 21, T.structDark);                              // fenced compound
  g.r(x + 1, y + 1, 19, 19, T.ground2);
  g.r(x + 3, y + 3, 15, 15, T.metal);                           // the transformer
  g.r(x + 3, y + 3, 15, 1, T.structLight);
  g.r(x + 3, y + 17, 15, 1, T.structDark);
  g.a(0.55, () => {                                             // cooling fins
    for (let i = x + 5; i < x + 17; i += 3) g.r(i, y + 5, 1, 11, T.structDark);
  });
  g.r(x + 8, y + 9, 5, 3, T.structDark);                        // core block
  for (const [bx, by] of [[x + 4, y + 4], [x + 16, y + 4], [x + 4, y + 16], [x + 16, y + 16]]) {
    g.r(bx, by, 1, 1, T.accent);                                // bushing terminals
  }
}

function crateCluster(g, rng, T, x, y, cols, rows) {
  dropShadow(g, T, x, y, cols * 12, rows * 12);
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      if (rng() < 0.15) continue;
      const c = rng() < 0.5 ? T.metal : T.struct;
      const bx = x + cx * 12, by = y + cy * 12;
      g.r(bx, by, 11, 11, c);
      g.r(bx, by, 11, 1, T.structLight);                        // lit lid edge
      g.r(bx, by, 1, 11, T.structLight);
      g.r(bx, by + 10, 11, 1, T.structDark);
      g.r(bx + 10, by, 1, 11, T.structDark);
      g.a(0.4, () => {                                          // lid slats
        g.r(bx + 1, by + 3, 9, 1, T.structDark);
        g.r(bx + 1, by + 7, 9, 1, T.structDark);
      });
      if (rng() < 0.35) g.r(bx + 3, by + 4, 4, 3, T.accent);    // stencilled marking
    }
  }
}

function fenceRing(g, rng, T, x, y, w, h, conn) {
  // chain-link: posts every 9px with a thin rail between them
  g.a(0.75, () => {
    g.r(x, y, w, 1, T.structDark);
    g.r(x, y + h - 1, w, 1, T.structDark);
    g.r(x, y, 1, h, T.structDark);
    g.r(x + w - 1, y, 1, h, T.structDark);
  });
  for (let px = x; px < x + w; px += 9) {                       // posts
    g.r(px, y, 1, 2, T.metal);
    g.r(px, y + h - 2, 1, 2, T.metal);
  }
  for (let py = y; py < y + h; py += 9) {
    g.r(x, py, 2, 1, T.metal);
    g.r(x + w - 2, py, 2, 1, T.metal);
  }
  for (const [cx, cy] of [[x, y], [x + w - 2, y], [x, y + h - 2], [x + w - 2, y + h - 2]]) {
    g.r(cx, cy, 2, 2, T.metal);                                // corner posts
  }
  // gaps where a connector passes through
  const gx = x + (w >> 1) - 9, gy = y + (h >> 1) - 9;
  if (conn.n) g.r(gx, y, 21, 2, T.ground1);
  if (conn.s) g.r(gx, y + h - 2, 21, 2, T.ground1);
  if (conn.w) g.r(x, gy, 2, 21, T.ground1);
  if (conn.e) g.r(x + w - 2, gy, 2, 21, T.ground1);
}

function tentTop(g, rng, T, x, y, s = 21) {
  for (let i = 0; i < s; i++) {
    const half = Math.min(i, s - 1 - i) + 1;
    g.r(x + (s >> 1) - half + 1, y + i, half * 2 - 1, 1, T.struct); // diamond canopy
  }
  g.r(x + (s >> 1), y, 1, s, T.structLight);                   // ridge seam
  g.a(0.3, () => {                                             // guy lines and panel folds
    g.r(x + (s >> 1) - 4, y + 4, 1, s - 8, T.structDark);
    g.r(x + (s >> 1) + 4, y + 4, 1, s - 8, T.structDark);
  });
}

function fireRing(g, rng, T, cx, cy) {
  for (let i = 0; i < 9; i++) {                                 // ring of stones
    const a = (i / 9) * Math.PI * 2;
    g.r(cx + Math.round(Math.cos(a) * 9) - 1, cy + Math.round(Math.sin(a) * 9) - 1, 3, 3, T.structDark);
  }
  g.a(0.5, () => disc(g, cx, cy, 6, T.structDark));             // ash bed
  dynamic((g, T) => {
    const f = flicker(cx, cy, 9);                     // fast flame flicker
    g.a(0.1 + 0.15 * f, () => disc(g, cx, cy, 13, T.glow));
    g.a(0.35 + 0.3 * f, () => disc(g, cx, cy, 6, T.glow));
    g.a(0.9, () => disc(g, cx, cy, 2 + (f > 0.5 ? 1 : 0), T.glow));
  });
}

// ===============================
// ELEMENT REGISTRY + RECIPE INTERPRETER
// ===============================
// Recipes in RENDER_CONFIG.recipes are pure data: lists of element specs
// that reference this registry by name. Every element receives the resolved
// params object p; color params are palette key names (literal hex also
// works). Adding a new drawable primitive = one entry here; composing a new
// room look = config only.
const pcolor = (T, c, fallback) => T[c] || c || T[fallback];

export const ELEMENTS = {
  rect(g, rng, T, tune, conn, p) {
    const c = pcolor(T, p.color, 'structDark');
    if (p.alpha != null) g.a(p.alpha, () => g.r(p.x, p.y, p.w, p.h, c));
    else g.r(p.x, p.y, p.w, p.h, c);
  },
  // ---- skill-anchor primitives ----
  // The deployed pad every anchor stands on. It does the work that collision
  // detection would otherwise have to: an anchor landing on a warehouse roof
  // reads as apparatus INSTALLED on the roof rather than a mis-layered sprite,
  // and the player learns one silhouette instead of fifteen. shape is set per
  // faction so allegiance is legible even when the device is too small to read.
  anchorPlate(g, rng, T, tune, conn, p) {
    const { x, y } = p, w = p.w ?? 24, h = p.h ?? 24;
    dropShadow(g, T, x, y, w, h);
    g.r(x, y, w, h, T.structDark);
    g.r(x + 1, y + 1, w - 2, h - 2, T.ground2);
    if (p.shape === 'cut') {                       // veil: chamfered corners
      for (let i = 0; i < 4; i++) {
        g.r(x + i, y + i, 4 - i, 1, T.structDark);
        g.r(x + w - 4, y + i, 4 - i, 1, T.structDark);
      }
    } else if (p.shape === 'round') {              // wilders: softened edge
      for (const [cx, cy] of [[x + 1, y + 1], [x + w - 2, y + 1], [x + 1, y + h - 2], [x + w - 2, y + h - 2]]) {
        g.r(cx, cy, 1, 1, T.structDark);
      }
    }
    g.r(x + 1, y + 1, w - 2, 1, T.structLight);    // lit N/W lip
    g.r(x + 1, y + 1, 1, h - 2, T.structLight);
    g.r(x + 1, y + h - 2, w - 2, 1, T.structDark);
    for (const [bx, by] of [[x + 2, y + 2], [x + w - 3, y + 2], [x + 2, y + h - 3], [x + w - 3, y + h - 3]]) {
      g.r(bx, by, 1, 1, T.metal);                  // corner bolts
    }
  },
  // One primitive covering the dish/emitter/bloom family: a disc body, an
  // optional rim, concentric ground rings and an optional feed strut. Three
  // anchors use it at different radii and colors rather than three near-copies.
  dish(g, rng, T, tune, conn, p) {
    const { x, y } = p, r = p.r ?? 9;
    const c = pcolor(T, p.color, 'accent');
    discShadow(g, T, x, y, r);
    for (let i = 1; i <= (p.rings ?? 0); i++) {     // ripples on the deck
      g.a(0.3 - i * 0.06, () => ringO(g, x, y, r + i * 4, c));
    }
    disc(g, x, y, r, T.metal);
    ringO(g, x, y, r, c);
    g.a(0.5, () => disc(g, x - 1, y - 1, Math.max(1, r - 4), T.structLight));
    if (p.arm) {                                   // feed strut off the rim
      g.r(x, y - r - 3, 1, r + 3, T.structDark);
      g.r(x - 1, y - r - 4, 3, 2, T.metal);
    }
    g.r(x - 1, y - 1, 3, 3, T.structDark);
  },
  // A landing pad with corner lights and one airframe sitting on it.
  dronePad(g, rng, T, tune, conn, p) {
    const { x, y } = p, s = p.s ?? 16;
    g.r(x, y, s, s, T.structDark);
    // the cross and the lights are what make this read as a pad rather than a
    // dark square, so they carry nearly full weight
    g.a(0.85, () => {                              // touchdown cross
      g.r(x + (s >> 1) - 1, y + 2, 2, s - 4, T.accent);
      g.r(x + 2, y + (s >> 1) - 1, s - 4, 2, T.accent);
    });
    for (const [cx, cy] of [[x, y], [x + s - 3, y], [x, y + s - 3], [x + s - 3, y + s - 3]]) {
      g.r(cx, cy, 3, 3, T.structDark);             // light housings
      g.r(cx + 1, cy + 1, 2, 2, T.glow);           // pad lights
    }
    const dx = x + (s >> 1) - 3, dy = y + (s >> 1) - 3;
    dropShadow(g, T, dx, dy, 6, 6);
    g.r(dx + 1, dy + 1, 4, 4, T.metal);            // parked airframe
    for (const [rx, ry] of [[dx - 1, dy - 1], [dx + 4, dy - 1], [dx - 1, dy + 4], [dx + 4, dy + 4]]) {
      g.r(rx, ry, 3, 3, T.structDark);             // rotor booms
    }
  },
  speckle(g, rng, T, tune, conn, p) {                // n scattered specks in an area
    const c = pcolor(T, p.color, 'structLight');
    for (let i = 0; i < (p.n ?? 8); i++) {
      // scraps, not pixels — n is a count in the recipe data, so the specks
      // themselves have to carry the area they used to cover as 3px blocks
      g.a(p.alpha ?? 1, () => g.r(p.x + ri(rng, 0, p.w), p.y + ri(rng, 0, p.h), ri(rng, 2, 4), ri(rng, 1, 3), c));
    }
  },
  darkWash(g, rng, T, tune, conn, p) {
    g.a(p.alpha ?? 0.15, () => g.r(0, 0, BASE, BASE, T.far));
  },
  building(g, rng, T, tune, conn, p) { buildingTop(g, rng, T, tune, p.x, p.y, p.w, p.h); },
  terrace(g, rng, T, tune, conn, p) { terraceTop(g, rng, T, tune, p.x, p.y, p.w, p.h, p.vert); },
  storefront(g, rng, T, tune, conn, p) {             // scaled roof with an awning row
    const w = Math.min(Math.round(p.w * tune.buildingScale), BASE - TILE_CLAMP - p.x);
    const h = Math.min(Math.round(p.h * tune.buildingScale), 60 - p.y);
    roofRect(g, rng, T, p.x, p.y, w, h);
    for (let i = 0; i < w; i += 3) {                 // striped awning, 3px bands
      g.r(p.x + i, p.y + h, 3, 6, (i / 3) % 2 ? T.accent : T.structDark);
    }
    g.a(0.4, () => g.r(p.x, p.y + h + 5, w, 1, T.structDark)); // awning drop shadow
  },
  stalls(g, rng, T, tune, conn, p) {                 // market stall tops
    for (let sx = 18; sx < 90; sx += 27) {
      const sy = ri(rng, 84, 93);
      g.r(sx, sy, 15, 12, T.struct);
      g.r(sx, sy, 15, 1, T.structLight);
      g.r(sx, sy + 11, 15, 1, T.structDark);
      g.a(0.35, () => { for (let i = 3; i < 15; i += 4) g.r(sx + i, sy + 1, 1, 10, T.structDark); });
      g.r(sx + 3, sy + 3, 9, 4, T.rust);             // goods on the counter
    }
  },
  transformerPad(g, rng, T, tune, conn, p) { transformerPad(g, rng, T, p.x, p.y); },
  tank(g, rng, T, tune, conn, p) { tankTop(g, rng, T, p.x, p.y, p.r ?? 15); },
  tower(g, rng, T, tune, conn, p) { towerTop(g, rng, T, p.x, p.y); },
  // The monumental tier, exposed to the recipes. These are authored for the
  // signature layer (see TD_SIGNATURE) but nothing about them is region-bound,
  // so a recipe that wants a silo yard or a glasshouse can just ask for one.
  silo(g, rng, T, tune, conn, p) { silo(g, rng, T, p.x, p.y, Math.round((p.r ?? 12) * tune.buildingScale)); },
  gantry(g, rng, T, tune, conn, p) { gantry(g, rng, T, p.x, p.y, p.len ?? 54, !!p.vert); },
  glassSpan(g, rng, T, tune, conn, p) {
    glassSpan(g, rng, T, p.x, p.y, Math.round((p.w ?? 54) * tune.buildingScale),
      Math.round((p.h ?? 24) * tune.buildingScale));
  },
  towerCore(g, rng, T, tune, conn, p) {
    towerCore(g, rng, T, p.x, p.y, Math.round((p.w ?? 33) * tune.buildingScale),
      Math.round((p.h ?? 33) * tune.buildingScale));
  },
  crates(g, rng, T, tune, conn, p) { crateCluster(g, rng, T, p.x, p.y, p.cols ?? 2, p.rows ?? 2); },
  container(g, rng, T, tune, conn, p) { containerBox(g, rng, T, p.x, p.y, !!p.vert); },
  vehicle(g, rng, T, tune, conn, p) { vehicle(g, rng, T, p.x, p.y, !!p.vert); },
  barrier(g, rng, T, tune, conn, p) {                // striped roadblock
    for (let i = 0; i < 6; i++) g.r(p.x + i * 3, p.y, 3, 6, i % 2 ? T.accent : T.structDark);
    g.r(p.x, p.y + 6, 18, 1, T.structDark);          // base shadow line
    g.r(p.x + 1, p.y - 1, 16, 1, T.structLight);     // lit top rail
  },
  fence(g, rng, T, tune, conn, p) {                  // gaps: 1 opens where connectors pass
    fenceRing(g, rng, T, p.x, p.y, p.w, p.h, p.gaps ? conn : { n: false, e: false, s: false, w: false });
  },
  pipeline(g, rng, T, tune, conn, p) { pipeRun(g, T, p.x, p.y, p.w, false, 6, 24); },
  tree(g, rng, T, tune, conn, p) { tree(g, rng, T, p.x, p.y, p.r ?? 9); },
  bush(g, rng, T, tune, conn, p) { bushPatch(g, rng, T, p.x, p.y); },
  foliage(g, rng, T, tune, conn, p) { foliageClump(g, rng, T, p.x, p.y, p.size ?? 5); },
  crack(g, rng, T, tune, conn, p) { crack(g, rng, T, p.x, p.y, p.len ?? 30); },
  rubble(g, rng, T, tune, conn, p) { rubble(g, rng, T, p.x, p.x + p.w, p.y, p.y + p.h, p.n ?? 12); },
  parkingLot(g, rng, T, tune, conn, p) { parkingLot(g, rng, T, p.x, p.y, p.w ?? 66, p.h ?? 33); },
  tent(g, rng, T, tune, conn, p) { tentTop(g, rng, T, p.x, p.y, p.s ?? 21); },
  fireRing(g, rng, T, tune, conn, p) { fireRing(g, rng, T, p.x, p.y); },
  manhole(g, rng, T, tune, conn, p) {
    disc(g, p.x, p.y, 4, T.structDark);
    ringO(g, p.x, p.y, 4, T.metal);
    g.a(0.4, () => g.r(p.x - 3, p.y, 7, 1, T.structLight));     // lid slot
  },
  streetLamp(g, rng, T, tune, conn, p) {             // post always visible; head lights at
    g.r(p.x, p.y, 2, 6, T.structDark);               // dusk on the intermittent grid
    g.r(p.x - 1, p.y - 4, 4, 4, T.metal);            // lamp head
    g.r(p.x - 1, p.y - 4, 4, 1, T.structLight);
    dynamic((g, T) => {
      if (ANIM.lamp <= 0.02) return;
      const a = ANIM.lamp * powerFlicker(p.x * 7.7 + p.y * 3.1);
      if (a <= 0.05) return;
      g.a(0.3 * a, () => disc(g, p.x, p.y - 3, 9, T.glow));
      g.a(0.9 * a, () => g.r(p.x - 1, p.y - 3, 3, 2, T.glow));
    });
  },
  sinkhole(g, rng, T, tune, conn, p) {
    const r = p.r ?? 21;
    voidPit(g, rng, T, p.x, p.y, r);
    for (let i = 0; i < 3; i++) {
      crack(g, rng, T, p.x + ri(rng, -r - 3, r + 3), p.y + ri(rng, -r - 3, r + 3), ri(rng, 15, 27));
    }
    // Spoil lands on the RIM, in a ring — scattering it across the pit's
    // bounding box put four fifths of it inside the shaft, debris hanging in a
    // hole that goes down to nothing.
    for (let i = 0; i < 8; i++) {
      const a = rng() * Math.PI * 2, d = r + ri(rng, 1, 7);
      rubbleChunk(g, rng, T, p.x + Math.cos(a) * d, p.y + Math.sin(a) * d);
    }
  },
  // A rift rather than a shaft. Built as one continuous swept shape — a chain
  // of overlapping discs each drew its own rim, and the result read as a row of
  // scalloped circles rather than as ground that had torn open.
  chasm(g, rng, T, tune, conn, p) {
    const len = p.len ?? 60, r = p.r ?? 15, vert = !!p.vert;
    const half = len / 2;
    // half-width along the rift: pinched to nothing at both tips, uneven between
    const knots = [];
    for (let i = 0; i <= 6; i++) knots.push(0.6 + rng() * 0.55);
    const widthAt = t => {
      const taper = Math.pow(Math.sin(Math.PI * Math.min(1, Math.max(0, t))), 0.55);
      const k = t * 6, i = Math.min(5, Math.floor(k)), f = k - i;
      const bend = knots[i] + (knots[i + 1] - knots[i]) * (f * f * (3 - 2 * f));
      return Math.max(1, r * taper * bend);
    };
    const drift = t => Math.round(Math.sin(t * Math.PI * 1.6) * r * 0.28);

    const spans = [];
    for (let d = 0; d <= len; d++) {
      const t = d / len, h = Math.round(widthAt(t)), off = drift(t);
      const along = Math.round(-half + d);
      spans.push(vert
        ? [Math.round(p.x + off - h), Math.round(p.y + along), h * 2, 1]
        : [Math.round(p.x + along), Math.round(p.y + off - h), 1, h * 2]);
    }
    for (const [x, y, w, h] of spans) g.r(x, y, w, h, T.far);      // the ground below
    g.a(0.6, () => {                                               // and the dark of it
      for (const [x, y, w, h] of spans) {
        const inset = Math.round((vert ? w : h) * 0.3);
        if ((vert ? w : h) - inset * 2 < 2) continue;
        g.r(vert ? x + inset : x, vert ? y : y + inset,
          vert ? w - inset * 2 : w, vert ? h : h - inset * 2, '#000000');
      }
    });
    for (const [x, y, w, h] of spans) {                            // crumbling rim
      if (vert) { g.r(x, y, 1, 1, T.structDark); g.r(x + w - 1, y, 1, 1, T.structDark); }
      else { g.r(x, y, 1, 1, T.structDark); g.r(x, y + h - 1, 1, 1, T.structDark); }
    }
    // Lit lip: ONE pixel on the sunward edge of each span, whichever way the
    // rift runs. Taking the span's full width when it ran north-south washed
    // the whole chasm pale instead of catching its edge.
    g.a(0.3, () => { for (const [x, y] of spans) g.r(x, y, 1, 1, T.structLight); });
    for (let i = 0; i < 5; i++) {                                  // ground splitting away
      const [x, y] = spans[ri(rng, 0, spans.length - 1)];
      crack(g, rng, T, x + ri(rng, -6, 6), y + ri(rng, -6, 6), ri(rng, 12, 27));
    }
    // Spoil lines both LIPS of the rift rather than filling its bounding box:
    // a rift is mostly hole, so a box scatter dropped most of the debris into
    // it. Each chunk picks a span and steps out past that span's own edge, so
    // the heap follows the tear however it wanders and pinches in at the tips.
    for (let i = 0; i < 14; i++) {
      const [x, y, w, h] = spans[ri(rng, 0, spans.length - 1)];
      const out = ri(rng, 1, 7), far = rng() < 0.5;
      rubbleChunk(g, rng, T,
        vert ? (far ? x + w + out : x - out - 4) : x,
        vert ? y : (far ? y + h + out : y - out - 4));
    }
  },
  pond(g, rng, T, tune, conn, p) {                   // the water tile's lake
    const C = POND.cx, R = POND.r;
    // A shoreline is not a circle: each side of every scanline gets its own
    // smooth wobble, and the bank, the water and the deep all follow the same
    // one so they stay concentric with it.
    const left = wobbler(rng, 6, 0.16), right = wobbler(rng, 6, 0.16);
    blob(g, C, C, R + 3, T.ground2, left, right);    // bank
    blob(g, C, C, R, T.water1, left, right);
    g.a(0.25, () => blob(g, C, C, R - 9, T.water2, left, right));  // deeper in the middle
    for (let i = 0; i < 26; i++) {                   // sparkle glints
      const dy = ri(rng, -R + 6, R - 6);
      const [l, r] = blobSpan(R, dy, left, right);
      if (l + r < 14) continue;
      g.r(C - l + 3 + ri(rng, 0, Math.max(1, l + r - 9)), C + dy, ri(rng, 3, 8), 1, T.water2);
    }
    // wave crests drift across the pond (position-phased, no rng, so the
    // pond layout stays identical while the crests slide when animating)
    dynamic((g, T) => {
      for (let i = -4; i <= 4; i++) {
        const wy = C + i * 8;
        const half = Math.min(...blobSpan(R, wy - C, left, right));
        if (half < 12) continue;
        const xo = Math.round(7 * Math.sin(ANIM.t * 0.8 + i * 1.9));
        g.a(0.5, () => {
          g.r(C - (half >> 1) + xo, wy, 9, 1, T.water2);
          g.r(C + (half >> 2) + xo, wy + 5, 7, 1, T.water2);
        });
      }
    });
    const dock = conn.e ? [81, 54, 36, 12] : conn.w ? [3, 54, 36, 12] : conn.n ? [54, 3, 12, 36] : [54, 81, 12, 36];
    g.r(...dock, T.rust);                            // plank walkway
    g.a(0.4, () => {                                 // planks
      if (dock[2] > dock[3]) for (let i = 3; i < dock[2]; i += 6) g.r(dock[0] + i, dock[1], 1, dock[3], T.structDark);
      else for (let i = 3; i < dock[3]; i += 6) g.r(dock[0], dock[1] + i, dock[2], 1, T.structDark);
    });
    g.r(dock[0], dock[1], dock[2] > dock[3] ? dock[2] : 1, dock[2] > dock[3] ? 1 : dock[3], T.structLight);
  },
  reactorCore(g, rng, T, tune, conn, p) {
    const C = BASE >> 1;
    disc(g, C, C, 27, T.metal);
    ringO(g, C, C, 27, T.structDark);
    ringO(g, C, C, 26, T.structDark);
    ringO(g, C, C, 15, T.structDark);
    g.a(0.3, () => {                                 // containment segment lines
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        for (let d = 16; d < 27; d++) g.r(C + Math.round(Math.cos(a) * d), C + Math.round(Math.sin(a) * d), 1, 1, T.structDark);
      }
    });
    // the core's own light: dynamic so the day tint never dims it (T.glow is
    // exempt from tinting, and the composite-time multiply is not)
    dynamic((g, T) => {
      g.a(0.6, () => disc(g, C, C, 9, T.glow));
      g.a(0.9, () => disc(g, C, C, 4, T.glow));
      g.r(C - 1, C - 1, 3, 3, T.accent);
    });
    for (const [cx, cy] of [[21, 21], [96, 21], [21, 96], [96, 96]]) g.r(cx, cy, 6, 6, T.accent); // marker pads
  },
  portalPad(g, rng, T, tune, conn, p) {
    // pad only — the energy glow is drawn by portalGlow() as the very last
    // pass so nothing (flood water, growth, clouds) can cover it
    const C = BASE >> 1;
    disc(g, C, C, 15, T.metal);
    ringO(g, C, C, 15, T.accent);
    ringO(g, C, C, 14, T.accent);
    g.a(0.35, () => ringO(g, C, C, 10, T.structDark));
  },
};

// Resolve a config param value: [min,max] draws a seeded integer in range;
// { pick: [...] } draws a seeded choice (entries may be scalars or ranges);
// anything else passes through.
function resolveVal(v, rng) {
  if (Array.isArray(v)) return ri(rng, v[0], v[1]);
  if (v && typeof v === 'object' && v.pick) return resolveVal(v.pick[ri(rng, 0, v.pick.length - 1)], rng);
  return v;
}

const SPEC_KEYS = new Set(['el', 'chance', 'when', 'repeat']);

// Interpret a recipe definition from RENDER_CONFIG.recipes. Purely a
// function of the rng stream, so the two-pass shadow/content replay draws
// identical layouts.
// ox/oy shift every element's x/y, so the same interpreter can run a recipe
// authored at the tile origin (recipes) or one authored plate-relative and
// dropped somewhere else on the tile (skill anchors, below).
/**
 * What a road may not be driven through: things somebody is inside, or a hole
 * in the ground. Everything absent from this list — a parking lot, a gate, a
 * fence, a pipeline, crates, a container, rubble, scatter, vegetation — a road
 * is free to cross, and generally looks better for it. Crates and containers
 * are clutter that gets moved, not structures that get built around.
 *
 * Full-tile elements (pond, reactorCore, portalPad, stalls) are deliberately
 * NOT here: they are the whole tile, so there is nothing to route around, and
 * a road running up to a reactor or into the water is the point.
 */
export const SOLID_ELEMENTS = new Set(['building', 'terrace', 'storefront', 'tower', 'tank',
  'transformerPad', 'sinkhole', 'tent', 'silo', 'glassSpan', 'towerCore']);

/**
 * The water tile's lake, as the disc it is actually drawn as.
 *
 * Exported because the raid team has to keep out of it and a bounding box will
 * not do — the box of a disc this size is very nearly the whole tile, which
 * would say the dry corners are wet. `bank` is the far edge of the shoreline
 * ring: water below `r`, walkable shingle between the two.
 */
export const POND = { cx: BASE >> 1, cy: BASE >> 1, r: 39, bank: 42 };


// ---- building density by distance from a road ----
// People build where they can get to. A map that draws every recipe at full
// strength everywhere reads as one uniform sprawl; thinning the built stock
// with distance from the roadway gives the map a grain — frontage along the
// trunk, back lots behind it, and near-open ground where nothing runs.

/**
 * What thins out with distance. Built structures only: the things somebody put
 * up because there was a way to reach them. Ground works — a parking lot, a
 * fence, a pipeline, a hardstand, crates — stay, and read the better for the
 * buildings having gone: an empty lot is exactly what the edge of a settlement
 * looks like.
 *
 * Tents and sinkholes are absent on purpose, though both are solid: a camp is
 * one tent, and a shaft is the whole point of the tile it opened on.
 */
const BUILT_ELEMENTS = new Set(['building', 'terrace', 'storefront', 'tower', 'tank',
  'transformerPad', 'container', 'silo', 'glassSpan', 'towerCore']);

/**
 * Recipes the map PLACES rather than grows: the portal facilities a raid enters
 * and leaves by, the reactor, the rest stop and its shoreline, and collapsed
 * ground. These are landmarks, drawn in full wherever they land. Checkpoints
 * are exempt too (see composeBody) for the same reason gateEmphasis dresses
 * them heavier — they are what the player navigates by.
 *
 * The roadway is exempt for the opposite reason: a trunk tile IS the frontage
 * this whole gradient is measured from, so it is never anything but full.
 */
const DENSITY_EXEMPT = new Set(['portal', 'reactor', 'water', 'campsite', 'blocked', 'road']);

/**
 * How much of a recipe's built stock survives, indexed by distance from the
 * nearest roadway. Index 0 is unused — a tile the road runs through is scored
 * by rank instead, since a trunk frontage is worth more than a back lane.
 * Coverage caps the distance at MAX_ROAD_DISTANCE (2), so the last entry is
 * the emptiest ground on the map.
 */
const DENSITY_BY_DIST = [1, 0.6, 0.3];
const MINOR_ROAD_DENSITY = 0.85;

/**
 * How built-up this part of the MAP is, on top of how near this tile is to a
 * road.
 *
 * Distance from the roadway is the only thing the gradient above knows, and
 * the network covers the grid to MAX_ROAD_DISTANCE (2) — so every tile on the
 * map lands in one of three states, interleaved evenly, and the map comes out
 * one settlement of uniform thickness with lanes through it. A town has a core
 * and it has outskirts, and which you are standing in has nothing to do with
 * the nearest road: there are busy back lots and there are empty frontages.
 *
 * The second gradient is the DISTRICT the cell is in — an authored figure on
 * the archetype, so a district's look and its name agree.
 *
 * This used to be a world-space noise field, and it was wrong in a way that
 * took a while to see: it sampled the tile's raw grid position with no per-map
 * term, and a map is at most 7x17 starting at (0,0). Every raid in the game
 * therefore drew from the same patch of lattice — dense in the north-west, a
 * hard sparse band right of centre — the same stencil every time. It broke up
 * density within a map, which was the point, but contributed nothing between
 * maps and imposed a fixed pattern in exchange.
 *
 * What the noise was genuinely good at is kept: it is continuous, so
 * neighbours agree, which makes it the right thing to vary a district
 * INTERNALLY with. It survives as grain of a few per cent about the district's
 * own figure, so a core does not read as flat.
 */
const SETTLEMENT_CELL = 5;               // tiles across one lobe of the field
const GRAIN = 0.08;                      // how far the grain moves a tile off its district

/**
 * What the average tile is built up to. Every district factor is centred on
 * this, so the mix redistributes stock rather than deleting it.
 */
const MEAN_BUILT = 0.85;

function settlement(col, row) {
  // Offset the sample so the lattice's own zero does not sit on the map origin,
  // where every region would open on the same value.
  const s = smoothNoise(col / SETTLEMENT_CELL + 11.3, row / SETTLEMENT_CELL + 7.7);
  // Value noise interpolates between lattice heights, so most of what comes out
  // of it is near the middle; the smoothstep pushes it toward its own ends.
  return s * s * (3 - 2 * s);
}

/**
 * How much of the sea's own tile is still standing. A shore tile is half under
 * water, and what the water took it took a long time ago — so the standing
 * stock there is thin and what is left of the rest is rubble.
 *
 * Expressed through `built` rather than as its own mechanism because `built`
 * already does exactly the right things with it: it shrinks footprints, drops
 * the LARGEST ones first (thinForDistance), lets growth back in, and wears the
 * tarmac. A separate "drown the buildings" pass would have had to reinvent all
 * four and would have disagreed with them at the margins.
 */
const SHORE_BUILT = 0.45;                // how much of a shore tile survives

/** How built up this tile is: its district, grained so the district is not flat. */
export function builtUp(zone, col, row, shore) {
  const base = ZONE_BUILT[zone] ?? MEAN_BUILT;   // unknown district, or a save from before them
  const drowned = shore === 0 ? SHORE_BUILT : shore === 1 ? (1 + SHORE_BUILT) / 2 : 1;
  return base * drowned * (1 - GRAIN + 2 * GRAIN * settlement(col, row));
}

const ZONE_BUILT = Object.fromEntries(
  Object.entries(zoneArchetypes).map(([zone, a]) => [zone, a.built ?? MEAN_BUILT]));

/**
 * Size moves less than count. Footprint drives the recipes' own
 * `when: buildingScale >= n` gates, and a district that swung it as hard as the
 * count would switch those on and off by neighborhood.
 */
const builtScale = b => 0.72 + 0.28 * (b / MEAN_BUILT);

/**
 * Growth fills what is not built, and tarmac nobody drives goes to seed. Both
 * run INVERSE to the district and are centred on the mean tile, so the map
 * gains no vegetation overall — it just stops spreading it evenly.
 */
const builtGrowth = b => 1 + (MEAN_BUILT - b);
const builtWear = (wear, b) => Math.min(1, Math.max(0, wear + (MEAN_BUILT - b) * 0.5));

/**
 * Which of a recipe's built elements to leave out, by tile instance index.
 *
 * The biggest footprints go first. Dropping the block and leaving the shed is
 * what the edge of a built-up area looks like; dropping at random just leaves a
 * thinner copy of the same town everywhere. One structure always survives —
 * every tile is a room the player walks into, and a bare one reads as a
 * rendering fault rather than as open ground.
 *
 * @param {Array}  boxes measured element footprints, as runRecipe records them
 * @param {number} dist  steps to the nearest roadway; 0 means one runs through
 * @param {string} rank  that road's rank, when dist is 0
 * @param {number} [built] how built up this tile's district is, 0..1. Absent
 *   means "no district", which multiplies by one and leaves the road-distance
 *   gradient to it alone.
 */
export function thinForDistance(boxes, dist, rank, built) {
  const stock = boxes.filter(b => BUILT_ELEMENTS.has(b.el));
  if (stock.length < 2) return new Set();
  const density = (dist > 0
    ? DENSITY_BY_DIST[Math.min(dist, DENSITY_BY_DIST.length - 1)]
    : (rank === MAJOR_RANK ? 1 : MINOR_ROAD_DENSITY))
    * (built != null ? built : 1);
  const drop = stock.length - Math.max(1, Math.round(stock.length * density));
  if (drop <= 0) return new Set();
  const area = b => (b.x1 - b.x0) * (b.y1 - b.y0);
  return new Set([...stock].sort((a, b) => area(b) - area(a)).slice(0, drop).map(b => b.n));
}

/**
 * Buildings do not only get fewer with distance from a road, they get smaller.
 *
 * Count alone barely moves half the map: most recipes carry one or two
 * structures, and the last one always stands. Scaling the footprint is what
 * actually opens the ground up — and it drives the recipes' own
 * `when: buildingScale >= n` gates, which is exactly where the extra hall and
 * the fourth block were already authored to appear only at a bigger scale. So
 * one knob thins by size and by count together, in the terms the art was
 * written in.
 */
const BUILDING_SCALE_BY_DIST = [1, 0.88, 0.78];
const MINOR_ROAD_SCALE = 0.95;

/**
 * The region's tune adjusted for one tile: its structures scaled to the
 * distance from a road and to how built up its district is, its growth and its
 * road wear set inverse to the same district figure.
 *
 * Scale follows `thinnable`, exactly as the count thinning does — a portal
 * facility or a rest stop is drawn in full wherever it lands. Growth and wear
 * do NOT: vegetation reclaiming the outskirts and tarmac going to seed have no
 * business stopping at a road tile's border, and a gate on the edge of town
 * should look like it is on the edge of town.
 *
 * Cached per (tune, factors): a repaint runs this for every tile on the grid,
 * and the tune is read on every element a recipe draws. The key quantises, so
 * a region holds a couple of dozen entries rather than one per tile.
 */
const scaledTunes = new WeakMap();
function tuneForTile(tune, dist, rank, built, thinnable) {
  const q = n => Math.round(n * 40) / 40;
  const scale = q(!thinnable ? 1 : (dist > 0
    ? BUILDING_SCALE_BY_DIST[Math.min(dist, BUILDING_SCALE_BY_DIST.length - 1)]
    : (rank === MAJOR_RANK ? 1 : MINOR_ROAD_SCALE))
    * (built != null ? builtScale(built) : 1));
  const growth = q(built != null ? builtGrowth(built) : 1);
  const wear = q(built != null ? builtWear(tune.roadWear, built) : tune.roadWear);
  if (scale === 1 && growth === 1 && wear === q(tune.roadWear)) return tune;
  const key = `${scale}|${growth}|${wear}`;
  let byKey = scaledTunes.get(tune);
  if (!byKey) scaledTunes.set(tune, byKey = new Map());
  let out = byKey.get(key);
  if (!out) {
    byKey.set(key, out = { ...tune,
      buildingScale: tune.buildingScale * scale,
      treeDensity: tune.treeDensity * growth,
      roadWear: wear });
  }
  return out;
}

/**
 * @param {object} [opts.record] array to collect solid element footprints into;
 *   when present NOTHING is drawn — this is the measuring pass
 * @param {Set} [opts.skip] element instance indices to run but not draw
 */
/**
 * @param {object} [opts.record] array to collect element footprints into; when
 *   present NOTHING is drawn — this is the measuring pass
 * @param {Set}    [opts.skip]   element instance indices to run but not draw
 * @param {number} [opts.variant] force a variant instead of rolling one
 * @param {Array}  [opts.shift]  per-element [dx, dy], indexed as `record` was
 */
function runRecipe(def, g, rng, T, tune, conn, ox = 0, oy = 0, opts = {}) {
  let elements = def.elements;
  if (def.variants) {
    // The roll happens either way, even when the variant is forced: element
    // functions draw from this stream, so skipping a draw from it would shift
    // everything after and the measuring pass would stop matching the real one.
    const rolled = def.variantKey != null
      ? (rng() < (tune[def.variantKey] ?? 0.5) ? 1 : 0)      // tune-gated pair
      : ri(rng, 0, def.variants.length - 1);
    elements = def.variants[opts.variant ?? rolled].elements;
  }
  // A measuring pass must not leave anything behind. Elements that animate hand
  // a closure to dynamic(), which either records it for the caller or runs it
  // against the live canvas — neither is wanted here, so it goes to a bin that
  // is thrown away. Guarding this at the call site instead left it to every
  // caller to remember.
  const wasDyn = DYN;
  if (opts.record) DYN = [];
  let index = 0;
  for (const spec of (elements || [])) {
    const fn = ELEMENTS[spec.el];
    if (!fn) continue;
    if (spec.when && (tune[spec.when.tune] ?? 0) < spec.when.gte) continue;
    if (spec.chance != null && rng() >= spec.chance) continue;
    const count = spec.repeat != null ? resolveVal(spec.repeat, rng) : 1;
    for (let i = 0; i < count; i++) {
      const n = index++;
      const [sx, sy] = opts.shift?.[n] || [0, 0];
      const p = {};
      for (const key of Object.keys(spec)) {
        if (SPEC_KEYS.has(key)) continue;
        const v = resolveVal(spec[key], rng);
        p[key] = key === 'x' ? v + ox + sx : key === 'y' ? v + oy + sy : v;
      }
      const probe = (opts.record || opts.skip?.has(n)) ? makeProbeG() : null;
      fn(probe || g, rng, T, tune, conn, p);
      if (opts.record && probe.box.x1 > probe.box.x0) {
        opts.record.push({ n, el: spec.el, solid: SOLID_ELEMENTS.has(spec.el), ...probe.box });
      }
    }
  }
  DYN = wasDyn;
}

/**
 * Choose which of a recipe's layouts to draw, given where the road already is.
 *
 * The road's position is settled by the tile's edges before any of this runs,
 * so the layout is what adapts. Each variant is measured once, then mirrored
 * about the tile's centre line — mirroring the PLACEMENT rather than the
 * drawing, so every roof keeps its lit north-west edge and every shadow still
 * falls the way the sun says. That turns one authored layout into four, with no
 * second copy of the art's coordinates to drift out of step with it.
 *
 * Whatever the winning layout still leaves under the road is suppressed: better
 * a missing outbuilding than a road driven through one.
 */
const MIRRORS = [[false, false], [true, false], [false, true], [true, true]];

/**
 * Every element's drawn footprint SIZE under a given shift, by instance index.
 * A probe pass, so nothing is drawn and nothing else is disturbed.
 */
function measureSizes(def, rng, T, tune, conn, variant, shift) {
  const boxes = [];
  runRecipe(def, null, rng, T, tune, conn, 0, 0, { record: boxes, variant, shift });
  return new Map(boxes.map(b => [b.n, `${b.x1 - b.x0}x${b.y1 - b.y0}`]));
}

/**
 * The per-element shift that reflects a measured layout about the tile's
 * centre line. Mirroring never changes a footprint's size or its index, so
 * this is a pure translation per element and the boxes stay comparable.
 */
function mirrorShift(boxes, flipX, flipY) {
  const shift = [];
  for (const b of boxes) {
    shift[b.n] = [flipX ? BASE - b.x1 - b.x0 : 0, flipY ? BASE - b.y1 - b.y0 : 0];
  }
  return shift;
}

/**
 * Every tile is entitled to a mirror; only a tile with a road through it has a
 * reason to prefer one. Off the network there is nothing to dodge, so the seed
 * picks — without which the whole map draws every recipe the same way round,
 * and a majority of tiles are off the network.
 */
function seededMirror(boxes, rng) {
  return mirrorShift(boxes, ...MIRRORS[Math.floor(rng() * MIRRORS.length)]);
}

export function chooseLayout(def, freshRng, T, tune, conn, samples, rng) {
  const count = def.variants ? def.variants.length : 1;
  const layouts = [];
  for (let v = 0; v < count; v++) {
    const boxes = [];
    runRecipe(def, null, freshRng(), T, tune, conn, 0, 0, { record: boxes, variant: v });
    for (const [flipX, flipY] of MIRRORS) {
      const shift = mirrorShift(boxes, flipX, flipY);
      const solids = [];
      for (const b of boxes) {
        if (!b.solid) continue;
        const [dx, dy] = shift[b.n];
        solids.push({ ...b, x0: b.x0 + dx, x1: b.x1 + dx, y0: b.y0 + dy, y1: b.y1 + dy });
      }
      layouts.push({ variant: v, shift, solids, boxes });
    }
  }
  const scored = shuffled(layouts, rng)
    .map(l => ({ ...l, hit: l.solids.filter(b => runsThrough(samples, b)) }));
  // What a layout costs, in the order the costs matter.
  //
  // Losing every structure is not one more of the same loss, it is a different
  // outcome: a market with no shop on it, a housing block with no housing, a
  // tile the player walks into and finds bare ground. Scoring on the count
  // alone could not see that — a variant carrying ONE structure that the road
  // runs through ties with a variant carrying four that loses one, and the
  // seeded shuffle then picked between them at random. Measured across every
  // road position on the tile, that emptied a habitation tile eighteen times
  // out of eighteen where a layout that kept a building was available.
  //
  // Nothing else changes: every layout still suppresses whatever it leaves
  // under the roadway, so this decides WHICH layout is drawn and never puts a
  // roof on the tarmac.
  const cost = l => [l.solids.length && l.hit.length === l.solids.length ? 1 : 0, l.hit.length];
  const best = scored.reduce((a, b) => {
    const [aBare, aHit] = cost(a), [bBare, bHit] = cost(b);
    return bBare < aBare || (bBare === aBare && bHit < aHit) ? b : a;
  });
  // boxes comes back so the caller can thin the winner by road distance: a
  // variant's element indices only mean anything within that variant, so the
  // thinning has to score the layout that actually won, not the one the first
  // measuring pass happened to roll. Mirroring never changes a footprint's
  // size or its index, so the unshifted boxes are the right thing to score.
  return { variant: best.variant, shift: best.shift, boxes: best.boxes,
    skip: new Set(best.hit.map(b => b.n)) };
}

// ---- where the tile sets its structures down ----
// Recipes anchor their structures a fixed distance in from the tile edge — 9
// to 18 units of a 120-unit tile — and chooseLayout mirrors corner to corner,
// which maps a margin to the margin on the opposite side and so preserves the
// set of them. Across a grid that repeats on a 120-unit rhythm and leaves an
// open lane at every tile join, and the eye reads the lattice long before it
// reads the town.
//
// Carrying the whole composition sideways does not fix it: a recipe puts a
// block in one corner and another in the opposite one, so its bounding box is
// most of the tile and there is nowhere rigid to carry it TO. Structures have
// to move against each other, which means the thing that was holding them
// apart — an author's coordinates — has to be replaced by something. That
// something is the measurement: what a footprint SITS ON may not change.
//
// One rule covers every case a recipe author was relying on:
//   - a block standing clear of everything has to stay clear of everything,
//     of the roadway included, so nothing lands on anything;
//   - a guard post inside its compound fence stays inside it, because leaving
//     would drop its overlap with the fence;
//   - a shed on a field of rubble is already covered by it and stays covered
//     wherever it goes, so ground litter never pins anything down;
//   - a storefront authored clear of its market keeps clear of it.
// No element list, no special cases, and a recipe can be rewritten without
// this having to be told about it.

/**
 * How far a structure may walk from where it was authored.
 *
 * A structure cannot leave the tile, so a wander is not symmetric: there is
 * always more room toward the middle than toward the join, and every unit of
 * reach therefore also widens the open lane between tiles a little. Measured
 * across a grid, reach buys a flatter tile interior and costs frontage, and
 * the two cross here — past this the hole in the middle of every tile is
 * already gone and the lane is all that is still growing.
 */
const PLACE_JITTER = 16;

/**
 * How many placements to try before leaving a structure where it was. Backs
 * off toward the authored position as it goes, so a tight tile ends up nudged
 * rather than unmoved.
 */
const PLACE_TRIES = 6;

/**
 * A footprint this long on an axis is the TILE, not something standing on it:
 * a pipe run, a compound fence, a field of scatter. Those never move — a
 * pipeline still has to meet its neighbour's at the join — but they are very
 * much still something to sit on, so they stay in the overlap test.
 */
const SPANNING = 96;
const tileWide = b => b.x1 - b.x0 >= SPANNING || b.y1 - b.y0 >= SPANNING;

const overlap = (a, b) =>
  Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) *
  Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));

/**
 * Walk each structure off its authored spot, as far as the tile allows.
 *
 * Works on footprints as they will be DRAWN — mirrored by the layout pass,
 * thinned by distance, scaled by the region — for the same reason everything
 * else in this file does: the authored coordinates are not where anything
 * ends up. Mutates the boxes it is given as it goes, so each structure is
 * placed against where the previous ones actually landed.
 *
 * @param {Array} placed  footprints in draw order; MUTATED to their new spots
 * @param {Array} samples the roadway's bound, when the network runs through
 * @returns {Map} element instance index -> [dx, dy]
 */
function placeStructures(placed, rng, samples) {
  const moved = new Map();
  const sits = (b, box) => {
    let before = 0, after = 0;
    for (const o of placed) {
      if (o === b) continue;
      before += overlap(o, b);
      after += overlap(o, box);
    }
    return before === after;
  };
  for (const b of placed) {
    if (tileWide(b)) continue;
    for (let t = 0; t < PLACE_TRIES; t++) {
      const reach = PLACE_JITTER * (1 - t / PLACE_TRIES);
      // Drawn from what the tile edge actually allows, not drawn and then
      // tested against it. Rejecting an out-of-bounds offset and rolling again
      // is a bias toward the middle of the tile — the closer a structure
      // stands to an edge, the more of its offsets die on that side — which
      // trades one uniformity for another, and this whole pass exists to stop
      // structures landing a predictable distance from the join.
      const span = (lo, hi) => {
        const a = Math.max(-reach, lo), z = Math.min(reach, hi);
        return z > a ? Math.round(a + rng() * (z - a)) : 0;
      };
      // A footprint that reaches the far clamp may have been TRUNCATED by it
      // — buildingTop and storefront both size themselves against the tile
      // edge — and what was measured is the truncated version. Move it back
      // toward the origin and the clamp stops biting: it draws wider than the
      // box this pass cleared, and lands on whatever that box was clearing it
      // of. So a structure against the clamp does not move on that axis; only
      // one with room to spare on both sides has a measurement to trust.
      const dx = span(b.x1 >= BASE - TILE_CLAMP ? 0 : TILE_CLAMP - b.x0, BASE - TILE_CLAMP - b.x1);
      const dy = span(b.y1 >= BASE - TILE_CLAMP ? 0 : TILE_CLAMP - b.y0, BASE - TILE_CLAMP - b.y1);
      if (!dx && !dy) break;
      const box = { x0: b.x0 + dx, x1: b.x1 + dx, y0: b.y0 + dy, y1: b.y1 + dy };
      // The layout that won did so by standing clear of the carriageway, and
      // an offset that puts a wall back on it costs more than the uniformity
      // it was buying.
      if (b.solid && samples && runsThrough(samples, box)) continue;
      if (!sits(b, box)) continue;
      Object.assign(b, box);
      moved.set(b.n, [dx, dy]);
      break;
    }
  }
  return moved;
}

// ---- daylight wells (underground regions) ----
// A few large cave openings light whole AREAS of the map, like sun pouring
// into a cavern. Wells live on a fixed WORLD grid (same trick as the cloud
// field): every tile derives the identical well set from hashed super-cell
// coordinates, then shades its own pixels by distance to the nearest well —
// so one opening illuminates a neighborhood of tiles with a wide, seamless
// falloff and no cross-tile coordination. tune.lightRays is well density.
const WELL_CELL = 120;                 // world units between candidate openings (3 tiles)

function lightWellsNear(col, row, density) {
  const wells = [];
  const cx = Math.floor((col * WORLD + WORLD / 2) / WELL_CELL);
  const cy = Math.floor((row * WORLD + WORLD / 2) / WELL_CELL);
  for (let gy = cy - 1; gy <= cy + 1; gy++) {
    for (let gx = cx - 1; gx <= cx + 1; gx++) {
      if (noiseHash(gx * 31 + 7, gy * 17 + 3) > density * 0.5) continue;
      wells.push({
        x: (gx + 0.15 + 0.7 * noiseHash(gx, gy)) * WELL_CELL,
        y: (gy + 0.15 + 0.7 * noiseHash(gx + 99, gy + 41)) * WELL_CELL,
        r: 55 + 40 * noiseHash(gx + 7, gy + 77),
      });
    }
  }
  return wells;
}

// The landing pool tracks the sun through the day (west of the opening at
// sunrise, plumb beneath it at noon, east at dusk) and its intensity
// follows the SURFACE day cycle — gone at night.
// Each well also casts visible god rays: thin streaks that hang from the
// STATIC opening and slant down to wherever the pool currently lands. The
// beam spreads cone-like from opening to landing, is longest when the sun
// is low, and fades out near noon when the vertical shaft is seen end-on.
// Lanes come from world-space noise on the perpendicular axis — seamless
// across tiles — and drift slowly with time like dust in the shaft.
// The rays' visible ORIGIN is fixed up-screen of the well — the ceiling
// opening projected ~one tile north — so beams always slant DOWN into the
// pool instead of lying flat across it; at noon they pour straight down-screen.
const RAY_RISE = 36;

/**
 * Where the sun is putting a shaft of daylight, this frame.
 *
 * Shared by the shafts themselves and by the shadows the rock throws in them,
 * because those two have to agree about where the light is coming from or the
 * stone will shade the wrong way.
 *
 * The landing point tracks the sun: west of the opening at sunrise, sweeping
 * left to right through the day, directly beneath it at noon. Vertically it
 * keeps a gentle southward drift that peaks mid-morning and mid-afternoon and
 * vanishes at dawn, noon and dusk.
 */
function sunShaft(anim) {
  let elev = 0.7, dayFrac = 0.35;                     // static renders: late-morning sun
  if (anim && anim.dayPhase != null) {
    const f = (anim.dayPhase - 0.25) / 0.5;           // 06:00 -> 18:00
    dayFrac = Math.min(1, Math.max(0, f));
    elev = f >= 0 && f <= 1 ? Math.sin(f * Math.PI) : 0;
  }
  const bx = (dayFrac - 0.5) * 160;
  const by = elev * (1 - elev) * 18;
  const vx = bx, vy = by + RAY_RISE;                  // beam vector: origin -> pool
  const bl = Math.hypot(vx, vy);
  return { elev, bx, by, bl, ux: vx / bl, uy: vy / bl };
}

function drawLightWells(g, T, tune, anim, col, row) {
  const { elev, bx, by, bl, ux, uy } = sunShaft(anim);
  if (elev <= 0.03) return;                           // night
  const wells = lightWellsNear(col, row, tune.lightRays);
  if (!wells.length) return;
  // world units, NOT the authoring grid — see WORLD/FX_STEP. Every constant
  // in this function is tuned in these units, and the loop below is the
  // per-tile cost of the whole ambient layer.
  const ox = col * WORLD, oy = row * WORLD;
  const t = anim?.t || 0;
  for (let y = 0; y < WORLD; y += FX_STEP) {
    for (let x = 0; x < WORLD; x += FX_STEP) {
      let I = 0, ray = 0, wave = 0.5;
      for (const w of wells) {
        const rx = ox + x - w.x - bx, ry = oy + y - w.y - by;
        const f = 1 - Math.hypot(rx, ry) / w.r;
        if (f > I) I = f;
        const orx = ox + x - w.x, ory = oy + y - (w.y - RAY_RISE); // relative to the raised origin
        const along = orx * ux + ory * uy;
        if (along < -2) continue;                      // nothing behind the origin
        const perp = -orx * uy + ory * ux;
        // the beam spreads cone-like from the origin toward the landing
        const width = w.r * (0.35 + 0.55 * Math.min(1, along / bl));
        const envP = 1 - Math.abs(perp) / width;
        if (envP <= 0) continue;
        const seed = w.x * 0.37 + w.y * 0.61;
        const lane = smoothNoise(perp / 7 + t * 0.1, seed);
        if (lane <= 0.6) continue;
        // each ray terminates at its own point INSIDE the landing pool: a
        // per-lane endpoint on this lane's chord, so the beam lands with a
        // ragged natural edge and never overshoots the pool
        const chord = Math.sqrt(Math.max(0, w.r * w.r - perp * perp));
        const end = bl + chord * (-0.5 + 1.2 * smoothNoise(perp / 7, seed + 57));
        if (along > end) continue;
        const stop = Math.min(1, (end - along) / 6);   // soft landing at the endpoint
        const start = Math.min(1, (along + 2) / 6);    // soft emergence at the origin
        // shimmer: a slow brightness wave travels down the beam toward the
        // pool, phased per lane so the streaks pulse independently; static
        // renders sit at the neutral midpoint (factor 1.0)
        const wv = t ? 0.5 + 0.5 * Math.sin(t * 1.6 - along * 0.12 + perp * 0.35) : 0.5;
        const s2 = envP * ((lane - 0.6) / 0.4) * stop * start * (0.78 + 0.44 * wv);
        if (s2 > ray) { ray = s2; wave = wv; }
      }
      const px = x * FX_PX, py = y * FX_PX, ps = FX_STEP * FX_PX;
      if (I > 0.03) {
        const s = I * I * (3 - 2 * I) * elev * 0.4;         // smooth wide falloff
        g.a(Math.min(0.62, s * 0.8), () => g.p(px, py, ps, ps, '#f6e9c2'));        // daylight
        if (s > 0.55) g.a((s - 0.55) * 0.8, () => g.p(px, py, ps, ps, '#fff8e2')); // hot core
      }
      if (ray > 0.02) {                               // subtle beam streaks
        // the shimmer wave also drifts color temperature: warmer in the
        // troughs, whiter at the crests (midpoint = the tuned #fbf3d9)
        const rr = Math.min(255, Math.round(243 + 16 * wave));
        const gg = Math.min(255, Math.round(233 + 20 * wave));
        const bb = Math.min(255, Math.round(197 + 40 * wave));
        g.a(Math.min(0.16, ray * elev * 0.1), () => g.p(px, py, ps, ps, `rgb(${rr},${gg},${bb})`));
      }
    }
  }
}

// The portal's energy glow, drawn as the final pass of a portal tile so it
// renders above flood water, vegetation, weathering, and even cloud cover.
function portalGlow(g, T) {
  const C = BASE >> 1;
  const f = flicker(C, C, 4);                         // slow energy shimmer
  g.a(0.15 + 0.18 * f, () => disc(g, C, C, 27, T.glow));
  g.a(0.65 + 0.3 * f, () => disc(g, C, C, 9, T.glow));
  g.a(0.5 + 0.4 * f, () => ringO(g, C, C, 13 + Math.round(f * 3), T.glow)); // pulsing containment ring
  g.r(C - 1, C - 1, 3, 3, T.structLight);
}

// Gates take twice as long to traverse as hazards, so dress them as major
// areas of interest: an extra structure in a free corner and a perimeter
// wall around the compound (skipped when the recipe already fenced the
// yard). Gate vegetation is also boosted in the renderer.
function gateEmphasis(g, rng, T, tune, conn, recipe, samples) {
  // Every roll happens up front, whichever corner wins — this draws on the same
  // stream the recipe used, so a choice that spent a different number of rolls
  // would move everything after it (see buildingTop).
  const pick = ri(rng, 0, 3);
  const jx = ri(rng, 0, 6), jy = ri(rng, 0, 6);
  const bw = ri(rng, 21, 27), bh = ri(rng, 18, 24);
  // This block is drawn OUTSIDE the recipe, so the layout pass never measures
  // it and cannot suppress it — on a gate the road runs through, it simply
  // landed on the roadway. Score the corners here on the same rule instead,
  // starting from the seeded pick so a tile with no road looks as it did; and
  // where a road reaches every corner, drop the block rather than pave over it.
  const corners = [[12, 12], [78, 12], [12, 78], [78, 78]];
  const s = tune.buildingScale;
  const boxAt = ([cx, cy]) => ({ x0: cx + jx, y0: cy + jy,
    x1: cx + jx + Math.round(bw * s), y1: cy + jy + Math.round(bh * s) });
  const order = corners.map((_, i) => corners[(pick + i) % 4]);
  const clear = samples?.length ? order.find(c => !runsThrough(samples, boxAt(c))) : order[0];
  const [bx, by] = clear || order[0];
  buildingTop(clear ? g : makeProbeG(), rng, T, tune, bx + jx, by + jy, bw, bh);
  if (recipe !== 'security') fenceRing(g, rng, T, 6, 6, 108, 108, conn);
  // Hand the footprint back. Drawn outside the recipe, this block is in no
  // measurement anyone downstream has — which is how the vegetation pass came
  // to plant on it, and every tree on a roof anywhere on the map was this one.
  return clear ? boxAt(clear) : null;
}

// ---- skill anchors ----
// A tile says what KIND of place it is (its recipe) but not what the team
// will be doing there. The anchor is a small deployed installation marking
// the skill the room's encounter tests, so the map can be read for that:
// a mast for signal-relay, a vault hatch for lock-breach, a grow bed for
// growth-control. Data lives in RENDER_CONFIG.skillAnchors and is drawn by
// the ordinary recipe interpreter, plate-relative.
export const ANCHOR_MAX = 24;   // largest footprint, in units (~4.8 m of ground)
export const CUTAWAY_PAD = 4;   // wall ring around an indoor anchor

// Connector bands run centre-to-edge and occupy x,y in [45,75] at their
// widest, so anchors take a seeded corner quadrant. The 9px outer margin also
// clears the perimeter fence gateEmphasis puts on gate tiles.
function anchorSpot(rng, w, h) {
  const c = ri(rng, 0, 3);
  return [
    c & 1 ? ri(rng, 75, 111 - w) : ri(rng, 9, 45 - w),
    c & 2 ? ri(rng, 75, 111 - h) : ri(rng, 9, 45 - h),
  ];
}

// Roof cutaway for an encounter that happens INDOORS. The map looks down at
// roofs, but most event prose is written inside halls and bays — rather than
// move those outdoors or pretend the tile is an interior, the one structure
// the encounter happens in gets its roof taken off, and the anchor sits on the
// floor of the open bay. A doorway faces the tile centre so the bay reads as
// enterable from the path rather than sealed.
function drawCutaway(g, T, x, y, w, h) {
  dropShadow(g, T, x, y, w, h);
  g.r(x, y, w, h, T.structDark);                          // wall mass
  g.r(x, y, w, 1, T.structLight);                         // lit wall cap
  g.r(x, y, 1, h, T.structLight);
  g.r(x, y + h - 1, w, 1, T.far);
  g.r(x + w - 1, y, 1, h, T.far);
  const p = CUTAWAY_PAD;
  g.r(x + p - 1, y + p - 1, w - 2 * p + 2, h - 2 * p + 2, T.ground2);  // interior floor
  g.a(0.35, () => g.r(x + p - 1, y + p - 1, w - 2 * p + 2, h - 2 * p + 2, T.far)); // roofed shade
  g.a(0.5, () => {                                        // inner wall face, catching light
    g.r(x + p - 1, y + p - 1, w - 2 * p + 2, 1, T.structDark);
    g.r(x + p - 1, y + p - 1, 1, h - 2 * p + 2, T.structDark);
  });
  const dw = 8;                                           // doorway, on the wall nearest the hub
  const cx = x + (w >> 1), cy = y + (h >> 1), mid = BASE >> 1;
  if (Math.abs(cx - mid) > Math.abs(cy - mid)) {
    g.r(cx < mid ? x + w - p : x, y + (h >> 1) - (dw >> 1), p, dw, T.ground2);
  } else {
    g.r(x + (w >> 1) - (dw >> 1), cy < mid ? y + h - p : y, dw, p, T.ground2);
  }
}

// Returns without touching the rng when there is no anchor to draw — roads,
// water, campsites, entry/exit and blocked cells all have no primary skill.
// Because the anchor owns its own rng phase, that early return leaves those
// tiles pixel-identical to what they rendered before anchors existed.
function runAnchor(skillId, g, rng, T, tune, conn, indoor) {
  const def = skillId && SKILL_ANCHORS[skillId];
  if (!def || !Array.isArray(def.elements)) return;
  const w = def.w ?? ANCHOR_MAX, h = def.h ?? ANCHOR_MAX;
  const pad = indoor ? CUTAWAY_PAD : 0;
  const [ox, oy] = anchorSpot(rng, w + pad * 2, h + pad * 2);
  if (indoor) drawCutaway(g, T, ox, oy, w + pad * 2, h + pad * 2);
  runRecipe(def, g, rng, T, tune, conn, ox + pad, oy + pad);
}

// ---- ruins pass ----
// The city was denser than what is still standing in it. A ruin is a
// building-sized footprint of what came down: the floor slab, the wall stubs
// that outlast a roof, and the roof itself piled on the floor. Drawn from the
// same vocabulary as an intact building so the two read as one architecture in
// two states, and drawn as a PASS rather than as a recipe element so every
// room gets them without thirteen recipes each having to say so.

/**
 * Rubble is DARKER than the building it came from. A heap of broken material
 * is mostly the shadow between its pieces, and the bright faces an intact roof
 * shows are surfaces still pointing at the sky — a pile has few of those. Some
 * of it burned, and what burned goes further: down and toward grey, because
 * char has no colour of its own left.
 *
 * The whole ruin is drawn from ONE derived palette rather than from T, so the
 * shared rubble helper stays exactly as the roads and the collapse tiles use
 * it, and the two states of a ruin differ in one place.
 */
const RUBBLE_SHADE = 0.72;
const CHARRED_CHANCE = 0.22;

function ruinPalette(T, charred) {
  const k = charred ? 0.4 : RUBBLE_SHADE;
  const grey = charred ? 0.85 : 0;
  return {
    ...T,
    struct: shade(T.struct, k, grey),
    structLight: shade(T.structLight, charred ? 0.34 : 0.62, grey),
    structDark: shade(T.structDark, charred ? 0.7 : 1, grey),
    metal: shade(T.metal, charred ? 0.4 : 0.72, grey),
    stub: shade(T.struct, charred ? 0.5 : 0.85, grey),
  };
}

function ruinedBuilding(g, rng, T, x, y, w, h, charred) {
  const P = ruinPalette(T, charred);
  dropShadow(g, T, x + 2, y + 2, w - 4, h - 4);
  // The heap, in the same masonry the buildings are made of and NOT in the
  // dark of a hole: this is material piled up, not ground fallen away, and the
  // collapse tiles already own the second reading. What separates it from a
  // roof at a glance is its edge — a rectangle that has lost its corners,
  // ragged a few units in and out on every row.
  for (let ry = 0; ry < h; ry++) {
    const l = ri(rng, 0, 4), r = ri(rng, 0, 4);
    g.r(x + l, y + ry, Math.max(1, w - l - r), 1, P.struct);
  }
  // broken up: light where a face has sheared and caught the sun, dark in the
  // gaps between slabs
  for (let i = 0, n = Math.round(w * h * 0.09); i < n; i++) {
    g.r(x + ri(rng, 2, Math.max(2, w - 4)), y + ri(rng, 1, Math.max(1, h - 2)),
      ri(rng, 2, 4), 1, rng() < 0.55 ? P.structDark : P.structLight);
  }
  // Wall stubs. A broken RUN on each side, never the whole side: four
  // continuous walls would read as a building with its roof off, and the point
  // of these is that they are past saving.
  const stub = (sx, sy, sw, sh) => {
    dropShadow(g, T, sx, sy, sw, sh);
    g.r(sx, sy, sw, sh, P.stub);                                // a standing face, so a shade up
    g.r(sx, sy, sw, 1, P.structLight);                          // lit top of the stub
    g.r(sx, sy + sh - 1, sw, 1, P.structDark);
  };
  const run = span => {
    const len = ri(rng, Math.max(5, span >> 2), Math.max(6, span - 5));
    return [ri(rng, 0, Math.max(0, span - len)), len];
  };
  const [ax, aw] = run(w); stub(x + ax, y, aw, 3);
  const [bx, bw] = run(w); stub(x + bx, y + h - 3, bw, 3);
  const [ay, ah] = run(h); stub(x, y + ay, 3, ah);
  const [by, bh] = run(h); stub(x + w - 3, y + by, 3, bh);
  // and what spilled off the edges when it came down, in the ruin's own tones
  rubble(g, rng, P, x - 4, x + w, y - 4, y + h, Math.max(5, Math.round((w * h) / 130)));
}

/** Rooms the map places whole, and which a fresh ruin would only clutter. */
const RUIN_EXEMPT = new Set(['water', 'blocked', 'reactor', 'portal', 'campsite']);

/**
 * @param {Array} keepOut structures already standing; a ruin never lands on one
 * @param {Array} samples the roadway, for the same reason
 */
function drawRuins(g, rng, T, tune, keepOut, samples, shore) {
  const taken = [...keepOut];
  const clash = b => taken.some(k => b.x0 <= k.x1 && b.x1 >= k.x0 && b.y0 <= k.y1 && b.y1 >= k.y0)
    || (!!samples?.length && runsThrough(samples, b));
  // On the shore row, the seaward half of the tile is mostly wrecks: more of
  // them, and pushed into the band the water reaches. Paired with the thinned
  // standing stock in builtUp, that half comes out as ruins in the shallows and
  // the landward half stays a street.
  const drowned = shore?.at === 0;
  const band = drowned
    ? (shore.side === 'n' ? [0, WATERLINE] : [BASE - WATERLINE, BASE])
    : null;
  for (let i = 0, n = ri(rng, 1, drowned ? 4 : 2); i < n; i++) {
    const w = Math.round(ri(rng, 24, 39) * tune.buildingScale);
    const h = Math.round(ri(rng, 18, 30) * tune.buildingScale);
    let box = null;
    // Same retry-and-give-up as the vegetation: on a tile with no room left,
    // the ruin that was never there is the right answer. Safe to vary the roll
    // count here — the ruins have their own seeded stream and both drawing
    // passes replay it against the same obstacles.
    for (let t = 0; t < 10 && !box; t++) {
      const x = ri(rng, 6, Math.max(6, BASE - 6 - w));
      const y = band
        ? ri(rng, band[0], Math.max(band[0], band[1] - h))
        : ri(rng, 6, Math.max(6, BASE - 6 - h));
      const b = { x0: x, y0: y, x1: x + w, y1: y + h };
      if (!clash(b)) box = b;
    }
    if (!box) continue;
    ruinedBuilding(g, rng, T, box.x0, box.y0, w, h, rng() < CHARRED_CHANCE);
    taken.push(box);       // ruins do not pile on each other either
  }
  // What this pass actually put down, so the signature layer can keep off it.
  // A gantry lying across a fresh ruin is the one clash the recipe's own
  // keep-out list cannot see, because a ruin is not in the recipe.
  return taken.slice(keepOut.length);
}

// ---- parking ----
/**
 * A parking lot is part of the road structure. It is the same tarmac, and it
 * only exists because something could drive onto it — so it is laid AGAINST the
 * carriageway off the road's own samples, the same way the traffic is, rather
 * than authored into a recipe's layout and then fought over.
 *
 * That is what fixes the overlap. A lot placed into a layout has to be either
 * mirrored clear of the road or dropped, and a lot is wide enough that most of
 * them ended up dropped; a lot placed off the roadway cannot be crossed by it,
 * and reads as what a lot actually is — an apron off the street.
 *
 * A tile the network misses gets no lot at all. There is nowhere to put one
 * that is not detached: an apron with no way onto it is not a car park, it is a
 * rectangle of tarmac in the middle of a block.
 */
const PARKING_RECIPES = new Set(['records', 'habitation', 'commerce']);

/**
 * Depth is fixed because it is a real dimension: 24 units is 4.8 m, one row of
 * bays parked nose-in, and a shallower lot would not be a lot. Length is
 * whatever fits — the longest row the spot allows, down to a floor of two. A
 * single bay is a driveway, not a car park, so a spot that only takes one is
 * treated as no spot at all.
 */
const MIN_BAYS = 2, MAX_BAYS = 5;

function drawParking(g, rng, T, samples, keepOut) {
  if (!samples?.length) return null;
  const clash = b => keepOut.some(k => b.x0 <= k.x1 && b.x1 >= k.x0 && b.y0 <= k.y1 && b.y1 >= k.y0);
  // Being clear of the sample it was hung off is not enough: a road that bends
  // can come back around and pass under the far end of the lot. Test the whole
  // footprint against the tarmac — measured a unit inside the sample bounds, or
  // the lot would reject the very band it is meant to be flush against.
  const crossed = b => samples.some(s => {
    const hx = s.hx - CLEARANCE - 1, hy = s.hy - CLEARANCE - 1;
    return s.x + hx > b.x0 && s.x - hx < b.x1 && s.y + hy > b.y0 && s.y - hy < b.y1;
  });
  // A lot is a big rectangle that has to be flush to the tarmac, clear of every
  // building, and clear of the rest of the road's run. On a busy tile that is a
  // narrow target, so it gets plenty of tries before giving up.
  for (let t = 0; t < 30; t++) {
    const s = samples[ri(rng, 0, samples.length - 1)];
    const vert = s.hx > s.hy;                   // the lot runs alongside, so it takes the road's axis
    const far = rng() < 0.5;
    // Flush to the DRAWN road, not to the sample bound — the samples run a
    // clearance wider than the tarmac they describe, and a lot set against that
    // would stand off the kerb by a pixel of bare ground.
    const ox = s.hx - CLEARANCE, oy = s.hy - CLEARANCE;
    // Longest row this spot will take. Depth never moves, so the only thing
    // being traded is how many bays fit alongside the road.
    for (let bays = MAX_BAYS; bays >= MIN_BAYS; bays--) {
      const long = lotLength(bays);
      const w = vert ? BAY_DEPTH : long, h = vert ? long : BAY_DEPTH;
      const x = Math.round(vert ? (far ? s.x + ox : s.x - ox - w) : s.x - w / 2);
      const y = Math.round(vert ? s.y - h / 2 : (far ? s.y + oy : s.y - oy - h));
      const box = { x0: x, y0: y, x1: x + w, y1: y + h };
      if (x < 3 || y < 3 || x + w > BASE - 3 || y + h > BASE - 3) continue;
      if (clash(box) || crossed(box)) continue;
      parkingLot(g, rng, T, x, y, w, h, vert);
      return box;
    }
  }
  return null;
}

// ---- roadside traffic ----
/**
 * What was on the road when it stopped being a road.
 *
 * Placed off the roadway's OWN centreline samples rather than scattered on the
 * tile: take a sample, take a side, and step out across the band. That gets a
 * wreck lying parallel to the carriageway it was on — whichever way the road
 * runs, and around a bend as readily as along a straight — without the pass
 * ever being told the road's shape. It also gets the wreck onto the shoulder
 * instead of the middle of the lane, which is where an abandoned car ends up.
 *
 * A trunk carried the traffic; a back lane carried less of it. Count follows
 * the rank the tile's roadway is drawn at.
 */
const ROADSIDE_COUNT = { major: [1, 3], minor: [0, 1] };

function drawRoadside(g, rng, T, samples, rank, keepOut) {
  if (!samples?.length) return;
  const [lo, hi] = ROADSIDE_COUNT[rank === MAJOR_RANK ? 'major' : 'minor'];
  for (let i = 0, n = ri(rng, lo, hi); i < n; i++) {
    const s = samples[ri(rng, 0, samples.length - 1)];
    // a sample is narrow ALONG the road and wide ACROSS it, so the wider half
    // says which way the traffic was going
    const vert = s.hx > s.hy;
    const far = rng() < 0.5;                  // which shoulder
    const bite = ri(rng, 0, 5);               // how far it is still on the tarmac
    const w = vert ? CAR_WIDE : CAR_LONG, h = vert ? CAR_LONG : CAR_WIDE;
    const x = Math.round(vert
      ? (far ? s.x + s.hx - bite : s.x - s.hx - w + bite)
      : s.x - w / 2);
    const y = Math.round(vert
      ? s.y - h / 2
      : (far ? s.y + s.hy - bite : s.y - s.hy - h + bite));
    if (x < 3 || y < 3 || x + w > BASE - 3 || y + h > BASE - 3) continue;
    if (keepOut.some(k => x <= k.x1 && x + w >= k.x0 && y <= k.y1 && y + h >= k.y0)) continue;
    vehicle(g, rng, T, x, y, vert);
  }
}

// ---- vegetation pass (per region backdrop, drawn over the content) ----
// Positions strongly favor the corner quadrants so growth stays off the
// road/path connectors — a stray tree over the roadway is allowed but rare.
//
// `keepOut` is the footprint of every structure the recipe actually drew. A
// canopy OVERHANGING a roof is the point — it is what makes the growth read as
// having taken the place back — but a trunk standing on one is not, so a spot
// that lands too near a building is re-rolled. Re-rolling is safe here where it
// would not be inside a recipe: vegetation has its own seeded stream, both the
// shadow and content passes replay it from identical copies, and both are
// handed the same keep-out list, so they always agree on where growth went.
const VEG_TRIES = 14;

/**
 * How far a TRUNK stands off a building, as a fraction of its own canopy. At
 * 0.6 a tree keeps most of its spread clear of the roof and laps the rest over
 * the edge. Testing the bare POINT, as this did, let a tree stand one unit from
 * a wall with its entire crown over the building.
 */
const VEG_CLEARANCE = 0.6;
const SHRUB_CLEARANCE = 4;      // growth with no radius of its own to scale by

/** @returns a spot, or null when there is nowhere on this tile to plant one. */
function vegSpot(rng, keepOut, clear = SHRUB_CLEARANCE) {
  const roll = () => {
    const band = () => (rng() < 0.5 ? ri(rng, 9, 33) : ri(rng, 84, 108));
    if (rng() < 0.8) return [band(), band()];
    return rng() < 0.5 ? [band(), ri(rng, 9, 108)] : [ri(rng, 9, 108), band()];
  };
  if (!keepOut?.length) return roll();
  const onStructure = ([x, y]) => keepOut.some(b =>
    x >= b.x0 - clear && x <= b.x1 + clear && y >= b.y0 - clear && y <= b.y1 + clear);
  for (let i = 0; i < VEG_TRIES; i++) {
    const spot = roll();
    if (!onStructure(spot)) return spot;
  }
  // Nowhere to put it. Giving up rather than planting anyway is also what ties
  // the density to the open ground: a tile built kerb to kerb grows little, and
  // the same budget over open tarmac lands in full.
  return null;
}

// m = density multiplier (tune.treeDensity, 5x for campsite/water tiles).
// The ceiling bounds how thick one tile can get; it is not a tuning knob, and
// at 12 the rest stops and shorelines were flattening against it.
const VEG_MAX = 24;
const vegCount = (rng, lo, hi, m) => Math.min(VEG_MAX, Math.round(ri(rng, lo, hi) * m));

/**
 * Irregular grass at the foot of a stand of trees.
 *
 * Only where they came out THICK. One tree on a street is a tree in a pavement
 * cutout and the paving around it is intact; three growing into each other have
 * had long enough to break the surface between them. Each qualifying trunk gets
 * its own wobbled patch and the patches merge, so the outline of a stand is the
 * union of its trees rather than a shape drawn on purpose.
 *
 * Laid as GROUND: before the canopies, so it reads as what they are standing
 * in, and outside castShadow, because grass casts nothing.
 */
const STAND_REACH = 24;         // how near two trunks must be to count as one stand
const STAND_MIN = 3;            // ...and how many neighbours before it is one

function grassUnder(g, rng, T, stand, keepOut) {
  // A patch spreads wider than the canopy above it, so the trunk's own
  // clearance does not cover it: without this the grass laps onto the roof
  // next door, and grass on a roof is not a thing that happens.
  const inTheOpen = (x, y, rad) => !keepOut.some(b =>
    x + rad > b.x0 && x - rad < b.x1 && y + rad > b.y0 && y - rad < b.y1);
  for (const [x, y, r] of stand) {
    const near = stand.filter(([ox, oy]) => Math.hypot(ox - x, oy - y) < STAND_REACH).length - 1;
    if (near < STAND_MIN) continue;
    const rad = r + ri(rng, 2, 6);
    if (!inTheOpen(x, y, rad)) continue;
    const left = wobbler(rng), right = wobbler(rng);
    blob(g, x, y, rad, T.veg1, left, right);
    for (let i = 0, n = ri(rng, 6, 14); i < n; i++) {          // tufts through it
      const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * rad;
      g.r(Math.round(x + Math.cos(a) * d), Math.round(y + Math.sin(a) * d),
        ri(rng, 2, 4), 1, T.veg2);
    }
  }
}

// Counts are doubled from what they were, authored here rather than hidden
// behind a global factor. A tree clears by its own canopy; a shrub takes the
// flat margin. Where there is nowhere to plant, nothing is planted — which is
// what puts the extra growth on the open tarmac instead of on the roofs.
//
// Trees are all SITED before any of them is drawn, so grassUnder can see the
// whole stand and get its grass down underneath them.
const TD_VEGETATION = {
  city(g, rng, T, m, keep) {                                         // scattered street trees
    const stand = [];
    for (let i = 0, n = vegCount(rng, 2, 4, m); i < n; i++) {
      const r = ri(rng, 6, 9), s = vegSpot(rng, keep, Math.round(r * VEG_CLEARANCE));
      if (s) stand.push([s[0], s[1], r]);
    }
    grassUnder(g, rng, T, stand, keep);
    for (const [x, y, r] of stand) tree(g, rng, T, x, y, r);
    for (let i = 0, n = vegCount(rng, 2, 6, m); i < n; i++) {
      const s = vegSpot(rng, keep);
      if (s) bushPatch(g, rng, T, s[0], s[1]);
    }
  },
  greenhouse(g, rng, T, m, keep) {                                   // heavy regrowth
    const stand = [];
    for (let i = 0, n = vegCount(rng, 4, 8, m); i < n; i++) {
      const r = ri(rng, 9, 15), s = vegSpot(rng, keep, Math.round(r * VEG_CLEARANCE));
      if (s) stand.push([s[0], s[1], r]);
    }
    grassUnder(g, rng, T, stand, keep);
    for (const [x, y, r] of stand) tree(g, rng, T, x, y, r);
    for (let i = 0, n = vegCount(rng, 6, 12, m); i < n; i++) {
      const s = vegSpot(rng, keep);
      if (s) bushPatch(g, rng, T, s[0], s[1]);
    }
  },
  harbor(g, rng, T, m, keep) {                                       // salt scrub
    for (let i = 0, n = vegCount(rng, 2, 4, m); i < n; i++) {
      const s = vegSpot(rng, keep);
      if (s) bushPatch(g, rng, T, s[0], s[1]);
    }
    const stand = [];
    for (let i = 0, n = vegCount(rng, 0, 2, m); i < n; i++) {
      const s = vegSpot(rng, keep, Math.round(6 * VEG_CLEARANCE));
      if (s) stand.push([s[0], s[1], 6]);
    }
    grassUnder(g, rng, T, stand, keep);
    for (const [x, y, r] of stand) tree(g, rng, T, x, y, r);
  },
  foundry(g, rng, T, m, keep) {                                      // dead snags
    for (let i = 0, n = vegCount(rng, 0, 2, m); i < n; i++) {
      const s = vegSpot(rng, keep);
      if (s) deadTree(g, rng, T, s[0], s[1]);
    }
    if (rng() < 0.8 * m) {
      const s = vegSpot(rng, keep);
      if (s) bushPatch(g, rng, T, s[0], s[1]);
    }
  },
  underground(g, rng, T, m, keep) {                                  // bioluminescent fungi
    for (let i = 0, n = vegCount(rng, 2, 6, m); i < n; i++) {
      const s = vegSpot(rng, keep);
      if (s) glowShroom(g, rng, T, s[0], s[1]);
    }
  },
};

// ===============================
// REGION SIGNATURE STRUCTURES
// ===============================
// The one drawing pass that is about a PARTICULAR region rather than about a
// town. Everything else on a tile — the recipes, the road network, the parking,
// the ruins — is deliberately region-agnostic, which is right for a game where
// the same fifteen room types appear everywhere. The cost of that was that five
// regions rendered as one settlement under five palettes: same footprints, same
// silhouettes, a colour filter apart.
//
// This layer is the answer, and it is kept to the same shape as FLOORS,
// TD_VEGETATION and TD_OVERLAYS above it — a table keyed by theme.backdrop — so
// there is one obvious place to add a region's own architecture and one obvious
// place to look for it.
//
// The contract:
//   (g, rng, T, tune, ctx) -> Array<{x0,y0,x1,y1}>   the footprints it drew
//   ctx = { keep, samples, conn, col, row, built, recipe }
// Returning the footprints matters: the growth pass has to keep off them, the
// same way it keeps off everything the recipe drew.

/**
 * Whether a signature prop fires on this tile. Scaled by the region's own
 * `signature` knob (0 turns the layer off entirely) and by how built up the
 * district is — an outskirt gets fewer monuments than a core, for the same
 * reason it gets fewer buildings.
 */
const sigRoll = (rng, chance, tune, built) =>
  rng() < chance * (tune.signature ?? 1) * (built != null ? 0.5 + 0.7 * built : 1);

/**
 * Somewhere on the tile to stand a prop of this size: clear of everything
 * already placed, off the roadway, and held inside TILE_CLAMP. The box-based
 * sibling of vegSpot, and it gives up the same way — on a tile with no room
 * left, the monument that was never built is the right answer, and it is what
 * ties this layer's density to the open ground.
 */
function sigSpot(rng, ctx, w, h, tries = 12) {
  const lo = TILE_CLAMP, hiX = BASE - TILE_CLAMP - w, hiY = BASE - TILE_CLAMP - h;
  if (hiX < lo || hiY < lo) return null;
  for (let i = 0; i < tries; i++) {
    const x = ri(rng, lo, hiX), y = ri(rng, lo, hiY);
    const b = { x0: x, y0: y, x1: x + w, y1: y + h };
    const clash = ctx.keep.some(k => b.x0 <= k.x1 && b.x1 >= k.x0 && b.y0 <= k.y1 && b.y1 >= k.y0)
      || (!!ctx.samples?.length && runsThrough(ctx.samples, b));
    if (!clash) return b;
  }
  return null;
}

// ---- world-lattice features ----
// TILE_CLAMP exists because a neighbour draws its OWN recipe and never
// continues this tile's, so a roof clipped by the canvas reads as a fault. A
// feature laid on a world lattice is the exception, and it is exempt by
// construction: both tiles derive the identical run from the same hashed world
// cell, both draw it, and each canvas clips its own half. The join is exact
// because neither tile decided anything locally.
//
// This is the lightWellsNear trick generalised, and it is what breaks the
// 120-unit rhythm the map otherwise reads on — a colonnade or a conveyor that
// marches across four tiles is the strongest cue that the map is one place
// rather than a grid of stamps.
/**
 * @param {number} cell  authoring units between candidate features
 * @returns {Array<{x,y,len,vert}>} runs in TILE-LOCAL coordinates
 */
// A run is up to LEN_MAX cells long, so a tile has to consider every cell that
// could REACH it, not just the ones next door. Searching ±1 cell looked right
// and was not: a long run originating two cells west was drawn by one tile and
// not by its neighbour, so it stopped dead at the join — the exact failure the
// whole world-lattice idea exists to avoid, and invisible in any single tile.
const LEN_MIN = 1.2, LEN_MAX = 2.2;                 // run length, in cells
const FEATURE_RADIUS = Math.ceil(LEN_MAX) + 1;      // cells to search either way

/**
 * @param {number} cell  authoring units between candidate features
 * @returns {Array<{x,y,len,vert}>} runs in TILE-LOCAL coordinates
 */
function worldFeaturesNear(col, row, cell, salt, density) {
  const out = [];
  const cx = Math.floor((col * BASE) / cell), cy = Math.floor((row * BASE) / cell);
  for (let gy = cy - FEATURE_RADIUS; gy <= cy + FEATURE_RADIUS; gy++) {
    for (let gx = cx - FEATURE_RADIUS; gx <= cx + FEATURE_RADIUS; gx++) {
      if (noiseHash(gx * 13 + salt, gy * 29 + salt) > density) continue;
      const vert = noiseHash(gx + salt, gy + 5 + salt) < 0.5;
      const len = Math.round(cell * (LEN_MIN + (LEN_MAX - LEN_MIN) * noiseHash(gx + 3 + salt, gy + 11 + salt)));
      const wx = (gx + 0.1 + 0.8 * noiseHash(gx + 17 + salt, gy + salt)) * cell;
      const wy = (gy + 0.1 + 0.8 * noiseHash(gx + salt, gy + 23 + salt)) * cell;
      // tile-local, so a run that straddles the join is drawn by both sides
      const x = Math.round(wx - col * BASE), y = Math.round(wy - row * BASE);
      // Reject on the run's whole BOX, both axes. Testing only the long axis
      // kept vertical runs that sat entirely off the west edge.
      const x1 = x + (vert ? FEATURE_TH : len), y1 = y + (vert ? len : FEATURE_TH);
      if (x1 < 0 || y1 < 0 || x > BASE || y > BASE) continue;
      out.push({ x, y, len, vert });
    }
  }
  return out;
}

// Widest a lattice run gets across its axis; only used to reject runs that
// cannot touch the tile, so an over-estimate is harmless.
const FEATURE_TH = 22;

// ---- the props ----

/**
 * The monumental tier: a tower's footprint seen from above. This is the one
 * silhouette that has to read at 80px, so it is drawn big, in the `mass`
 * material rather than the ordinary building stock, and it throws a shadow at
 * twice the usual reach because it is the tallest thing for blocks. The skirt
 * of debris around its base is what says the tower is a ruin rather than a
 * warehouse: nothing this tall came through the centuries with its cladding on.
 */
function towerCore(g, rng, T, x, y, w, h) {
  castShadow(() => g.a(SHADOW.a * 0.8, () =>
    g.r(x + SHADOW.dx * 3, y + SHADOW.dy * 3, w, h, T.far)));
  g.r(x, y, w, h, T.mass);
  g.r(x, y, w, 1, T.massLight);                       // lit north and west faces
  g.r(x, y, 1, h, T.massLight);
  g.r(x, y + h - 1, w, 1, T.massDark);
  g.r(x + w - 1, y, 1, h, T.massDark);
  // the service core: every tower of this era has one, and from above it is
  // the only thing that breaks the roof plate
  const cw = Math.max(6, w >> 2), ch = Math.max(6, h >> 2);
  const cxp = x + ((w - cw) >> 1), cyp = y + ((h - ch) >> 1);
  g.r(cxp, cyp, cw, ch, T.massDark);
  g.r(cxp, cyp, cw, 1, T.mass);
  g.a(0.35, () => {                                    // curtain-wall mullions
    for (let i = 6; i < w - 2; i += 6) g.r(x + i, y + 1, 1, h - 2, T.massDark);
  });
  rubble(g, rng, T, x - 5, x + w + 5, y - 5, y + h + 5, Math.max(6, (w * h) / 150));
}

/**
 * PARKED — not referenced by TD_SIGNATURE.city. Kept because the idea is right
 * and the execution is not: at 80px this reads as a plain grey bar, because
 * what says "elevated walkway" is the support piers under it and the shadow it
 * throws across the street, and neither is drawn. Bring it back with those.
 *
 * What is left of an elevated walkway: a deck that starts nowhere and stops in
 * mid-air. Drawn as a world-lattice run so it crosses tiles, which is the whole
 * point — a skybridge that fits inside one cell is a footbridge.
 */
function skybridgeStub(g, rng, T, x, y, len, vert) {
  const th = 11;
  castShadow(() => g.a(SHADOW.a * 0.7, () => g.r(
    x + SHADOW.dx * 2, y + SHADOW.dy * 2, vert ? th : len, vert ? len : th, T.far)));
  pipeRun(g, T, x, y, len, vert, th, 0, 'mass');
  g.a(0.4, () => {                                     // handrail posts down both edges
    for (let d = 3; d < len; d += 5) {
      if (vert) { g.r(x, y + d, 1, 2, T.massDark); g.r(x + th - 1, y + d, 1, 2, T.massDark); }
      else { g.r(x + d, y, 2, 1, T.massDark); g.r(x + d, y + th - 1, 2, 1, T.massDark); }
    }
  });
  // the broken end, ragged and shedding
  const ex = vert ? x : x + len - 4, ey = vert ? y + len - 4 : y;
  g.r(ex, ey, vert ? th : 4, vert ? 4 : th, T.structDark);
  rubble(g, rng, T, ex - 4, ex + th, ey - 4, ey + th, 6);
}

/**
 * A civic perimeter block: a monumental range built right round an open yard.
 *
 * This is what replaced the sign gantry, and the reason is the camera. A sign
 * is defined by its FACE, and from directly overhead a face is an edge — the
 * gantry came out as a small striped bar standing in the open, and no amount of
 * detail on a surface the player cannot see was going to fix that. What reads
 * from above is what is defined by its PLAN, and the plan of a perimeter block
 * — a rectangle with a hole in it — is unmistakable at any size.
 *
 * It pairs with towerCore rather than repeating it: one is a solid mass, this
 * is a hollow one, so the monumental tier has two silhouettes instead of one.
 */
function civicBlock(g, rng, T, x, y, w, h) {
  const th = Math.max(7, Math.min(w, h) >> 2);        // depth of the range
  castShadow(() => g.a(SHADOW.a * 0.6, () =>
    g.r(x + SHADOW.dx * 2, y + SHADOW.dy * 2, w, h, T.far)));
  g.r(x, y, w, h, T.mass);
  g.r(x, y, w, 1, T.massLight);                       // lit north and west faces
  g.r(x, y, 1, h, T.massLight);
  g.r(x, y + h - 1, w, 1, T.massDark);
  g.r(x + w - 1, y, 1, h, T.massDark);
  g.a(0.3, () => {                                    // roof plant along the range
    for (let i = 5; i < w - 4; i += 9) g.r(x + i, y + 2, 4, 3, T.massDark);
    for (let i = 5; i < h - 4; i += 9) g.r(x + 2, y + i, 3, 4, T.massDark);
  });

  // The yard. Open ground, so the region's own floor shows through and the
  // growth has somewhere to be — a courtyard nobody has crossed in centuries.
  const ix = x + th, iy = y + th, iw = w - th * 2, ih = h - th * 2;
  if (iw > 3 && ih > 3) {
    g.r(ix, iy, iw, ih, T.ground2);
    g.a(0.5, () => {
      for (let i = 0, n = Math.round((iw * ih) / 22); i < n; i++) {
        g.r(ix + ri(rng, 0, iw - 1), iy + ri(rng, 0, ih - 1), 2, 1, T.ground1);
      }
    });
    for (let i = 0, n = ri(rng, 3, 7); i < n; i++) {  // what has come up in it
      g.r(ix + ri(rng, 0, Math.max(0, iw - 3)), iy + ri(rng, 0, Math.max(0, ih - 2)),
        ri(rng, 2, 4), 2, rng() < 0.6 ? T.veg1 : T.veg2);
    }
    // the yard is a well: its own walls light the opposite way to the outside
    g.r(ix, iy, iw, 1, T.massDark);
    g.r(ix, iy, 1, ih, T.massDark);
    g.r(ix, iy + ih - 1, iw, 1, T.massLight);
    g.r(ix + iw - 1, iy, 1, ih, T.massLight);
  }

  // ...and a length of the range that has come down, which is what makes it a
  // ruin rather than a building. Taken out of one side, full depth, so the yard
  // is open to the street through the gap.
  for (let i = 0, n = ri(rng, 1, 2); i < n; i++) {
    const side = ri(rng, 0, 3);
    const span = ri(rng, 9, 18);
    if (side === 0 || side === 1) {
      const gx = x + ri(rng, 2, Math.max(2, w - span - 2));
      const gy = side === 0 ? y : y + h - th;
      g.r(gx, gy, span, th, T.ground2);
      rubble(g, rng, T, gx - 2, gx + span + 2, gy - 2, gy + th + 2, Math.max(4, span >> 1));
    } else {
      const gy = y + ri(rng, 2, Math.max(2, h - span - 2));
      const gx = side === 2 ? x : x + w - th;
      g.r(gx, gy, th, span, T.ground2);
      rubble(g, rng, T, gx - 2, gx + th + 2, gy - 2, gy + span + 2, Math.max(4, span >> 1));
    }
  }
}

/**
 * A glasshouse span: a long shallow roof of glazing, ridged down its spine,
 * with panes missing. The gaps show the growth underneath, which is the thing
 * that makes it read as a greenhouse rather than as a shed — the region's whole
 * story is that the glass failed and the planting did not.
 */
function glassSpan(g, rng, T, x, y, w, h) {
  dropShadow(g, T, x, y, w, h);
  g.r(x, y, w, h, T.mass);
  g.r(x, y, w, 1, T.massLight);
  g.r(x, y + h - 1, w, 1, T.massDark);
  const vert = h > w;
  const ridge = vert ? x + (w >> 1) : y + (h >> 1);
  if (vert) { g.r(ridge, y, 2, h, T.massLight); g.r(ridge + 2, y, 1, h, T.massDark); }
  else { g.r(x, ridge, w, 2, T.massLight); g.r(x, ridge + 2, w, 1, T.massDark); }
  g.a(0.4, () => {                                     // glazing bars
    if (vert) for (let d = 5; d < h - 2; d += 5) g.r(x + 1, y + d, w - 2, 1, T.massDark);
    else for (let d = 5; d < w - 2; d += 5) g.r(x + d, y + 1, 1, h - 2, T.massDark);
  });
  for (let i = 0, n = ri(rng, 2, 5); i < n; i++) {     // panes gone, growth showing through
    const pw = ri(rng, 4, 9), ph = ri(rng, 4, 9);
    const px = ri(rng, x + 1, Math.max(x + 1, x + w - pw - 1));
    const py = ri(rng, y + 1, Math.max(y + 1, y + h - ph - 1));
    g.r(px, py, pw, ph, T.veg1);
    g.a(0.5, () => g.r(px, py, pw, 1, T.veg2));
  }
}

/** A grid of irrigation pipe over what used to be beds. */
function irrigationLattice(g, rng, T, x, y, w, h) {
  for (let d = 0; d < h; d += 9) pipeRun(g, T, x, y + d, w, false, 3, 0);
  pipeRun(g, T, x, y, h, true, 4, 0);                  // the header main they all feed off
  g.a(0.4, () => {
    for (let d = 6; d < w; d += 12) g.r(x + d, y, 1, h, T.structDark);
  });
}

/** A storage silo: tall, round, with a conical cap and a feed chute. */
function silo(g, rng, T, cx, cy, r) {
  castShadow(() => g.a(SHADOW.a * 0.8, () => disc(g, cx + SHADOW.dx * 2, cy + SHADOW.dy * 2, r, T.far)));
  disc(g, cx, cy, r, T.mass);
  ringO(g, cx, cy, r, T.massDark);
  g.a(0.5, () => disc(g, cx - 2, cy - 2, Math.max(2, r - 5), T.massLight));  // sheen off the cap
  disc(g, cx, cy, Math.max(2, r >> 2), T.massDark);                          // the cap vent
  const a = rng() * Math.PI * 2;                                             // feed chute off one side
  pipeRun(g, T, Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a) * r),
    ri(rng, 9, 18), Math.abs(Math.sin(a)) > 0.5, 4, 0);
}

/** Mooring bollards, set in a row back from the water. */
function bollardRow(g, rng, T, x, y, len, vert) {
  for (let d = 0; d < len; d += 12) {
    const bx = vert ? x : x + d, by = vert ? y + d : y;
    discShadow(g, T, bx, by, 3);
    disc(g, bx, by, 3, T.metal);
    ringO(g, bx, by, 3, T.structDark);
    g.r(bx - 1, by - 1, 2, 2, T.rust);
  }
}

/** A gantry crane straddling a bay: two leg pads and a lattice beam between. */
function gantry(g, rng, T, x, y, len, vert) {
  const th = 7;
  castShadow(() => g.a(SHADOW.a * 0.7, () => g.r(
    x + SHADOW.dx * 2, y + SHADOW.dy * 2, vert ? th : len, vert ? len : th, T.far)));
  pipeRun(g, T, x, y, len, vert, th, 0, 'mass');
  g.a(0.5, () => {                                     // the lattice web
    for (let d = 2; d < len - 2; d += 4) {
      if (vert) { g.r(x + 1, y + d, th - 2, 1, T.massDark); }
      else { g.r(x + d, y + 1, 1, th - 2, T.massDark); }
    }
  });
  for (const end of [0, len - 8]) {                    // leg pads at both ends
    const px = vert ? x - 3 : x + end, py = vert ? y + end : y - 3;
    g.r(px, py, vert ? th + 6 : 8, vert ? 8 : th + 6, T.metal);
    g.r(px, py, vert ? th + 6 : 8, 1, T.structLight);
  }
}

/** A conveyor run: a belt on trestles, seized where it stopped. */
function conveyorRun(g, rng, T, x, y, len, vert) {
  pipeRun(g, T, x, y, len, vert, 9, 15, 'metal');
  g.a(0.5, () => {                                     // the belt itself, darker than its frame
    if (vert) g.r(x + 2, y, 5, len, T.structDark);
    else g.r(x, y + 2, len, 5, T.structDark);
  });
  for (let d = 8; d < len; d += 8) {                   // spillage down both sides
    if (rng() < 0.5) continue;
    const sx = vert ? x + (rng() < 0.5 ? -3 : 9) : x + d;
    const sy = vert ? y + d : y + (rng() < 0.5 ? -3 : 9);
    g.r(sx, sy, 3, 3, T.rust);
  }
}

/** A bunded tank farm: two or three tanks inside a spill wall. */
function tankFarm(g, rng, T, x, y, w, h) {
  g.a(0.5, () => g.r(x, y, w, h, T.structDark));       // the bund floor
  g.r(x, y, w, 2, T.mass); g.r(x, y + h - 2, w, 2, T.mass);
  g.r(x, y, 2, h, T.mass); g.r(x + w - 2, y, 2, h, T.mass);
  const r = Math.max(6, Math.min(w, h) >> 2);
  for (let i = 0, n = ri(rng, 2, 3); i < n; i++) {
    tankTop(g, rng, T, ri(rng, x + r + 3, Math.max(x + r + 3, x + w - r - 3)),
      ri(rng, y + r + 3, Math.max(y + r + 3, y + h - r - 3)), r);
  }
}

/**
 * A structural pillar in a sublevel hall, seen from above: a squat column with
 * a flared capital and a pool of shadow under it. Laid in colonnades on a world
 * lattice — a row of these marching off the edge of the tile and picked up by
 * the neighbour is the single strongest cue that the ceiling is overhead.
 */
function pillarSquat(g, rng, T, cx, cy, r) {
  discShadow(g, T, cx, cy, r + 2);
  disc(g, cx, cy, r, T.massDark);                      // the flared capital
  disc(g, cx, cy, Math.max(2, r - 2), T.mass);
  g.a(0.5, () => disc(g, cx - 1, cy - 1, Math.max(1, r - 4), T.massLight));
  ringO(g, cx, cy, r, T.structDark);
}

/**
 * Ballast, sleepers and two rails, laid down the tile like a minor road.
 *
 * A railway is a ROUTE, so it is drawn as one: it enters at the north edge and
 * leaves at the south, and both crossings come from edgeOffset — the same
 * function the roads use, so the two tiles either side of a join derive the
 * same crossing and the line meets itself exactly. It was a scattered prop
 * before, which gave the map short lengths of track starting and stopping in
 * the middle of nowhere.
 */
const RAIL_TH = 21;

function railBed(g, rng, T, row, col) {
  const th = RAIL_TH;
  // where the line crosses this tile's north and south edges
  const top = BASE / 2 + edgeOffset(row, col, 'n', th);
  const bot = BASE / 2 + edgeOffset(row, col, 's', th);
  for (let y = 0; y < BASE; y++) {
    const t = y / (BASE - 1);
    const s = t * t * (3 - 2 * t);                     // ease between the two crossings
    const cx = Math.round(top + (bot - top) * s);
    const x = cx - (th >> 1);
    g.r(x, y, th, 1, T.ground2);                       // the ballast shoulder
    if (y % 6 < 3) g.r(x + 2, y, th - 4, 1, T.structDark);          // sleepers
    for (const off of [6, th - 8]) {                   // the pair of rails
      g.r(x + off, y, 2, 1, T.metal);
      g.r(x + off, y, 1, 1, T.structLight);
    }
  }
}

/** A tray of cable running along a wall line, lids long gone. */
function cableTray(g, rng, T, x, y, len, vert) {
  pipeRun(g, T, x, y, len, vert, 6, 0, 'structDark');
  g.a(0.6, () => {                                     // the bundle inside it
    if (vert) g.r(x + 1, y, 4, len, T.metal);
    else g.r(x, y + 1, len, 4, T.metal);
  });
  for (let d = 4; d < len; d += 9) {                   // hangers
    if (vert) g.r(x - 2, y + d, 10, 1, T.structDark);
    else g.r(x + d, y - 2, 1, 10, T.structDark);
  }
}

// ---- the table ----
// Chances are authored so a region shows one prop on roughly a third to a half
// of its tiles and two on about a tenth, with the biggest silhouettes rarest.
// A monument on every tile is wallpaper, and wallpaper is what we started with.
//
// Note the lattice densities look small next to the roll chances. They are per
// world CELL and worldFeaturesNear samples a 3x3 neighbourhood of them, so a
// density of 0.09 is roughly a one-in-two chance that a tile has a run on it,
// not one in eleven. Authored at 0.25 they put something on nine tiles in ten.
export const TD_SIGNATURE = {
  // Two monumental silhouettes, one solid and one hollow. Both are read from
  // their PLAN, which is the only thing a top-down camera can show — the two
  // props cut from here failed on exactly that point (a skybridge deck and a
  // sign gantry are both defined by a face, and a face seen from overhead is a
  // bar). A shape that could be anything is worse than no shape: it reads as a
  // rendering fault rather than as architecture.
  city(g, rng, T, tune, ctx) {
    const drew = [];
    // Towers only where the district is genuinely built up — a tower on an
    // outskirt is what made the old map read as one uniform city.
    if ((ctx.built == null || ctx.built >= 0.8) && sigRoll(rng, 0.34, tune, ctx.built)) {
      const w = ri(rng, 27, 42), h = ri(rng, 27, 42);
      // The debris skirt spills SKIRT units past the tower on every side, so the
      // spot is cleared at that size and the tower is drawn inset into it.
      // Clearing only the tower's own box let the rubble land in a sinkhole next
      // door — which the shared "nothing hangs in the hole" test caught.
      const SKIRT = 6;
      const b = sigSpot(rng, ctx, w + SKIRT * 2, h + SKIRT * 2);
      if (b) {
        towerCore(g, rng, T, b.x0 + SKIRT, b.y0 + SKIRT, w, h);
        drew.push(b);
      }
    } else if ((ctx.built == null || ctx.built >= 0.6) && sigRoll(rng, 0.26, tune, ctx.built)) {
      // ...and where a tower did not go up, the block that ran round a yard.
      // Deliberately in the tower's `else`: two monuments on one tile is a
      // skyline, not a street. Gated on density too, if more loosely — a civic
      // range needs a city round it as much as a tower does, just less of one.
      const w = ri(rng, 45, 63), h = ri(rng, 45, 63);
      const b = sigSpot(rng, ctx, w + 4, h + 4);
      if (b) {
        civicBlock(g, rng, T, b.x0 + 2, b.y0 + 2, w, h);
        drew.push(b);
      }
    }
    return drew;
  },
  greenhouse(g, rng, T, tune, ctx) {
    const drew = [];
    if (sigRoll(rng, 0.28, tune, ctx.built)) {
      const vert = rng() < 0.5;
      const w = vert ? ri(rng, 18, 27) : ri(rng, 42, 66);
      const h = vert ? ri(rng, 42, 66) : ri(rng, 18, 27);
      const b = sigSpot(rng, ctx, w, h);
      if (b) { glassSpan(g, rng, T, b.x0, b.y0, w, h); drew.push(b); }
    }
    if (sigRoll(rng, 0.20, tune, ctx.built)) {
      const w = ri(rng, 30, 48), h = ri(rng, 21, 33);
      const b = sigSpot(rng, ctx, w, h);
      if (b) { irrigationLattice(g, rng, T, b.x0, b.y0, w, h); drew.push(b); }
    }
    if (sigRoll(rng, 0.12, tune, ctx.built)) {
      const r = ri(rng, 9, 14);
      const b = sigSpot(rng, ctx, r * 2, r * 2);
      if (b) { silo(g, rng, T, b.x0 + r, b.y0 + r, r); drew.push(b); }
    }
    return drew;
  },
  // The sea wall is NOT here. It belongs to the tide pass (drawSeaWall), because
  // a coast defence and the coast have to be two readings of one field or they
  // disagree — which is exactly what happened while this layer owned it: the
  // wall was laid on a world lattice that knew nothing about the water, so it
  // wandered in and out of the sea on the shore row and, worse, went on being
  // laid two rows inland where there was no water at all. Walls across dry
  // districts were the single loudest wrong note in the region.
  harbor(g, rng, T, tune, ctx) {
    const drew = [];
    // Bollards, though, ARE dockside furniture rather than coast defence, and
    // they read as a quay wherever the water reaches.
    if (ctx.shore === 0) {
      const y = ctx.shoreSide === 's' ? BASE - WATERLINE + 16 : WATERLINE + 4;
      bollardRow(g, rng, T, 8, y, BASE - 8, false);
    }
    if (sigRoll(rng, 0.2, tune, ctx.built)) {
      const b = sigSpot(rng, ctx, 30, 30);
      if (b) {
        for (let i = 0, n = ri(rng, 2, 4); i < n; i++) {
          containerBox(g, rng, T, b.x0 + ri(rng, 0, 15), b.y0 + ri(rng, 0, 3) * 9, rng() < 0.5);
        }
        drew.push(b);
      }
    }
    return drew;
  },
  foundry(g, rng, T, tune, ctx) {
    const drew = [];
    for (const f of worldFeaturesNear(ctx.col, ctx.row, 140, 23, 0.09 * (tune.signature ?? 1))) {
      conveyorRun(g, rng, T, f.x, f.y, f.len, f.vert);
      drew.push({ x0: f.x, y0: f.y, x1: f.x + (f.vert ? 9 : f.len), y1: f.y + (f.vert ? f.len : 9) });
    }
    if (sigRoll(rng, 0.22, tune, ctx.built)) {
      const vert = rng() < 0.5, len = ri(rng, 42, 72);
      const b = sigSpot(rng, ctx, vert ? 13 : len, vert ? len : 13);
      if (b) { gantry(g, rng, T, b.x0 + 3, b.y0 + 3, len, vert); drew.push(b); }
    }
    if (sigRoll(rng, 0.13, tune, ctx.built)) {
      const w = ri(rng, 39, 54), h = ri(rng, 33, 45);
      const b = sigSpot(rng, ctx, w, h);
      if (b) { tankFarm(g, rng, T, b.x0, b.y0, w, h); drew.push(b); }
    }
    return drew;
  },
  underground(g, rng, T, tune, ctx) {
    const drew = [];
    // The line first, so the pillars and everything after it keep off the track.
    if (ctx.rail) {
      railBed(g, rng, T, ctx.row, ctx.col);
      const c = BASE / 2 + edgeOffset(ctx.row, ctx.col, 'n', RAIL_TH);
      const d = BASE / 2 + edgeOffset(ctx.row, ctx.col, 's', RAIL_TH);
      drew.push({ x0: Math.min(c, d) - RAIL_TH, y0: 0, x1: Math.max(c, d) + RAIL_TH, y1: BASE });
    }
    // The colonnade is not rolled for — a sublevel hall always has its pillars,
    // and it is their rhythm across tiles that says there is a ceiling on this.
    //
    // The pitch is wide. It was half this, which put roughly a dozen pillars on
    // every tile and read as a car park rather than as a hall — and it was doing
    // work the rock masses in the corners already do, since a hall with its
    // walls coming in needs fewer columns, not more.
    const pitch = 55, r = 7;
    const ox = ((ctx.col * BASE) % pitch + pitch) % pitch;
    const oy = ((ctx.row * BASE) % pitch + pitch) % pitch;
    for (let py = -oy; py < BASE + pitch; py += pitch) {
      for (let px = -ox; px < BASE + pitch; px += pitch) {
        const cx = px + (pitch >> 1), cy = py + (pitch >> 1);
        if (cx < -r || cy < -r || cx > BASE + r || cy > BASE + r) continue;
        const b = { x0: cx - r, y0: cy - r, x1: cx + r, y1: cy + r };
        // `drew` as well as ctx.keep: the rail bed went down a moment ago and a
        // pillar standing in the four-foot is the same mistake as one standing
        // in the roadway.
        const hits = k => b.x0 <= k.x1 && b.x1 >= k.x0 && b.y0 <= k.y1 && b.y1 >= k.y0;
        if (ctx.keep.some(hits) || drew.some(hits)) continue;
        if (ctx.samples?.length && runsThrough(ctx.samples, b)) continue;
        pillarSquat(g, rng, T, cx, cy, r);
        drew.push(b);
      }
    }
    if (sigRoll(rng, 0.22, tune, ctx.built)) {
      const vert = rng() < 0.5, len = ri(rng, 42, 78);
      const b = sigSpot(rng, ctx, vert ? 10 : len, vert ? len : 10);
      if (b) { cableTray(g, rng, T, b.x0 + 2, b.y0 + 2, len, vert); drew.push(b); }
    }
    return drew;
  },
};

// Small standing-water lakes: 2-3 overlapping lobes each give irregular
// shapes, drawn like the water tile — bank ring, opaque water, sparkle
// glints — just smaller. All banks across every lake are laid down before
// any water, so one lake's shore never cuts across another's surface where
// they overlap; touching lakes merge into a single larger body.
function drawLakes(g, rng, T, count) {
  const lobes = [];
  for (let i = 0; i < count; i++) {
    const cx = ri(rng, 21, 96), cy = ri(rng, 21, 96);
    for (let j = 0, m = ri(rng, 2, 3); j < m; j++) {
      lobes.push([cx + ri(rng, -9, 9), cy + ri(rng, -9, 9), ri(rng, 6, 12)]);
    }
  }
  for (const [x, y, r] of lobes) disc(g, x, y, r + 3, T.ground2);  // all banks first
  for (const [x, y, r] of lobes) disc(g, x, y, r, T.water1);       // then all water
  for (const [x, y, r] of lobes) {
    if (rng() < 0.8) g.r(x - (r >> 1) + ri(rng, 0, r), y + ri(rng, -3, 3), ri(rng, 4, 7), 1, T.water2); // glints
  }
}

// ---- the tide ----
// Where the water is, on a region the sea got into. The old flood pass dropped
// one to three lakes on every tile, which read as polka dots: uniformly damp,
// with no sense that the water had come from anywhere. This one grades inland
// from an ocean along one edge of the map, so the same region reads drowned at
// the shore, tidal a row in, and dry at the far side.
//
// Two rules make it work across tile joins without any tile knowing what its
// neighbour drew:
//
//   1. Depth is measured in WORLD units from the ocean edge, not in rows. The
//      shore row's far edge is 119 and the next row's near edge is 120, so the
//      waterline is continuous across a horizontal seam. Measuring per row would
//      put a step at every join.
//   2. The wobble that makes the waterline ragged is sampled on world X, so two
//      tiles either side of a vertical seam compute the identical value there.
//
// WATERLINE is half a tile on purpose. The shore row has to stay walkable — a
// row of impassable water would be a balance change, and this is meant to be
// nothing but a change of scenery — so the sea takes the outer half of those
// tiles and stops.
//
// The line itself is the SEA WALL, not a noise contour. A ragged natural
// waterline with a wall drawn on top of it read as neither: the wall wandered
// in and out of the water instead of holding it. So the wall is straight, it
// defines where the sea stops, and it has BREACHES — and the breaches are the
// point. They say why this district drowned, they give the water a way in, and
// every inland flood traces back through one of them. A sea defence that failed
// somewhere is a story; a sea defence with water on both sides is a mistake.
// How far inland the wall — and so the sea — reaches. Set so the wall clears
// the roadway rather than being cut in half by it: an east-west road runs down
// the tile's mid-line and is ROAD_W wide, so it occupies 45..75, and the wall
// has to finish before that. The coast road then runs just INSIDE the defence,
// which is where a coast road goes.
const WATERLINE = 45;
const WALL_TH = 9;         // the wall's own thickness
const BREACH_CELL = 190;      // one gap per this much wall
const BREACH_HALF_MIN = 16;   // ...of this half-width
const BREACH_HALF_MAX = 27;
const BREACH_SHOULDER = 14;   // how quickly a gap opens to full width
const SPILL = 130;         // how far inland the widest breach reaches
const REACH = 330;         // how far inland standing water is found at all
// The low-ground field does NOT start at the wall. On the shore row the only
// water past the wall should be what came through a gap — that is what makes
// the breaches read as the cause rather than as decoration, and it is what
// keeps the landward half of the row looking like land.
const LOW_START = 85;
const TIDE_STEP = 3;       // sampling resolution, in authoring units

/**
 * How far open the wall is at a world point: 0 is solid masonry, 1 is the
 * middle of a gap. Smooth rather than boolean so a breach has shoulders and the
 * water fans out of it instead of leaving through a slot.
 *
 * Each BREACH_CELL of wall gets exactly ONE gap, positioned and sized from that
 * cell's own hash. The first cut thresholded a noise field instead, and over
 * the map's actual span that field never once crossed the threshold — the wall
 * came out unbroken end to end and no water got through anywhere. A design that
 * REQUIRES gaps should not be expressed as a hope that some noise clears a bar.
 */
export function breachAt(wx) {
  const c = Math.floor(wx / BREACH_CELL);
  let open = 0;
  // the neighbouring cells too, so a gap sitting near a cell edge is not clipped
  for (let i = c - 1; i <= c + 1; i++) {
    const mid = (i + 0.25 + 0.5 * noiseHash(i * 7 + 3, 91)) * BREACH_CELL;
    const half = BREACH_HALF_MIN + (BREACH_HALF_MAX - BREACH_HALF_MIN) * noiseHash(i * 11, 57);
    const d = Math.abs(wx - mid);
    if (d < half) open = Math.max(open, Math.min(1, (half - d) / BREACH_SHOULDER));
  }
  return open;
}

/**
 * The SEA: everything seaward of the wall, plus what is coming through the gaps
 * in it. Above zero is water.
 *
 * Kept apart from the standing water below because they are drawn differently —
 * the sea and its spill are deep water, the pools left behind inland are not,
 * and telling them apart is the difference between "the coast got in here" and
 * "this street holds a puddle". A single depth threshold could not say it: the
 * far end of a spill fan is shallow and still sea.
 */
function tideSea(wx, depthIn, level) {
  const wall = WATERLINE * level;
  if (depthIn < wall) return 1 - 0.4 * (depthIn / Math.max(1, wall));   // open sea
  const open = breachAt(wx);
  const past = depthIn - wall;
  // Through a breach: a fan of water spreading inland from the gap, narrowing
  // as it goes. This is the ONLY thing that connects the inland water to the
  // sea, so it is deliberately generous near the wall.
  // A SUBTRACTIVE taper, not a multiplicative one. Scaling the gap's width by
  // distance keeps every gap wet for the whole spill length, just fainter, so
  // the wetted area behind the wall is the gap's full width all the way in.
  // Subtracting distance instead means only the wider gaps reach far, and the
  // wet patch narrows to a point — which is what water coming through a hole
  // and spreading out actually looks like from above.
  const fan = open > 0 ? open - past / (SPILL * level) : -1;
  // Smoothstep the fan. A linear taper meets the ground along a straight line,
  // which was half of why the flood used to have corners.
  return fan > 0 ? fan * fan * (3 - 2 * Math.min(1, fan)) : fan;
}

/**
 * The STANDING WATER: what the sea left behind once it was through the wall.
 *
 * One continuous field rather than scattered pools, and biased toward the gaps —
 * `breachAt` widens it wherever a breach is upstream, so the inland water joins
 * up with the fan that fed it instead of sitting in the map as an archipelago
 * nothing explains.
 */
function tideStanding(wx, depthIn, level, roadBias) {
  const past = depthIn - WATERLINE * level;
  if (past < 0) return -1;
  // How far the water got is a property of the GROUND, so it varies along the
  // coast. Holding it at a constant depth put the inland edge of the flood on a
  // ruler-straight line right across the map — the one thing that says "field
  // evaluated against a threshold" rather than "water found the low ground".
  // Both ends wander, so neither edge is a horizon.
  const drift = smoothNoise(wx / 260 + 51.3, 9.1);
  const reach = REACH * (0.45 + 1.1 * drift) * level;
  const start = LOW_START * (0.6 + 0.8 * smoothNoise(wx / 180 + 7.7, 33.3));
  const inland = (past - start) / Math.max(1, reach);
  if (inland < 0 || inland > 1) return -1;
  // Almost all of the shape comes from ONE coarse octave, smoothstepped, so
  // the body is round and the edges curve. The fine octave that used to carry
  // a fifth of the weight was what gave the flood its crimped, angular outline
  // — detail at that scale reads as noise rather than as a shoreline.
  const n = smoothNoise(wx / 150 + 31.7, depthIn / 150 + 5.1);
  const body = n * n * (3 - 2 * n);
  const fine = smoothNoise(wx / 62 + 3.3, depthIn / 62 + 17.9);
  return (body * 0.9 + fine * 0.1) + roadBias + 0.22 * breachAt(wx) - (0.46 + 0.42 * inland);
}

/**
 * Rasterise the tide over the tile. Walks in coarse rows and emits ONE rect per
 * continuous run of submerged cells rather than one per cell — 1600 samples
 * become something like 120 fills, which is what makes a per-pixel field
 * affordable in a pass that also has to run on 119 tiles.
 */
/**
 * The coast defence, along the waterline, with the gaps the sea came through.
 *
 * Drawn only on the shore row and only where `breachAt` says there is masonry,
 * so the wall and the water are two readings of one field. Both are pure
 * functions of world X, so neighbouring tiles continue the same wall and agree
 * about where each gap starts and stops without either knowing about the other.
 *
 * This is TERRAIN, and it goes down with the floor rather than with the
 * weathering — the wall is effectively where the map stops, so everything the
 * tile is built out of sits on top of it. Drawn late it covered roads and
 * buildings with concrete, which reads as a wall built through a street.
 */
export function seaWallY(shore, shoreSide, level) {
  if (shore !== 0) return null;
  const wall = WATERLINE * level;
  const y0 = shoreSide === 's' ? BASE - wall : wall - WALL_TH;
  return y0 < 0 || y0 + WALL_TH > BASE ? null : Math.round(y0);
}

function drawSeaWall(g, T, ctx, level, side) {
  const y0 = seaWallY(ctx.shore, side, level);
  if (y0 == null) return;
  const wx0 = ctx.col * BASE;
  const y = Math.round(y0);
  let runX = -1;
  const flush = x1 => {
    if (runX < 0) return;
    const w = x1 - runX;
    g.r(runX, y, w, WALL_TH, T.mass);
    g.r(runX, y, w, 1, T.massLight);                        // lit crown
    g.r(runX, y + WALL_TH - 1, w, 1, T.massDark);
    for (let d = 7; d < w; d += 15) {                       // landward buttresses
      if (side === 'n') g.r(runX + d, y + WALL_TH, 5, 5, T.mass);
      else g.r(runX + d, y - 5, 5, 5, T.mass);
    }
    g.a(0.45, () => {                                       // rust weeping down the wet face
      for (let d = 2; d < w; d += 5) {
        g.r(runX + d, side === 'n' ? y : y + WALL_TH - 2, 2, 2, T.rust);
      }
    });
    runX = -1;
  };
  for (let x = 0; x < BASE; x += TIDE_STEP) {
    if (breachAt(wx0 + x) > 0) { flush(x); continue; }      // the gap
    if (runX < 0) runX = x;
  }
  flush(BASE);
  // Rubble where it failed — the ends of the standing sections, spilling into
  // the gap. Without this a breach reads as a wall that was built with a hole.
  let prev = false;
  for (let x = 0; x < BASE; x += TIDE_STEP) {
    const open = breachAt(wx0 + x) > 0;
    if (open !== prev) {
      for (let i = 0; i < 4; i++) {
        g.r(x + ri3(x + i * 7) - 6, y + ri3(x + i * 3) - 3, 3, 2, T.massDark);
      }
    }
    prev = open;
  }
}

/** A tiny deterministic 0..8 jitter off a world coordinate. */
const ri3 = n => Math.floor(noiseHash(n | 0, 977) * 9);

function drawTide(g, rng, T, ctx) {
  const level = Math.max(0.01, ctx.floodLevel ?? 1);
  const side = ctx.shoreSide === 's' ? 's' : 'n';
  const wx0 = ctx.col * BASE;
  // Streets flood first: they are the low ground, and a drowned district reads
  // as one the moment the carriageway goes under while the lots are still dry.
  // The roadway is rasterised once into a coarse mask so the per-cell test is a
  // lookup — testing every sample against every road sample is not affordable.
  const N = Math.ceil(BASE / TIDE_STEP);
  let roadMask = null;
  if (ctx.samples?.length) {
    roadMask = new Uint8Array(N * N);
    for (const s of ctx.samples) {
      const x0 = Math.max(0, ((s.x - s.hx) / TIDE_STEP) | 0), x1 = Math.min(N - 1, ((s.x + s.hx) / TIDE_STEP) | 0);
      const y0 = Math.max(0, ((s.y - s.hy) / TIDE_STEP) | 0), y1 = Math.min(N - 1, ((s.y + s.hy) / TIDE_STEP) | 0);
      for (let yy = y0; yy <= y1; yy++) for (let xx = x0; xx <= x1; xx++) roadMask[yy * N + xx] = 1;
    }
  }
  // Structures stand OUT of the water — the recipe's and the region's own both.
  const standing = [...(ctx.keep || []), ...(ctx.props || [])];
  const blocked = (x, y) => standing.some(b => x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1);

  // The wall went down with the ground, so the water stops at its face rather
  // than washing over the top of it — otherwise the defence disappears under
  // the very sea it is holding back.
  const wallTop = seaWallY(ctx.shore, side, level);
  const onWall = y => wallTop != null && y + TIDE_STEP > wallTop && y < wallTop + WALL_TH;

  // Two lists. The distinction is WHAT the water is first and how deep it is
  // second: standing water left inland is always the light tone, while the sea
  // and its inflows are deep in the body and light around their margins — the
  // shelving edge where a breach spills onto the street. Splitting on depth
  // alone put the same contour through the standing water as through the sea;
  // ignoring depth entirely flattened the inflows into one hard shape.
  const SEA_SHALLOW = 0.35;
  const seaRuns = [], poolRuns = [];
  for (let iy = 0; iy < N; iy++) {
    const y = iy * TIDE_STEP;
    const depthIn = ctx.shore * BASE + (side === 'n' ? y : BASE - 1 - y);
    let runX = -1, runDeep = false;
    const flush = x1 => {
      if (runX >= 0) (runDeep ? seaRuns : poolRuns).push([runX, y, x1 - runX, TIDE_STEP]);
      runX = -1;
    };
    for (let ix = 0; ix < N; ix++) {
      const x = ix * TIDE_STEP, wx = wx0 + x;
      const sea = tideSea(wx, depthIn, level);
      const pool = tideStanding(wx, depthIn, level, roadMask?.[iy * N + ix] ? 0.12 : 0);
      // The wall stops the water only where the wall is actually THERE. Skipping
      // the whole band left a dry stripe straight through every breach — the sea
      // pouring in, then a gap, then the flood again — which is the one place
      // the water most needs to be continuous.
      const walled = onWall(y) && breachAt(wx) <= 0;
      const wet = Math.max(sea, pool) > 0 && !walled && !blocked(x + 1, y + 1);
      if (!wet) { flush(x); continue; }
      // Deep only where the SEA is deep. Standing water never is, however much
      // of it there is — a flooded street is ankle deep whatever its extent.
      const deep = sea >= pool && sea > SEA_SHALLOW;
      if (runX >= 0 && deep !== runDeep) flush(x);
      if (runX < 0) { runX = x; runDeep = deep; }
    }
    flush(BASE);
  }
  for (const [x, y, w, h] of seaRuns) g.r(x, y, w, h, T.water1);
  g.a(0.55, () => { for (const [x, y, w, h] of poolRuns) g.r(x, y, w, h, T.water2); });
  // The line where the water meets the land, picked out along the top of every
  // run so a shelving beach reads as a shore rather than as a flat blue shape.
  g.a(0.5, () => { for (const [x, y, w] of poolRuns) g.r(x, y, w, 1, T.water2); });
  // What is floating in it. Seeded, unlike everything above — this is debris,
  // not terrain, and it only ever lands where the field already said water is.
  const wetRuns = seaRuns.concat(poolRuns);
  if (!wetRuns.length) return;
  for (let i = 0, n = ri(rng, 0, 3); i < n; i++) {
    const [x, y, w] = wetRuns[ri(rng, 0, wetRuns.length - 1)];
    if (w < 15) continue;
    if (rng() < 0.5) containerBox(g, rng, T, x + ri(rng, 0, w - 12), y, rng() < 0.5);
    else rubbleChunk(g, rng, T, x + ri(rng, 0, w - 6), y);
  }
}

// ---- the rock ----
// A sublevel drawn top-down has a problem no other region has: the one thing
// that makes it a sublevel — the ceiling — is the thing a top-down view cannot
// show. Left to the palette alone it came out as an open suburb at night, which
// is why it was the region furthest from its own description.
//
// So the walls come down instead. Rock mass laps into the tile from its corners
// and outer band, carved back around the room, the roadway and the structures,
// which reads as a hall whose roof has come in over the edges of the block. The
// same field grades the darkness: black where the rock is close, clear over the
// middle where the room is. One function, two jobs, and the second one is what
// makes the region legible again — the flat 0.35 wash it replaces took the
// centre of the tile as hard as it took the corners.
const ROCK_STEP = 2;       // sampling resolution: fine enough for a broken edge
const ROAD_R = 26;         // clearance kept around the roadway, so a route never seals
const DARK_MIN = 0.12;     // the darkness wash out in the open
const DARK_MAX = 0.34;     // ...and up against the rock

// A mass is anchored to a tile CORNER — that is, to a vertex of the grid, the
// point four tiles share. Each of those four draws the quarter of it that falls
// on its own canvas, so the mass is one shape spanning a junction rather than
// four shapes that have to be made to agree. Seamless by construction, corner-
// only by construction, and no carving needed to keep the routes open: a vertex
// is ROCK_KEEP away from the nearest ground a connection can cross, and no lobe
// is allowed to reach that far.
//
// This replaces a noise field clipped by a hard cross. That guaranteed the same
// things but looked it — the field's soft blobs came out with two straight sides
// wherever the clip bit, which read as black buildings rather than as stone. The
// shape has to be right in the first place, not corrected afterwards.
const ROCK_DENSITY = 0.55;    // share of grid vertices carrying a mass
const ROCK_R_MIN = 12, ROCK_R_MAX = 19;
const ROCK_WOBBLE = 0.15;     // how far the outline strays from a circle
const ROCK_LOBES = 4;         // satellites per mass, scattered round the vertex
const ROCK_KEEP = 21;         // nearest a connection comes to a vertex

// Where a connection can run, in the VERTEX's own frame: four rays leaving the
// corner along the tile edges, starting ROCK_KEEP out. Expressed from the
// vertex rather than from a tile because that is the frame a mass lives in —
// the same four rays whichever of the four tiles is asking, so every one of
// them fits a lobe identically.
const CROSS_BANDS = [
  [ROCK_KEEP, 0, BASE - ROCK_KEEP, 0],
  [-ROCK_KEEP, 0, -(BASE - ROCK_KEEP), 0],
  [0, ROCK_KEEP, 0, BASE - ROCK_KEEP],
  [0, -ROCK_KEEP, 0, -(BASE - ROCK_KEEP)],
];

// How far rock has to stay off a crossing. The bands above are LINES along the
// tile edges, but what has to stay clear is a strip: a carriageway occupies the
// last few pixels before the join, and a run of rock is filled ROCK_STEP wide
// from its last solid sample. Measuring to the line and allowing only the run
// overhang left rock a pixel inside the strip — which is one pixel of stone
// across a route, and the guard exists precisely so that cannot happen.
const CROSS_CLEAR = ROCK_STEP + 4;

function distToSegment(px, py, [x0, y0, x1, y1]) {
  const dx = x1 - x0, dy = y1 - y0, len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / len2)) : 0;
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy));
}

/**
 * The largest this lobe may be and still clear every crossing band.
 *
 * The guarantee is stated here, once, rather than being hand-computed into the
 * radius constants — which is how it got away twice: the main lobe sits on the
 * diagonal and the satellites do not, so each has a different worst case, and
 * an arithmetic margin that covered one missed the other by a pixel. Shrinking
 * rather than dropping keeps the mass where the map wanted one, and it is
 * deterministic, so all four tiles sharing the vertex shrink it identically.
 */
function fitLobe(x, y, r) {
  let limit = Infinity;
  for (const band of CROSS_BANDS) limit = Math.min(limit, distToSegment(x, y, band));
  return Math.min(r, (limit - CROSS_CLEAR) / (1 + ROCK_WOBBLE));
}

/**
 * The rock lobes touching this tile, in tile-local coordinates.
 *
 * Everything is derived from the VERTEX's own grid coordinates, so all four
 * tiles meeting there compute the identical set — including the wobble, which
 * is seeded from the vertex rather than from the tile.
 */
export function rockLobesNear(col, row) {
  const out = [];
  for (const [vx, vy] of [[0, 0], [BASE, 0], [0, BASE], [BASE, BASE]]) {
    const gx = col + vx / BASE, gy = row + vy / BASE;
    if (noiseHash(gx * 17 + 5, gy * 23 + 9) > ROCK_DENSITY) continue;
    const seed = makePRNG(`rock|${gx}|${gy}`);
    const r = ROCK_R_MIN + (ROCK_R_MAX - ROCK_R_MIN) * seed();
    // Lobes are placed in the VERTEX's frame and then translated onto this
    // tile, so all four tiles round the corner derive one identical mass and
    // each simply draws the part that lands on its own canvas.
    //
    // They used to be offset toward whichever tile was asking, on the reasoning
    // that the diagonal is the direction furthest from every crossing. It is —
    // but it meant the four tiles each pulled a lobe into themselves, and the
    // union came out as a four-pointed clover on every junction, the same way
    // round every time. A mass has to be ONE shape, not four agreeing ones.
    const add = (lx, ly, rad) => {
      const fitted = fitLobe(lx, ly, rad);
      if (fitted <= 2) return;
      // How far this lobe can matter at all. Cached so the per-sample test can
      // reject on a box comparison before it reaches for a hypot and an atan2 —
      // a tile is thousands of samples against a score of lobes, and all but
      // two or three are nowhere near.
      //
      // TWICE the outline's reach, not once: the darkness gradient runs on the
      // same field and fades out over a lobe's width of floor BEYOND the stone,
      // so cutting at the outline would pull the vignette in tight around it.
      const fade = fitted * (1 + ROCK_WOBBLE) * 2;
      out.push({ x: vx + lx, y: vy + ly, r: fitted, fade, wob: wobbler(seed, 5, ROCK_WOBBLE) });
    };
    add(0, 0, r);
    for (let i = 0; i < ROCK_LOBES; i++) {
      const a = seed() * Math.PI * 2;
      const d = 8 + 11 * seed();
      const sr = r * (0.45 + 0.4 * seed());
      add(Math.cos(a) * d, Math.sin(a) * d, sr);
    }
  }
  return out;
}

/**
 * How far INSIDE the rock a point is: above zero is stone, and the larger the
 * value the deeper. Wobbled per lobe, so the outline is an irregular closed
 * curve rather than a threshold on a grid.
 */
function rockDepth(lobes, x, y) {
  let best = -1;
  for (const m of lobes) {
    const dx = x - m.x, dy = y - m.y;
    // Outside the lobe's reach in either axis, it cannot contribute anything
    // above -1, and `best` already starts there. Two comparisons instead of a
    // hypot and an atan2; without it a hollow-core tile was spending millions
    // of transcendentals a frame on lobes at the far corner.
    if (dx > m.fade || dx < -m.fade || dy > m.fade || dy < -m.fade) continue;
    const d = Math.hypot(dx, dy);
    if (d > m.fade) continue;
    const t = Math.atan2(dy, dx) / (Math.PI * 2) + 0.5;
    const rr = m.r * m.wob(t);
    if (rr > 0) best = Math.max(best, 1 - d / rr);
  }
  return best;
}

// How far a mass throws its shadow, and how dark. LONG: a formation runs from
// the floor to the cavern roof, so it is taller than anything built down here
// by an order of magnitude and its shade reaches most of a tile. Scaled by the
// sun's elevation the way a real one is — shortest at noon, drawn right out
// when the shaft comes in low.
const ROCK_SHADOW_LEN = 30;
const ROCK_SHADOW_A = 0.5;

/**
 * The shade a rock mass throws when a daylight shaft is falling on it.
 *
 * Drawn on the overhead channel with the stone, so it lands over the shaft that
 * casts it — a shadow painted UNDER the light would be lit back up by it, which
 * is the one thing a shadow cannot survive.
 *
 * Direction comes from `sunShaft`, the same function that aims the beams, so
 * the shade always falls away from the light rather than in some fixed
 * direction that happens to look right at noon.
 */
// The offset is quantised before the shade is rasterised, so the sun has to
// move a noticeable amount before the work is redone — the same bargain
// staticKey strikes for the building shadows, and for the same reason.
const ROCK_SHADOW_STEP = 3;

/**
 * Where the sun is throwing the shade of a set of upright things, or null if
 * they are throwing none.
 *
 * Nothing in a sublevel casts a shadow through the ordinary machinery: dayState
 * caps the light down here below the threshold at which it gives the sun a
 * direction, so SHADOW comes out as a zero offset and every drop shadow lands
 * exactly under the thing that cast it. That is right for a cavern lit by
 * nothing — and wrong the moment a shaft of daylight comes through the roof.
 *
 * @param {Array<{x,y}>} pts  where the uprights stand, for the light test
 * @param {number} baseLen    how far the shade reaches, before elevation
 */
function shaftShadowPlan(fx, pts, baseLen) {
  if (!fx || !pts.length || !(fx.tune?.lightRays > 0)) return null;
  const { elev, bx, by, ux, uy } = sunShaft(fx.anim);
  if (elev <= 0.03) return null;                      // night: no shaft, no shade
  const wells = lightWellsNear(fx.col, fx.row, fx.tune.lightRays);
  if (!wells.length) return null;
  // How lit the uprights are. One figure for the whole group rather than one
  // per pixel: a well is one to two tiles across, so the light over a single
  // tile barely varies, and this is asked every frame.
  const ox = fx.col * WORLD, oy = fx.row * WORLD;
  const scale = WORLD / BASE;                         // authoring units -> world units
  let lit = 0;
  for (const p of pts) {
    const px = ox + p.x * scale, py = oy + p.y * scale;
    for (const w of wells) {
      lit = Math.max(lit, 1 - Math.hypot(px - w.x - bx, py - w.y - by) / w.r);
    }
  }
  if (lit <= 0.05) return null;
  const len = baseLen * (0.8 + 0.9 * (1 - elev));
  const q = v => Math.round(v * len / ROCK_SHADOW_STEP) * ROCK_SHADOW_STEP;
  return { dx: q(ux), dy: q(uy), lit: Math.min(1, lit) };
}

/**
 * The shade itself, as runs: everything within a lobe's radius of the SEGMENT
 * from where that lobe stands to where its shade falls.
 *
 * A segment, not a point. A shadow is the whole column projected, so it runs
 * from the foot of the stone out to the tip — drawing the offset shape alone
 * left a dark patch floating clear of the mass that threw it, with a gap of
 * lit floor between the two.
 */
function rockShadowRuns(lobes, dx, dy) {
  const N = Math.ceil(BASE / ROCK_STEP);
  const segs = [];
  for (const m of lobes) {
    const x1 = m.x + dx, y1 = m.y + dy;
    // the swept capsule's bounds, for a cheap reject before the distance test
    const lo = { x: Math.min(m.x, x1) - m.r, y: Math.min(m.y, y1) - m.r };
    const hi = { x: Math.max(m.x, x1) + m.r, y: Math.max(m.y, y1) + m.r };
    if (hi.x < 0 || hi.y < 0 || lo.x > BASE || lo.y > BASE) continue;
    segs.push({ seg: [m.x, m.y, x1, y1], r: m.r, lo, hi });
  }
  const runs = [];
  if (!segs.length) return runs;
  for (let iy = 0; iy < N; iy++) {
    const y = iy * ROCK_STEP;
    let runX = -1;
    const flush = x1 => { if (runX >= 0) runs.push([runX, y, x1 - runX, ROCK_STEP]); runX = -1; };
    for (let ix = 0; ix < N; ix++) {
      const x = ix * ROCK_STEP;
      let hit = false;
      for (const s of segs) {
        if (x < s.lo.x || x > s.hi.x || y < s.lo.y || y > s.hi.y) continue;
        if (distToSegment(x, y, s.seg) <= s.r) { hit = true; break; }
      }
      if (!hit) { flush(x); continue; }
      if (runX < 0) runX = x;
    }
    flush(BASE);
  }
  return runs;
}

/**
 * A drawer for one mass's shade, with the rasterised runs cached against the
 * quantised sun offset.
 *
 * Cast from the LOBES, not from the stone this tile happened to rasterise. At
 * this length a shadow lands a good way from the mass that threw it — often in
 * a neighbouring tile — and each tile only ever rasterises the quarter of a
 * mass inside its own bounds. Offsetting those runs would throw away three
 * quarters of every shadow and leave a hard edge down the tile centre lines.
 * The lobes are shared by all four tiles round the vertex, so casting from them
 * means whichever tile the shade falls in draws the whole of it.
 */
// A building, a pillar, a rail gantry — none of them reach the roof, so their
// shade is a fraction of what the stone throws.
const BUILT_SHADOW_LEN = 11;

/** Is (x,y) anywhere the box passes through as it is dragged along (dx, dy)? */
function inSweptBox(b, x, y, dx, dy) {
  let lo = 0, hi = 1;
  if (dx === 0) { if (x < b.x0 || x > b.x1) return false; }
  else {
    const t1 = (x - b.x1) / dx, t2 = (x - b.x0) / dx;
    lo = Math.max(lo, Math.min(t1, t2)); hi = Math.min(hi, Math.max(t1, t2));
  }
  if (dy === 0) { if (y < b.y0 || y > b.y1) return false; }
  else {
    const t1 = (y - b.y1) / dy, t2 = (y - b.y0) / dy;
    lo = Math.max(lo, Math.min(t1, t2)); hi = Math.min(hi, Math.max(t1, t2));
  }
  return lo <= hi;
}

/**
 * The shade the tile's uprights throw, as runs.
 *
 * The footprints themselves are cut out of it. This pass lands after the world
 * effects — it has to, or the shaft it is cast into would wash it out — and by
 * then the buildings have long since been painted, so anything drawn over their
 * own ground would be drawn over THEM. What is left is exactly the part a
 * player can see anyway: the shade beyond the walls.
 */
function structureShadowRuns(boxes, dx, dy) {
  const N = Math.ceil(BASE / ROCK_STEP);
  const runs = [];
  for (let iy = 0; iy < N; iy++) {
    const y = iy * ROCK_STEP;
    let runX = -1;
    const flush = x1 => { if (runX >= 0) runs.push([runX, y, x1 - runX, ROCK_STEP]); runX = -1; };
    for (let ix = 0; ix < N; ix++) {
      const x = ix * ROCK_STEP;
      let onFootprint = false, swept = false;
      for (const b of boxes) {
        // a footprint wins outright: no shade is drawn on the thing casting it
        if (x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1) { onFootprint = true; break; }
        if (!swept && inSweptBox(b, x, y, dx, dy)) swept = true;
      }
      if (onFootprint || !swept) { flush(x); continue; }
      if (runX < 0) runX = x;
    }
    flush(BASE);
  }
  return runs;
}

/** A drawer for the tile's structure shade, cached the same way the rock's is. */
export function makeStructureShadow(boxes) {
  const pts = boxes.map(b => ({ x: (b.x0 + b.x1) / 2, y: (b.y0 + b.y1) / 2 }));
  let key = null, runs = null;
  return (g, T, fx) => {
    const plan = shaftShadowPlan(fx, pts, BUILT_SHADOW_LEN);
    if (!plan) return;
    const k = `${plan.dx},${plan.dy}`;
    if (k !== key) { key = k; runs = structureShadowRuns(boxes, plan.dx, plan.dy); }
    if (!runs.length) return;
    g.a(ROCK_SHADOW_A * plan.lit, () => {
      for (const [x, y, w, h] of runs) g.r(x, y, w, h, T.far);
    });
  };
}

export function makeRockShadow(lobes) {
  let key = null, runs = null;
  return (g, T, fx) => {
    const plan = shaftShadowPlan(fx, lobes, ROCK_SHADOW_LEN);
    if (!plan) return;
    const k = `${plan.dx},${plan.dy}`;
    if (k !== key) { key = k; runs = rockShadowRuns(lobes, plan.dx, plan.dy); }
    if (!runs.length) return;
    g.a(ROCK_SHADOW_A * plan.lit, () => {
      for (const [x, y, w, h] of runs) g.r(x, y, w, h, T.far);
    });
  };
}

function drawRockMass(g, rng, T, ctx) {
  const amount = ctx?.enclosure ?? 0;
  const col = ctx?.col ?? 0, row = ctx?.row ?? 0;
  const N = Math.ceil(BASE / ROCK_STEP);
  // The roadway carves its own corridor. The road network is symmetric across
  // every edge by construction, so both tiles carve the same seam and a route
  // is never sealed off by rock that only one side knew about.
  let roadMask = null;
  if (ctx?.samples?.length) {
    roadMask = new Uint8Array(N * N);
    for (const s of ctx.samples) {
      const x0 = Math.max(0, ((s.x - s.hx - ROAD_R) / ROCK_STEP) | 0);
      const x1 = Math.min(N - 1, ((s.x + s.hx + ROAD_R) / ROCK_STEP) | 0);
      const y0 = Math.max(0, ((s.y - s.hy - ROAD_R) / ROCK_STEP) | 0);
      const y1 = Math.min(N - 1, ((s.y + s.hy + ROAD_R) / ROCK_STEP) | 0);
      for (let yy = y0; yy <= y1; yy++) for (let xx = x0; xx <= x1; xx++) roadMask[yy * N + xx] = 1;
    }
  }
  // ...and so does everything standing on the tile, or the mass buries the room
  // it is supposed to be the walls of.
  const built = (x, y) => ctx?.keep?.some(b =>
    x > b.x0 - 10 && x < b.x1 + 10 && y > b.y0 - 10 && y < b.y1 + 10);

  const lobes = amount ? rockLobesNear(col, row) : [];
  const face = [], deep = [], wash = [];
  for (let iy = 0; iy < N; iy++) {
    const y = iy * ROCK_STEP;
    let runX = -1, runKind = 0;
    const flush = x1 => {
      if (runX < 0) return;
      (runKind === 2 ? deep : face).push([runX, y, x1 - runX, ROCK_STEP]);
      runX = -1;
    };
    for (let ix = 0; ix < N; ix++) {
      const x = ix * ROCK_STEP;
      const m = lobes.length ? rockDepth(lobes, x, y) : -1;
      // The roadway still clears itself. The mass cannot reach a crossing by
      // construction, but a road wanders inside the tile too and rock standing
      // in the carriageway would be wrong wherever it happened.
      const onRoad = roadMask?.[iy * N + ix];
      const solid = m > 0 && !onRoad && !built(x + 1, y + 1);
      // The darkness follows the same field: full out where the rock is close,
      // easing off over the open floor in the middle where the room is. `m` is
      // -1 at twice a lobe's radius and 1 at its core, so the gradient covers
      // roughly a lobe's width of floor outside the stone.
      const near = Math.max(0, Math.min(1, (m + 1) / 1.2));
      wash.push([x, y, DARK_MIN + (DARK_MAX - DARK_MIN) * near]);
      if (!solid) { flush(x); continue; }
      const kind = m > 0.1 ? 2 : 1;
      if (runX >= 0 && kind !== runKind) flush(x);
      if (runX < 0) { runX = x; runKind = kind; }
    }
    flush(BASE);
  }
  // The wash first, so the rock itself is not dimmed by its own shadow.
  // Quantised to a handful of levels and merged into runs — a per-cell alpha
  // would be 1600 fills of near-identical darkness.
  let runA = -1, runX = 0, runY = -1;
  const flushWash = (x1, y, a) => {
    if (runA <= 0.001) return;
    g.a(runA, () => g.r(runX, y, x1 - runX, ROCK_STEP, T.far));
  };
  for (const [x, y, a] of wash) {
    const q = Math.round(a * 12) / 12;
    if (y !== runY || q !== runA) {
      if (runY >= 0) flushWash(y === runY ? x : BASE, runY, runA);
      runA = q; runX = x; runY = y;
    }
  }
  if (runY >= 0) flushWash(BASE, runY, runA);
  // Everything upright on the tile throws shade in a shaft, not just the stone.
  const upright = [...(ctx?.keep || []), ...(ctx?.props || [])];
  // Lobes, not runs: a tile with a mass at one of its corners may rasterise
  // none of it (buried under a structure, or the whole quarter suppressed) and
  // still be the tile that mass throws its shade into. And a tile with no mass
  // at all still has buildings on it, which is why the structures are their own
  // clause rather than something the rock's presence gates.
  if (!amount || (!lobes.length && !upright.length)) return;

  // Facets are rolled HERE, while the seeded stream is in hand, and drawn from
  // the resulting list below. The draw is replayed every frame on the overhead
  // channel and a replay must never touch the rng — it would reshuffle the
  // stone on every tick.
  const facets = [];
  for (const [x, y, w] of face.concat(deep)) {
    if (w < 5 || rng() < 0.5) continue;
    const fw = ri(rng, 2, Math.min(9, w));
    facets.push([x + ri(rng, 0, w - fw), y, fw, rng() < 0.55]);
  }

  // The mass goes ON TOP OF EVERYTHING, above even the clouds and the daylight
  // shafts. It is a cutaway through stone that carries on up to the cavern
  // roof, so nothing in the room can be in front of it and a shaft of daylight
  // crossing one would be light coming through the ceiling.
  //
  // Rock is not a silhouette either, and drawing it as two flat fills of the
  // void colour made it one — a black shape with a hard edge, which the eye
  // files as a building seen from above. What says stone is the interior:
  // facets that catch what little light there is, crevices that catch none,
  // and a broken lit edge where the mass has come away.
  // Built once per still-layer repaint, so their run caches survive frames.
  // The buildings, pillars and gantries are shorter than the stone, so their
  // shadows are, and they go down first because the stone stands over them all.
  const builtShade = upright.length ? makeStructureShadow(upright) : null;
  const rockShade = makeRockShadow(lobes);
  overhead(g, T, (g2, T2, fx) => {
    // The shadows first: they darken the lit floor, and the stone then covers
    // the end of its own. Skipped entirely when the tile is nowhere near a
    // shaft, which is most of them — a sublevel is dark, and a shadow with no
    // light to cast it is just a smudge.
    if (builtShade) builtShade(g2, T2, fx);
    rockShade(g2, T2, fx);
    for (const [x, y, w, h] of face) g2.r(x, y, w, h, T2.structDark);
    for (const [x, y, w, h] of deep) g2.r(x, y, w, h, shade(T2.structDark, 0.6));
    g2.a(0.5, () => {
      for (const [x, y, w, light] of facets) g2.r(x, y, w, ROCK_STEP, light ? T2.mass : T2.far);
    });
    g2.a(0.4, () => {                                 // spall along the rock face
      for (const [x, y, w] of face) if (w > 5) g2.r(x, y, w, 1, T2.mass);
    });
  });
}

// ---- top-down weathering (same overlay keys as the side view) ----
export const TD_OVERLAYS = {
  dust(g, rng, T) {
    for (let i = 0; i < 60; i++) {                             // wind-blown grit
      g.a(0.12, () => g.r(ri(rng, 0, 118), ri(rng, 0, 118), ri(rng, 1, 3), 1, T.accent));
    }
    for (let i = 0; i < 6; i++) {                              // drift streaks in the lee of things
      const dx = ri(rng, 6, 100), dy = ri(rng, 6, 114);
      g.a(0.08, () => g.r(dx, dy, ri(rng, 9, 20), ri(rng, 1, 2), T.structLight));
    }
  },
  vines(g, rng, T) {
    for (let i = 0; i < 4; i++) {                              // creepers reach in from the edges
      const edge = ri(rng, 0, 3);
      const p = ri(rng, 12, 105);
      const [x, y] = edge === 0 ? [p, 3] : edge === 1 ? [p, 114] : edge === 2 ? [3, p] : [114, p];
      foliageClump(g, rng, T, x, y, ri(rng, 3, 5));
      // a runner trailing inward from the clump
      let rx = x, ry = y;
      for (let s = 0, n = ri(rng, 8, 18); s < n; s++) {
        rx += ri(rng, -2, 2) + (x < 20 ? 1 : x > 100 ? -1 : 0);
        ry += ri(rng, -2, 2) + (y < 20 ? 1 : y > 100 ? -1 : 0);
        g.r(rx, ry, 1, 1, T.veg1);
        if (s % 4 === 0) g.r(rx + ri(rng, -1, 1), ry + ri(rng, -1, 1), 1, 1, T.veg2);
      }
    }
    g.a(0.05, () => g.r(0, 0, BASE, BASE, T.veg2));
  },
  flood(g, rng, T, recipe, ctx) {
    // A tile with no shoreline stamped on it — the image lab previewing a room
    // on its own, or a save written before the sea existed — gets the old
    // scattered standing water rather than nothing at all.
    if (recipe === 'water' || recipe === 'portal') return;
    if (ctx?.shore == null) {
      drawLakes(g, rng, T, ri(rng, 1, 3));
    } else {
      drawTide(g, rng, T, ctx);
    }
    for (let i = 0; i < 24; i++) g.r(ri(rng, 6, 111), ri(rng, 6, 111), ri(rng, 1, 3), ri(rng, 1, 2), T.rust);
  },
  // Rust is what happens to STEEL, so it goes on the steel. Scattered over the
  // whole tile it landed on grass, in sinkholes and across open chasms — which
  // reads as an orange filter over the region rather than as corrosion, and it
  // was the one thing making a foundry tile look painted rather than weathered.
  //
  // What is left on open ground is the oil: a yard that ran machinery for a
  // century has stains on its hardstanding, and those belong on the floor.
  rust(g, rng, T, recipe, ctx) {
    const steel = [...(ctx?.keep || []), ...(ctx?.props || [])];
    for (const b of steel) {
      const w = b.x1 - b.x0, h = b.y1 - b.y0;
      if (w < 6 || h < 6) continue;
      // Patches, not a wash: a few irregular blooms per structure, each a
      // cluster of overlapping discs so the outline is ragged.
      for (let i = 0, n = ri(rng, 1, 3); i < n; i++) {
        const cx = b.x0 + ri(rng, 2, Math.max(2, w - 2));
        const cy = b.y0 + ri(rng, 2, Math.max(2, h - 2));
        const r = ri(rng, 2, Math.max(2, Math.min(7, Math.min(w, h) >> 1)));
        g.a(0.45, () => {
          for (let j = 0, m = ri(rng, 2, 4); j < m; j++) {
            const dx = cx + ri(rng, -r, r), dy = cy + ri(rng, -r, r);
            // clipped to the structure — a bloom that spills off its own edge
            // is back to being paint on the ground
            const rr = Math.max(1, Math.min(r, dx - b.x0, b.x1 - dx, dy - b.y0, b.y1 - dy));
            disc(g, dx, dy, rr, T.rust);
          }
        });
      }
      // and the streaks running down off it
      for (let i = 0, n = ri(rng, 1, 3); i < n; i++) {
        g.a(0.3, () => g.r(b.x0 + ri(rng, 0, Math.max(0, w - 4)), b.y1 - ri(rng, 0, 2),
          ri(rng, 2, 5), 1, T.rust));
      }
    }
    // Oil on the hardstanding, wherever the ground is open.
    for (let i = 0, n = ri(rng, 1, 2); i < n; i++) {
      g.a(0.3, () => disc(g, ri(rng, 24, 93), ri(rng, 24, 93), ri(rng, 6, 10), T.structDark));
    }
  },
  dark(g, rng, T, recipe, ctx) {
    drawRockMass(g, rng, T, ctx);
  },
};

// ===============================
// RENDERER (browser only)
// ===============================
// The game draws live tiles via drawRoomTile (see raidMapAnim.js); the
// static renderRoomImage below remains for the image lab's export tooling.

// ---- cloud cover ----
// Deterministic 2D noise. Clouds are sampled in WORLD coordinates (grid
// col/row * BASE + pixel), so every tile reads the same global cloud field
// and banks flow seamlessly across tile edges — no whole-map canvas needed.
function hashBits(x, y) {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

// 0..1 from the same bits. Daylight wells, shorelines and the lamp flicker
// all sample this and the value noise below; only the clouds moved on to
// gradient noise, so those are left exactly as they drew before.
function noiseHash(x, y) {
  return hashBits(x, y) / 4294967296;
}

function smoothNoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const a = noiseHash(xi, yi), b = noiseHash(xi + 1, yi);
  const c = noiseHash(xi, yi + 1), d = noiseHash(xi + 1, yi + 1);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

// Gradient (Perlin) noise, which the clouds use in place of the value noise
// above. Value noise interpolates four random HEIGHTS and the lattice
// survives it: features line up with the grid axes and a big bank comes out
// a bumpy quilt.
// Gradient noise interpolates four random DIRECTIONS through a quintic
// fade, which is the shape every cloud and terrain generator has been built
// on since Perlin '85. Four dot products dearer per sample, no more hashes.
// The eight gradients are the axes and diagonals — the classic set; using
// unnormalised diagonals is the standard cheat and costs nothing visible.
const GRAD_X = [1, -1, 1, -1, 1, -1, 0, 0];
const GRAD_Y = [1, 1, -1, -1, 0, 0, 1, -1];

function gradDot(ix, iy, dx, dy) {
  const g = hashBits(ix, iy) & 7;
  return GRAD_X[g] * dx + GRAD_Y[g] * dy;
}

// Returns ~0..1, centred on 0.5. Perlin's own range is roughly ±0.7 and
// bell-shaped rather than flat, so SPREAD sets how much of the 0..1 band it
// actually uses — it is what keeps the CLOUD_TYPES thresholds, which were
// authored against the old flat-ish value noise, meaning what they meant.
const NOISE_SPREAD = 0.70;
function gradNoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const u = fx * fx * fx * (fx * (fx * 6 - 15) + 10);
  const v = fy * fy * fy * (fy * (fy * 6 - 15) + 10);
  const n00 = gradDot(xi, yi, fx, fy);
  const n10 = gradDot(xi + 1, yi, fx - 1, fy);
  const n01 = gradDot(xi, yi + 1, fx, fy - 1);
  const n11 = gradDot(xi + 1, yi + 1, fx - 1, fy - 1);
  const a = n00 + (n10 - n00) * u;
  const b = n01 + (n11 - n01) * u;
  return 0.5 + NOISE_SPREAD * (a + (b - a) * v);
}

// How far a cloud's shadow lands from the cloud itself, up and to the left
// of it, in WORLD units (a tile is WORLD across). Far enough that the shadow
// is a dark patch of its own rather than a rim around the bright one, close
// enough that it is still recognisably the same bank's shape — which means
// it is really a fraction of CLOUD_TYPES.scale, about half a feature, and
// wants moving if the presets are ever rescaled. The sun is a fixed
// direction here rather than the day's swinging shadowAngle: a shadow that
// swung round the compass over ten real minutes would draw the eye to the
// sky instead of the map. CLOUD_SHADOW_A scales the shadow off the
// cloud's own opacity — thicker cloud, darker ground — up to a ceiling that
// keeps a dense bank from blacking the tile out.
const CLOUD_SHADOW_OFF = 50;
const CLOUD_SHADOW_A = 1.5;
const CLOUD_SHADOW_MAX = 0.34;
const CLOUD_SHADOW_INK = '#0b1018';

// The sky is not one sheet. Three decks at different heights ride the same
// wind at different speeds, and it is that shear — banks sliding across each
// other, gaps opening and closing where they cross — that stops a drifting
// field from reading as one rigid texture towed over the map.
//   speed: multiplies the drift the caller integrates (1 = the wind it
//     thinks it is blowing, so the mid deck is the old single layer).
//   px/py: a fixed lead into the same noise field, so no two decks ever
//     show the same shape. Far larger than any preset's feature scale.
// Each deck is thresholded at a SHARE of the requested cover rather than
// all of it: three decks veiling a third of the sky each come out at the
// coverage one deck at full cover used to give. The share is not 1/3 — the
// decks overlap, and cover maps to a threshold on a bell-shaped noise
// distribution, not to an area — so it is calibrated, not derived.
const CLOUD_DECKS = [
  { speed: 0.62, px: 0,     py: 0 },      // low: the slow heavy banks
  { speed: 1.00, px: 811.3, py: 517.7 },  // mid
  { speed: 1.55, px: 269.1, py: 943.5 },  // high: thin stuff, outruns the rest
];
const CLOUD_DECK_SHARE = 0.65;

// Per-deck world offsets for the tile being drawn. Module scratch, not a
// fresh array per tile: one cloud pass runs at a time, and this is the
// hottest loop in the frame.
const deckOx = new Float64Array(CLOUD_DECKS.length);
const deckOy = new Float64Array(CLOUD_DECKS.length);

// The cloud field, resolved once per tile so the cloud pass and its shadow
// read the same noise. dx/dy shift where the field is SAMPLED, so a positive
// offset moves what is drawn the other way — see the call below.
function cloudField(anim, tune) {
  const type = CLOUD_TYPES[tune.cloudType] || CLOUD_TYPES.scattered;
  // WORLD units, not the authoring grid: the noise scales are tuned in
  // them, and this loop is the single biggest per-frame cost in the game's
  // composite pass — following a finer art grid would multiply it by 9.
  // The tile's own world position is common to every deck; only the drift
  // is scaled, which is what makes one deck outrun another.
  const wx = (anim.col || 0) * WORLD, wy = (anim.row || 0) * WORLD;
  for (let i = 0; i < CLOUD_DECKS.length; i++) {
    const d = CLOUD_DECKS[i];
    deckOx[i] = wx - (anim.cloud.x || 0) * d.speed + d.px;
    deckOy[i] = wy - (anim.cloud.y || 0) * d.speed + d.py;
  }
  return {
    type,
    thr: type.thr(anim.cloud.cover * CLOUD_DECK_SHARE),
    sx: type.scale * type.stretch,
    sy: type.scale,
    morph: (anim.t || 0) * 0.05,
  };
}

// How far the fine octave drags the coarse lookup, in coarse noise cells.
// This is domain warping: sampling the broad shape at a position that the
// detail octave has displaced. It is the cheapest trick in procedural
// texturing and the one that matters most here — summed octaves alone give
// lumpy blobs with the same lumpiness everywhere, while a warped lookup
// stretches, hooks and curls the edges the way moving air actually does.
// Free, because the octave doing the warping is one the field already pays
// for. Push it past ~1.2 and banks tear into filaments.
const CLOUD_WARP = 0.75;

// Density at one point of the sky, already shaped into an alpha. 0 = clear.
// Each deck is two octaves warped by each other, the fine one's coordinates
// creeping with time so shapes slowly morph while the whole field drifts.
// The decks are combined as stacked translucency (what is left clear after
// all three) rather than painted one over another — identical result, since
// they share a colour, at one fill per cell instead of three.
function cloudAlpha(f, x, y, dx, dy) {
  let clear = 1;
  for (let i = 0; i < CLOUD_DECKS.length; i++) {
    const wx = (x + dx + deckOx[i]) / f.sx, wy = (y + dy + deckOy[i]) / f.sy;
    const fine = gradNoise(wx * 2.7 + 31.7 + f.morph, wy * 2.7 + 17.3);
    // the warp is stronger across than along, so banks shear rather than
    // just wobble — the two axes must differ or the whole field skews 45°
    const w = (fine - 0.5) * CLOUD_WARP;
    const d = gradNoise(wx + w, wy + w * 0.55) * 0.65 + fine * 0.35;
    if (d <= f.thr) continue;
    // smoothstep, not a linear ramp into a hard ceiling: the old profile put
    // a visible contour line wherever a bank hit full opacity
    const e = Math.min(1, (d - f.thr) * f.type.sharp / f.type.maxA);
    clear *= 1 - f.type.maxA * e * e * (3 - 2 * e);
  }
  return 1 - clear;
}

// anim.cloud = { cover: 0..1, x, y } — x/y are the accumulated drift offset
// (world px, integrated by the caller so gusty wind doesn't jitter the
// clouds). Two passes: the shadow the bank casts on the ground, then the
// bank itself, pale and thin over the top. The tile's own contents are what
// the shadow falls on, so the darkening is what sells the weather — the
// clouds alone read as fog laid over the map.
function drawCloudLayer(g, anim, light, tune) {
  const f = cloudField(anim, tune);
  const cell = FX_STEP * FX_PX;

  // Half resolution: a shadow is a soft dark patch with no edge to lose, so
  // this costs a quarter of the cloud pass rather than another one of it.
  // It scales with the light that casts it — by night the ground is dark
  // already and the mottling all but disappears.
  const step = FX_STEP * 2, scell = step * FX_PX;
  for (let y = 0; y < WORLD; y += step) {
    for (let x = 0; x < WORLD; x += step) {
      // sampled down-sun, so the shadow lands up and left of its own cloud
      const a = cloudAlpha(f, x, y, CLOUD_SHADOW_OFF, CLOUD_SHADOW_OFF);
      if (a === 0) continue;
      g.a(Math.min(CLOUD_SHADOW_MAX, a * CLOUD_SHADOW_A * light),
        () => g.p(x * FX_PX, y * FX_PX, scell, scell, CLOUD_SHADOW_INK));
    }
  }

  const l = Math.round(80 + 150 * light);            // pale by day, near-dark by night
  const color = `rgb(${l},${l + 4},${Math.min(255, l + 12)})`;
  for (let y = 0; y < WORLD; y += FX_STEP) {
    for (let x = 0; x < WORLD; x += FX_STEP) {
      const alpha = cloudAlpha(f, x, y, 0, 0);
      if (alpha === 0) continue;
      g.a(alpha, () => g.p(x * FX_PX, y * FX_PX, cell, cell, color));
    }
  }
}

// Dim and cool a palette toward night. light: 1 = full day, 0 = midnight.
// Blue dims less than red/green so nights read cold rather than gray, and
// glow is exempt entirely — handheld lights and fires pop after dark.
// Memoized per palette on a 1/512 light quantization: light sweeps a full
// day in 10 minutes, so each palette re-tints at most every ~1.2s instead
// of re-parsing every hex color on every tile draw (up to 119 tiles/frame).
// The 1/512 snap is far below a visible color step.
const tintCache = new WeakMap(); // pal -> Map<bucket, tinted pal>
export function tintPalette(pal, light) {
  if (light >= 1) return pal;
  const bucket = Math.round(Math.max(0, light) * 512);
  let byBucket = tintCache.get(pal);
  if (!byBucket) tintCache.set(pal, byBucket = new Map());
  const cached = byBucket.get(bucket);
  if (cached) return cached;

  const q = bucket / 512;
  const out = {};
  for (const [key, hex] of Object.entries(pal)) {
    if (key === 'glow') { out[key] = hex; continue; }
    const n = parseInt(hex.slice(1), 16);
    const r = Math.round(((n >> 16) & 255) * (0.30 + 0.70 * q));
    const g = Math.round(((n >> 8) & 255) * (0.34 + 0.66 * q));
    const b = Math.round((n & 255) * (0.50 + 0.50 * q));
    out[key] = `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }
  if (byBucket.size > 600) byBucket.clear(); // a day sweeps ~300 buckets
  byBucket.set(bucket, out);
  return out;
}

// Merged-tune cache for the game's static REGION_THEMES only - the image
// lab passes opts.theme and mutates its tune live, so that path recomputes
const tuneCache = new WeakMap(); // theme -> merged tune
function resolveTheme(regionKey, opts) {
  const theme = opts.theme || REGION_THEMES[regionKey] || FALLBACK_THEME;
  if (opts.theme) return { theme, tune: { ...DEFAULT_TUNE, ...(theme.tune || {}) } };
  let tune = tuneCache.get(theme);
  if (!tune) tuneCache.set(theme, tune = { ...DEFAULT_TUNE, ...(theme.tune || {}) });
  return { theme, tune };
}

// Everything the time of day does to a tile, gathered in one place so the
// one-shot, static and composite paths cannot disagree about the sun.
// dayPhase null (static renders) = full daylight, dead grid.
export function dayState(theme, tune, dayPhase) {
  if (dayPhase == null) {
    return { light: 1, shadowAngle: tune.shadowAngle, shadowDist: 1.4, shadowAlpha: tune.shadowAlpha, lamp: 0 };
  }
  // 1. the raw daylight curve (15 hours of light, 9 hours of dark)
  const clamped = Math.max(0.4, Math.min(1, 0.4 + 0.85 * Math.cos((dayPhase - 0.5) * 2 * Math.PI)));
  // 2. smoothstep for a soft twilight and dawn fade
  const surface = clamped * clamped * (3 - 2 * clamped);
  // Sublevels get a faint ambient echo of the surface day: a floor so the dark
  // hours aren't pitch black, plus a slice of the normal brightness curve.
  // The floor was 0.06, which compounded with the night tint (x0.30 red at the
  // bottom of its range) and the darkness wash into a tile you could not read
  // at all — the region was distinguishable only by being black. The ceiling
  // MUST stay under 0.4: above it dayState switches on moving sun shadows, and
  // a sublevel that has no sun would start churning its cached still layer for
  // shadows it should never cast.
  const light = theme.backdrop === 'underground' ? 0.22 + 0.16 * surface : surface;
  const dayFrac = Math.min(1, Math.max(0, (dayPhase - 0.25) / 0.5)); // 06:00 -> 18:00
  const ds = {
    light,
    shadowAngle: 180 - dayFrac * 180, // morning shadows point west, noon south, evening east
    shadowDist: 0,
    shadowAlpha: tune.shadowAlpha,
    // powered lights ramp on as daylight fades below ~0.45 (dusk) and off
    // again at dawn; they never light in static daytime renders
    lamp: Math.min(1, Math.max(0, (0.45 - light) / 0.2)),
  };
  if (light > 0.4) {
    ds.shadowDist = 1 + (tune.shadowLength - 1) * (1 - Math.sin(dayFrac * Math.PI)); // long at dawn/dusk, short at noon
    ds.shadowAlpha = ((light - 0.4) / 0.6) * tune.shadowAlpha;
  }
  return ds;
}

// tintPalette scales each channel by a constant, which is exactly what a
// 'multiply' composite with this color does — so the cached static layer can
// be rendered ONCE in full daylight and darkened at composite time with a
// single rect, instead of being repainted as the sun moves. (The palette's
// glow key is exempt from tinting and the multiply is not, which is why
// every light source is a dynamic() draw — those land after the tint.)
export function dayTint(light) {
  const q = Math.min(1, Math.max(0, light));
  return `rgb(${Math.round(255 * (0.30 + 0.70 * q))},${Math.round(255 * (0.34 + 0.66 * q))},${Math.round(255 * (0.50 + 0.50 * q))})`;
}

// What the cached static layer actually depends on in the day cycle: the
// integer shadow offset and its alpha, nothing else. Both change in coarse
// steps, so a tile repaints a couple of dozen times a game day rather than
// 12 times a second.
export function staticKey(ds) {
  const a = (ds.shadowAngle * Math.PI) / 180;
  return `${Math.round(Math.cos(a) * ds.shadowDist)},${Math.round(Math.sin(a) * ds.shadowDist)},${Math.round(ds.shadowAlpha * 6)}`;
}

function setAnim(anim, ds, tune) {
  ANIM = {
    wind: anim?.wind || 0, t: anim?.t || 0, lamp: ds.lamp,
    lightUptime: tune.lightUptime, lightSpeed: tune.lightFlickerSpeed,
    tintScale: ds.light * TINT_STEPS,
  };
}

// The tile itself: floor, roads, structures, weathering, growth. Identical
// for the one-shot and static paths — the only difference is whether
// dynamic() draws inline or records.
function composeBody(g, T, theme, baseTune, regionKey, recipe, roomName, opts) {
  const conn = opts.conn || { n: true, e: true, s: true, w: true };
  // each drawing phase gets its own seeded stream: the shadow and content
  // passes below replay identical layouts from fresh copies of the same
  // stream, and no phase can perturb another's composition
  const phaseRng = phase => makePRNG(`img|${regionKey}|${roomName}${opts.salt || ''}|${phase}`);
  // A region may draw one of the shared recipes from its own layout instead:
  // a harbour's storage is a container yard, a foundry's is a silo farm. This
  // resolves ONLY the layout lookup — `recipe` itself stays the string it was,
  // because it is a classification key everywhere else in this file
  // (CLEARING_RECIPES, DENSITY_EXEMPT, RUIN_EXEMPT, PARKING_RECIPES, and the
  // `=== 'portal'` / `!== 'water'` tests in the effect and weathering passes).
  // Rewriting it here would quietly reclassify the tile as well as redraw it.
  const def = ROOM_RECIPES[theme.recipes?.[recipe] || recipe] || ROOM_RECIPES.infrastructure;
  const rng = phaseRng('floor');
  (FLOORS[theme.backdrop] || FLOORS.city)(g, rng, T);
  // The clearings lay their turf straight over the region floor, off their own
  // stream so the floor underneath stays byte-identical to every other tile's.
  if (CLEARING_RECIPES.has(recipe)) clearing(g, phaseRng('turf'), T, opts.clearing);
  // The sea wall is TERRAIN — effectively where the map stops — so it goes down
  // with the ground and everything the tile is built of sits on top of it. Drawn
  // with the weathering, as it was, it laid concrete over roads and roofs, which
  // reads as a wall built straight through a street.
  if (theme.overlay === 'flood' && opts.shore === 0) {
    drawSeaWall(g, T, { shore: opts.shore, col: opts.col ?? opts.anim?.col ?? 0 },
      Math.max(0.01, baseTune.floodLevel ?? 1), opts.shoreSide === 's' ? 's' : 'n');
  }
  // Roads come from the generated network (opts.road), so only tiles the
  // network runs through draw one. The network is authoritative even over a
  // lake or a collapsed slab: those rooms are placed AFTER it is laid out, so
  // letting them suppress the roadway would punch gaps into a through route.
  //
  // The road is routed around the recipe's buildings, so it has to know where
  // they will land before it draws. A measuring pass runs the recipe against a
  // probe that draws nothing (makeProbeG) off a FRESH copy of the same stream
  // the real passes use, so what it measures is exactly what gets drawn. A
  // parking lot, gate, fence or pipeline is not measured — a road crossing one
  // of those is fine, and usually better.
  const isGate = opts.roomType === 'gate';
  // How much of the recipe's built stock this tile is entitled to. A tile the
  // roadway runs through is at distance 0 by definition, whatever a stale save
  // says, so the road itself is the authority when it is present.
  const thinnable = !DENSITY_EXEMPT.has(recipe) && !isGate;
  const roadDist = opts.road ? 0 : (opts.roadDist ?? 0);
  // ...and how built-up its DISTRICT is, which the roadway knows nothing about.
  // A tile with no district — a save written before they existed, or the image
  // lab previewing a room on its own — gets none of this and is drawn exactly
  // as it was.
  const built = opts.zone ? builtUp(opts.zone,
    opts.col ?? opts.anim?.col ?? 0, opts.row ?? opts.anim?.row ?? 0, opts.shore) : null;
  // Everything downstream — the measuring pass, the road's route around the
  // buildings, and the drawing itself — works off the tile's own scale, so it
  // has to be settled here, before any of it runs.
  const tune = tuneForTile(baseTune, roadDist, opts.roadRank, built, thinnable);

  // One measuring pass, always: three separate things downstream need to know
  // what this recipe is about to put where — routing the road around it,
  // thinning it back by distance, and keeping the vegetation off its roofs.
  // It runs off a fresh copy of the content stream, so it rolls the same
  // variant the drawing passes will.
  let skip = new Set(), variant, shift = [], samples = null;
  let boxes = [];
  runRecipe(def, g, phaseRng('content'), T, tune, conn, 0, 0, { record: boxes });
  if (opts.road) {
    // The road is routed by the tile's edges, not by what is on the tile, so it
    // can be laid before the recipe — and the recipe can then be laid to suit
    // it. The dead-end forms still want to know what is in the way first, which
    // is what the measurement above gives them.
    samples = connectorBands(g, rng, T, opts.road, tune, boxes.filter(b => b.solid),
      opts.row ?? opts.anim?.row, opts.col ?? opts.anim?.col);
    // The layout pass re-measures per variant and returns the winner's own
    // boxes: a variant's element indices only mean anything within it.
    ({ variant, shift, skip, boxes } = chooseLayout(def, () => phaseRng('content'), T, tune, conn,
      samples || [], phaseRng('layout')));
  } else if (thinnable) {
    // Off the network the choice is free, and the same stream makes it, so a
    // tile that later gains a road does not also change hands.
    shift = seededMirror(boxes, phaseRng('layout'));
  }
  // Thin the buildings back with distance from the roadway, on top of whatever
  // the road already suppressed. Suppression, not relocation: a structure that
  // was never built leaves its lot behind, which is the look we are after.
  if (thinnable) {
    for (const n of thinForDistance(boxes, roadDist, opts.roadRank, built)) skip.add(n);
  }
  // Where the structures actually end up: measured footprints, moved to
  // wherever the layout put them, minus whatever was suppressed.
  const at = () => boxes.filter(b => !skip.has(b.n)).map(b => {
    const [dx, dy] = shift[b.n] || [0, 0];
    return { ...b, x0: b.x0 + dx, y0: b.y0 + dy, x1: b.x1 + dx, y1: b.y1 + dy };
  });
  let placed = at();
  // Then walk them off the tile's own corners, so the map stops repeating on a
  // 120-unit rhythm. Landmarks sit it out for the same reason they sit out the
  // thinning: a portal facility or a rest stop is PLACED, and a placed thing
  // belongs where it was put.
  if (thinnable) {
    const moved = placeStructures(placed, phaseRng('place'), samples);
    if (moved.size) {
      const before = measureSizes(def, phaseRng('content'), T, tune, conn, variant, shift);
      for (const [n, [dx, dy]] of moved) {
        shift[n] = [(shift[n]?.[0] || 0) + dx, (shift[n]?.[1] || 0) + dy];
      }
      // Not every element is the same size wherever it stands: buildingTop
      // sizes itself against the tile edge, storefront against a line halfway
      // down. A structure that changed size is one whose drawn footprint is no
      // longer the one this pass cleared — bigger, and it lands on what it was
      // cleared of. Rather than teach the placement pass every clamp in the
      // element library, measure again and take back the offsets that moved a
      // size. An element's size depends only on its own position, so putting
      // one back restores it exactly and no second correction is needed.
      const after = measureSizes(def, phaseRng('content'), T, tune, conn, variant, shift);
      for (const [n, [dx, dy]] of moved) {
        if (after.get(n) === before.get(n)) continue;
        shift[n] = [shift[n][0] - dx, shift[n][1] - dy];
      }
      placed = at();
    }
  }
  // What the vegetation, the ruins and the traffic have to grow around.
  const keepOut = placed.filter(b => b.solid)
    .map(({ x0, y0, x1, y1 }) => ({ x0, y0, x1, y1 }));
  // vegetation density: region knob x the recipe's own multiplier;
  // gates are dressed denser as major areas of interest
  const vegMult = tune.treeDensity * (def.treeDensity ?? 1) * (isGate ? 1.7 : 1);
  // Returns the gate's extra block, which nothing else has measured, so the
  // growth can keep off it like it keeps off everything the recipe drew.
  const drawStructures = r => {
    runRecipe(def, g, r, T, tune, conn, 0, 0, { skip, variant, shift });
    return isGate ? gateEmphasis(g, r, T, tune, conn, recipe, samples) : null;
  };
  // `extra` is everything drawn on this tile that the recipe did not measure —
  // a gate's dressing block and the signature props. Growth has to keep off all
  // of it, or you get a tree on a tower roof (see the gateEmphasis test).
  const drawVegetation = (r, extra) => {
    const more = extra?.filter(Boolean) || [];        // a tile with no gate passes null
    return (TD_VEGETATION[theme.backdrop] || TD_VEGETATION.city)(
      g, r, T, vegMult, more.length ? [...keepOut, ...more] : keepOut);
  };
  // Ruins go down with the structures, not with the growth: they are what is
  // left of the same city, and vegetation is meant to reclaim them, so the
  // canopies come over the top afterwards. Rooms the map places whole sit them
  // out — a shaft, a lake, a rest stop or a portal facility is the tile.
  const drawRuinsPass = RUIN_EXEMPT.has(recipe)
    ? () => []
    : (r, lot) => drawRuins(g, r, T, tune, lot ? [...keepOut, lot] : keepOut, samples,
      opts.shore != null ? { at: opts.shore, side: opts.shoreSide } : null);
  // The region's own architecture, and the only pass on the tile that is about
  // a particular place. It goes down AFTER the recipe (so it can see where the
  // buildings landed) and BEFORE the traffic, weathering and growth (which are
  // things that happen TO architecture, and must happen to this too).
  // Rooms the map places WHOLE sit the signature layer out, for exactly the
  // reason they sit the ruins out: a shaft, a lake, a rest stop or a portal
  // facility IS the tile, and a tower core or a colonnade standing in one is
  // the same mistake as a fresh ruin there. This is also what kept a tower's
  // debris skirt out of the collapsed ground it was landing in.
  const drawSignature = RUIN_EXEMPT.has(recipe)
    ? () => []
    : (r, ruins, gate) =>
    (TD_SIGNATURE[theme.backdrop] || (() => []))(g, r, T, tune, {
      keep: [...keepOut, ...(ruins || []), ...(gate ? [gate] : [])],
      samples, conn,
      col: opts.col ?? opts.anim?.col ?? 0, row: opts.row ?? opts.anim?.row ?? 0,
      built, recipe, shore: opts.shore, shoreSide: opts.shoreSide, rail: opts.rail,
    });
  // Traffic sits against the roadway, so it needs the road's samples and the
  // rank they were drawn at. Tiles the network misses have neither and get none.
  // It also needs the lot: both hug the same band and the traffic draws last, so
  // without it a wreck lies across the bays of the car park it is parked beside.
  const drawTraffic = (r, lot) =>
    drawRoadside(g, r, T, samples, opts.roadRank, lot ? [...keepOut, lot] : keepOut);
  // Parking goes down before the ruins so a ruin never lands on the lot, and
  // hands its footprint on for exactly that.
  const drawParkingPass = PARKING_RECIPES.has(recipe)
    ? r => drawParking(g, r, T, samples, keepOut)
    : () => null;
  // pass 1: every shadow lands on the bare ground first, so no object's
  // before the weathering overlay so dust and rust settle on it like anything
  // else. Its own 'anchor' stream keeps every other phase byte-identical to
  // what it drew before anchors existed, and means a skill rebalance changes
  // WHICH device a tile shows without moving WHERE it sits.
  const drawAnchor = r => runAnchor(opts.primary, g, r, T, tune, conn, opts.indoor);
  // pass 1: every shadow lands on the bare ground first, so no object's
  // shadow can fall across a neighboring object
  SHADOW_PASS = true;
  const shadowLot = drawParkingPass(phaseRng('lot'));
  const shadowRuins = drawRuinsPass(phaseRng('ruin'), shadowLot);
  const shadowGate = drawStructures(phaseRng('content'));
  const shadowSig = drawSignature(phaseRng('signature'), shadowRuins, shadowGate);
  drawTraffic(phaseRng('traffic'), shadowLot);
  drawAnchor(phaseRng('anchor'));
  drawVegetation(phaseRng('veg'), [shadowGate, ...shadowSig]);
  SHADOW_PASS = false;
  // pass 2: the objects over their shadows — anchor off a FRESH copy of the
  // same stream, or it would pick a different corner than its shadow did
  const lot = drawParkingPass(phaseRng('lot'));
  const ruins = drawRuinsPass(phaseRng('ruin'), lot);
  const gate = drawStructures(phaseRng('content'));
  const sig = drawSignature(phaseRng('signature'), ruins, gate);
  drawTraffic(phaseRng('traffic'), lot);
  drawAnchor(phaseRng('anchor'));
  // The weathering pass gets the same context the signature layer does, plus
  // the shoreline: the tide is weather in the sense that matters here — it
  // settles on top of what was built and under what has grown since.
  // `keep` is what the recipe built; `props` is what the signature layer added.
  // They are handed over separately because the two overlays that use them want
  // different things: the tide has to stay out of BOTH (a sea wall standing in
  // the sea is not holding anything back), while the rock mass only answers to
  // the recipe — a colonnade is inside the hall, and if pillars pushed the walls
  // away there would be no walls left anywhere.
  (TD_OVERLAYS[theme.overlay] || TD_OVERLAYS.dust)(g, phaseRng('overlay'), T, recipe, {
    keep: keepOut, props: sig.filter(Boolean),
    samples, col: opts.col ?? opts.anim?.col ?? 0, row: opts.row ?? opts.anim?.row ?? 0,
    shore: opts.shore, shoreSide: opts.shoreSide, floodLevel: tune.floodLevel,
    enclosure: tune.enclosure, built,
  });
  // vegetation content still renders after weathering: canopies overhang
  // it, and hollow-core's bioluminescent fungi must glow through the
  // darkness overlay (their shadows already went down in pass 1)
  drawVegetation(phaseRng('veg'), [gate, ...sig]);

  // Hand the finished ground plan to whoever asked for one. Deliberately the
  // same list the vegetation keeps off — anything a tree may not grow on is
  // something a person may not stand on, and keeping the two answers identical
  // is what stops the map disagreeing with itself about where the ground is.
  //
  // Ruins are NOT in it: rubble is walkable, and half of it is ankle-high.
  if (PLAN) {
    PLAN.blocks = [...keepOut, ...(gate ? [gate] : []), ...sig.filter(Boolean)]
      .map(({ x0, y0, x1, y1 }) => ({ x0, y0, x1, y1 }));
    // The lake is a disc, not one of these boxes — see POND.
    PLAN.water = recipe === 'water' ? { ...POND } : null;
    // What the tile is ABOUT, when it is about one thing. Only the campsite
    // has one so far: its fire ring is where a team that has stopped to rest
    // would actually sit, and it is dealt a different spot on every map, so
    // nothing downstream can assume where it is.
    const focus = placed.find(b => b.el === 'fireRing');
    PLAN.focus = focus
      ? { x: (focus.x0 + focus.x1) / 2, y: (focus.y0 + focus.y1) / 2 }
      : null;
  }
}

// World-space effects, drawn over everything and never cached: they move
// every frame and are sampled by grid position so neighbouring tiles stay
// seamless. Cheap despite the tile size — both step in AUTHORING units, so
// their cell count is what it was at 40px.
function drawEffects(g, T, theme, tune, ds, anim, recipe, opts) {
  // daylight wells: cave openings overhead light whole areas of the map
  if (tune.lightRays > 0 && theme.backdrop === 'underground') {
    drawLightWells(g, T, tune, anim, opts.col ?? anim?.col ?? 0, opts.row ?? anim?.row ?? 0);
  }
  // clouds pass over everything; the sublevels have no sky
  if (anim?.cloud?.cover > 0 && theme.backdrop !== 'underground') {
    drawCloudLayer(g, anim, ds.light, tune);
  }
  // the portal's energy glow punches through flood, growth, and cloud
  if (recipe === 'portal') portalGlow(g, T);
}

// Draw a complete tile (TILE_PX square) in one shot — static and animated
// parts together, nothing cached. The image lab and the PNG exporter use
// this; the game's grid goes through createTile/composite instead.
// opts.theme overrides the region theme (image lab only); opts.salt perturbs
// the seed — the game passes the grid position so twin rooms differ.
// opts.anim = { wind, dayPhase, t } enables animation: wind (0..1) sways
// tree canopies, dayPhase (0..1, 0 = midnight) dims/cools the palette and
// swings the sun's shadow angle across the day, t is elapsed seconds.
export function drawRoomTile(ctx, regionKey, recipe, roomName, opts = {}) {
  const { theme, tune } = resolveTheme(regionKey, opts);
  const anim = opts.anim;
  const ds = dayState(theme, tune, anim?.dayPhase);
  const T = tintPalette(theme.pal, ds.light);
  setAnim(anim, ds, tune);
  setShadowAngle(ds.shadowAngle, ds.shadowDist, ds.shadowAlpha);
  const g = makeG(ctx);
  DYN = null;                       // draw the animated parts inline
  CUR_G = g; CUR_T = T;
  // ...but the overhead channel is still RECORDED even here, because its whole
  // point is to land after the effects, and composeBody runs before them.
  const over = OVER = [];
  composeBody(g, T, theme, tune, regionKey, recipe, roomName, opts);
  OVER = null;
  drawEffects(g, T, theme, tune, ds, anim, recipe, opts);
  for (const fn of over) fn(g, T, overheadFx(tune, anim, opts));
}

/**
 * What an overhead closure is told about the frame it is being replayed into.
 *
 * The same handful of facts drawEffects works from, because anything drawn
 * above the effects generally has to agree with them about the light — the
 * rock's shadow is cast by the very shafts it is drawn over.
 */
const overheadFx = (tune, anim, opts) => ({
  tune, anim,
  col: anim?.col ?? opts.col ?? 0,
  row: anim?.row ?? opts.row ?? 0,
});

// ---- the game's cached path ----
// A tile is a still layer plus a short list of things that move. createTile
// builds the handle, refreshStatic repaints the still layer when (and only
// when) the sun has moved it, and composite draws a frame.

export function createTile(regionKey, recipe, roomName, opts = {}) {
  const { theme, tune } = resolveTheme(regionKey, opts);
  // `plan` is the tile's ground plan (see PLAN): where its buildings and its
  // water are, for anything that has to walk about on it. Filled by the first
  // refreshStatic, because it takes a full compose pass to know.
  return { regionKey, recipe, roomName, opts, theme, tune, canvas: null, dyn: [], over: [], key: null, plan: null };
}

// Repaint the still layer if the sun has moved it since last time. Returns
// true when it actually repainted, so the caller can budget the work.
export function refreshStatic(tile, dayPhase) {
  const ds = dayState(tile.theme, tile.tune, dayPhase);
  const key = staticKey(ds);
  if (key === tile.key) return false;
  if (!tile.canvas) {
    tile.canvas = document.createElement('canvas');
    tile.canvas.width = tile.canvas.height = TILE_PX;
  }
  const g = makeG(tile.canvas.getContext('2d'));
  setAnim(null, ds, tile.tune);
  setShadowAngle(ds.shadowAngle, ds.shadowDist, ds.shadowAlpha);
  const dyn = DYN = [];
  const over = OVER = [];
  // The plan does not depend on the hour, so it is only collected the first
  // time — a tile repaints a couple of dozen times a game day and the buildings
  // do not move between one and the next.
  const plan = PLAN = tile.plan ? null : { blocks: [], water: null, focus: null };
  CUR_G = null; CUR_T = null;
  // UNTINTED: brightness is applied at composite time as a multiply
  composeBody(g, tile.theme.pal, tile.theme, tile.tune,
    tile.regionKey, tile.recipe, tile.roomName, tile.opts);
  DYN = null;
  OVER = null;
  PLAN = null;
  tile.dyn = dyn;
  tile.over = over;
  if (plan) tile.plan = plan;
  tile.key = key;
  return true;
}

// One frame of one tile: blit the still layer, darken it to the hour, replay
// what moves, then the world-space effects on top.
export function composite(ctx, tile, anim) {
  const ds = dayState(tile.theme, tile.tune, anim?.dayPhase);
  const T = tintPalette(tile.theme.pal, ds.light);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(tile.canvas, 0, 0);
  if (ds.light < 1) {
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = dayTint(ds.light);
    ctx.fillRect(0, 0, TILE_PX, TILE_PX);
    ctx.globalCompositeOperation = 'source-over';
  }
  setAnim(anim, ds, tile.tune);
  setShadowAngle(ds.shadowAngle, ds.shadowDist, ds.shadowAlpha);
  SHADOW_PASS = false;
  IN_SHADOW = false;
  DYN = null;
  OVER = null;
  const g = makeG(ctx);
  for (const fn of tile.dyn) fn(g, T);
  drawEffects(g, T, tile.theme, tile.tune, ds, anim, tile.recipe,
    { ...tile.opts, col: anim?.col ?? tile.opts.col, row: anim?.row ?? tile.opts.row });
  // ...and last of all, what is over the room rather than in it
  const fx = overheadFx(tile.tune, anim, tile.opts);
  for (const fn of tile.over) fn(g, T, fx);
}

export function renderRoomImage(regionKey, recipe, roomName, opts = {}) {
  const out = document.createElement('canvas');
  out.width = TILE_PX;
  out.height = TILE_PX;
  drawRoomTile(out.getContext('2d'), regionKey, recipe, roomName, opts);
  return out.toDataURL('image/png');
}
